// src/pages/MuultiPage.js
import React, {useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef} from 'react';
import {useWallet} from '@solana/wallet-adapter-react';
import {WalletMultiButton} from '@solana/wallet-adapter-react-ui';
import { Link as RouterLink } from 'react-router-dom';
import {
    collection,
    addDoc,
    updateDoc,
    query,
    where,
    getDocs,
    serverTimestamp,
    deleteDoc,
    doc,
    getDoc,
    setDoc,
} from 'firebase/firestore';
import {db} from '../../firebaseConfig';
import '../MuultiStyle.css';
import '../MuultiSpaceCarousel.css'
import TextComponent from '../TextComponent';
import LinkComponent from '../LinkComponent';
import SocialLinksComponent from '../SocialLinksComponent';
import NewTextComponentForm from '../NewTextComponentForm';
import NewLinkComponentForm from '../NewLinkComponentForm';
import NewSocialLinksForm from '../NewSocialLinksForm';
import VideoComponent from "../VideoComponent";
import NewVideoComponentForm from "../NewVideoComponentForm";
import ConnectWalletStepComponent from "../ConnectWalletStepComponent";
import ChooseYourUsernameStepComponent from "../ChooseYourUsernameStepComponent";
import ImageComponent from '../ImageComponent';
import NewImageComponentForm from '../NewImageComponentForm';
import {auth} from "../../firebaseConfig";
import BackgroundPicker from '../BackgroundPicker';
import NewAudioComponentForm from "../NewAudioComponentForm";
import AudioComponent from "../AudioComponent";
import DonationComponent from "../DonationComponent";
import NewDonationComponentForm from "../NewDonationComponentForm";
import AdComponent from "../AdComponent";
import NewAdComponentForm from "../NewAdComponentForm";
import NewLegacyNFTComponentForm from "../NewLegacyNFTFormComponent";
import LegacyNFTComponent from "../LegacyNFTComponent";
import NewMintNFTComponentForm from "../NewMintNFTComponentForm";
import {ref, uploadBytesResumable, getDownloadURL} from "firebase/storage";
import {storage} from "../../firebaseConfig";
import HeaderEditor from "../HeaderEditor";
import PublicHeader from "../PublicHeader";
import ComponentTypeListLinks from "../ComponentTypeListLinks";
import ProgressBar from '../ProgressBar';


import MintNFTComponent from "../MintNFTComponent";
import MuultiSpacesCarousel from "../MuultiSpacesCarousel";
import HtmlComponent from '../HtmlComponent';
import NewHtmlComponentForm from "../NewHtmlComponentForm";
import EditorWorkbench from "../EditorWorkbench";
import ContainerFrameEditor from "../ContainerFrameEditor";
import FontsStyleEditor from "../FontsStyleEditor";
import ComponentControls from "../ComponentControls";
import ComponentAnalyticsSummary from "../ComponentAnalyticsSummary";
import FeaturedMuultiSpaces from "../FeaturedMuultiSpaces";
import { openPublishSessionForWallet } from "../../utils/auth";
import { setWalletUsername } from "../../utils/walletUsername";
import { NATIVE_EMAIL_WALLET_NAME } from "../../utils/nativeEmailWalletAdapter";
import {getApp} from "@firebase/app";
import { normalizeImageGallery } from '../../utils/imageGallery';
import { mediaMintEligibility } from '../../utils/mediaMint';
import {
    buildDraftGate, composeComponentContent, getComponentGateMetadata, placeInlineComponent,
} from '../../utils/componentGateMetadata';
import {
    countListableComponents,
    normalizeComponentListSettings,
} from '../../utils/ComponentTypeLists';


// --- Line-height sanitizers (unitless always) ---
const toUnitlessLH = (v) => {
    if (v == null || v === '') return undefined;
    if (typeof v === 'number' && isFinite(v)) return v;
    const n = parseFloat(String(v).trim().replace(/px$/i, ''));
    return isFinite(n) ? n : undefined;
};

const sanitizeTypography = (t) => {
    if (!t) return null;
    return {
        ...(t || {}),
        headers: t.headers ? { ...t.headers, lineHeight: toUnitlessLH(t.headers.lineHeight) } : t.headers,
        paragraph: t.paragraph ? { ...t.paragraph, lineHeight: toUnitlessLH(t.paragraph.lineHeight) } : t.paragraph,
    };
};

const sanitizeStyle = (st) => {
    const s = st || {};
    return {
        ...s,
        headers: s.headers ? { ...s.headers, lineHeight: toUnitlessLH(s.headers.lineHeight) } : s.headers,
        paragraph: s.paragraph ? { ...s.paragraph, lineHeight: toUnitlessLH(s.paragraph.lineHeight) } : s.paragraph,
    };
};

const buildEmptySocialLinks = () => ({
    instagram: { url: '', label: '' },
    facebook: { url: '', label: '' },
    twitter: { url: '', label: '' },
    threads: { url: '', label: '' },
    exchangeArt: { url: '', label: '' },
    mallow: { url: '', label: '' },
    drip: { url: '', label: '' },
    bluesky: { url: '', label: '' },
    tiktok: { url: '', label: '' },
    reddit: { url: '', label: '' },
    youtube: { url: '', label: '' },
    github: { url: '', label: '' },
    linkedin: { url: '', label: '' },
    website: { url: '', label: '' },
});

const INLINE_ADD_DRAFT_COMPONENT_ID = '__inline_add_draft_component__';

const buildDefaultNewGateDetails = () => ({
    donationCompId: '',
    donationThreshold: 0,
    campaignName: '',
    currency: 'SOL',
    tokenMint: '',
    tokenThreshold: 0,
    priceSol: 0,
    password: '',
    passwordPrompt: '',
    scheduleName: '',
    startAt: '',
    endAt: '',
    targetId: '',
});

const normalizeSocialLinks = (links = {}) => {
    const base = buildEmptySocialLinks();
    Object.keys(base).forEach((key) => {
        const value = links?.[key];
        if (typeof value === 'string') {
            base[key] = { url: value, label: '' };
        } else if (value && typeof value === 'object') {
            base[key] = {
                url: value.url || value.link || '',
                label: value.label || value.name || '',
            };
        }
    });
    return base;
};

const toPositiveInt = (value, fallback = 1) => {
    const next = Number.parseInt(value, 10);
    if (Number.isFinite(next) && next > 0) return next;
    return fallback;
};

const toOptionalPositiveInt = (value) => {
    const next = Number.parseInt(value, 10);
    if (Number.isFinite(next) && next > 0) return next;
    return null;
};

const normalizeComponentLayout = (raw) => {
    const next = raw || {};
    return {
        colStart: toOptionalPositiveInt(next.colStart),
        rowStart: toOptionalPositiveInt(next.rowStart),
        colSpan: toPositiveInt(next.colSpan, 1),
        rowSpan: toPositiveInt(next.rowSpan, 1),
    };
};

const clampComponentLayout = (layout, renderedColumns, allowHorizontalScroll = false) => {
    const normalized = normalizeComponentLayout(layout);
    const cols = Math.max(1, toPositiveInt(renderedColumns, 1));
    const colSpan = allowHorizontalScroll
        ? normalized.colSpan
        : Math.min(normalized.colSpan, cols);
    let colStart = normalized.colStart;
    if (colStart != null) {
        if (allowHorizontalScroll) {
            colStart = Math.max(1, colStart);
        } else {
            colStart = Math.min(Math.max(1, colStart), cols - colSpan + 1);
        }
    }
    return {
        ...normalized,
        colSpan,
        colStart,
    };
};

const layoutsEqual = (a = {}, b = {}) => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
        const av = normalizeComponentLayout(a[key]);
        const bv = normalizeComponentLayout(b[key]);
        if (
            av.colStart !== bv.colStart ||
            av.rowStart !== bv.rowStart ||
            av.colSpan !== bv.colSpan ||
            av.rowSpan !== bv.rowSpan
        ) {
            return false;
        }
    }
    return true;
};

const layoutsOverlap = (a, b) => {
    const aColEnd = a.colStart + a.colSpan - 1;
    const bColEnd = b.colStart + b.colSpan - 1;
    const aRowEnd = a.rowStart + a.rowSpan - 1;
    const bRowEnd = b.rowStart + b.rowSpan - 1;
    return !(aColEnd < b.colStart || bColEnd < a.colStart || aRowEnd < b.rowStart || bRowEnd < a.rowStart);
};

const findFirstOpenSlot = (placed, desired, renderedColumns, allowHorizontalScroll = false) => {
    const minMaxColStart = Math.max(1, renderedColumns - desired.colSpan + 1);
    const maxColStart = allowHorizontalScroll
        ? Math.max(minMaxColStart, desired.colStart || 1, 256)
        : minMaxColStart;
    let col = Math.min(Math.max(1, desired.colStart || 1), maxColStart);
    let row = Math.max(1, desired.rowStart || 1);
    const maxIterations = 15000;

    for (let i = 0; i < maxIterations; i += 1) {
        const candidate = {
            colStart: col,
            rowStart: row,
            colSpan: desired.colSpan,
            rowSpan: desired.rowSpan,
        };
        const hasCollision = placed.some((p) => layoutsOverlap(candidate, p));
        if (!hasCollision) return candidate;
        col += 1;
        if (col > maxColStart) {
            col = 1;
            row += 1;
        }
    }

    return {
        colStart: 1,
        rowStart: Math.max(1, row),
        colSpan: desired.colSpan,
        rowSpan: desired.rowSpan,
    };
};

const resolveCollisionSafeLayouts = (
    components,
    inputLayouts,
    renderedColumns,
    priorityCompId = null,
    allowHorizontalScroll = false
) => {
    const cols = Math.max(1, toPositiveInt(renderedColumns, 1));
    const componentIds = (components || []).map((comp, idx) => String(comp?.id ?? idx));
    let orderedIds = componentIds;
    if (priorityCompId != null) {
        const priority = String(priorityCompId);
        if (componentIds.includes(priority)) {
            orderedIds = [priority, ...componentIds.filter((id) => id !== priority)];
        }
    }

    const placed = [];
    const nextLayouts = {};
    orderedIds.forEach((id) => {
        const normalized = clampComponentLayout(
            normalizeComponentLayout(inputLayouts?.[id]),
            cols,
            allowHorizontalScroll
        );
        const desired = {
            colStart: normalized.colStart ?? 1,
            rowStart: normalized.rowStart ?? 1,
            colSpan: normalized.colSpan,
            rowSpan: normalized.rowSpan,
        };
        const placedSlot = findFirstOpenSlot(placed, desired, cols, allowHorizontalScroll);
        const resolved = {
            ...normalized,
            colStart: placedSlot.colStart,
            rowStart: placedSlot.rowStart,
        };
        nextLayouts[id] = resolved;
        placed.push(resolved);
    });

    return nextLayouts;
};

const compactLayoutsToTopLeft = (
    components,
    inputLayouts,
    renderedColumns,
    allowHorizontalScroll = false
) => {
    const cols = Math.max(1, toPositiveInt(renderedColumns, 1));
    const componentIds = (components || []).map((comp, idx) => String(comp?.id ?? idx));
    const placed = [];
    const nextLayouts = {};

    componentIds.forEach((id) => {
        const normalized = clampComponentLayout(
            normalizeComponentLayout(inputLayouts?.[id]),
            cols,
            allowHorizontalScroll
        );
        const desired = {
            colStart: 1,
            rowStart: 1,
            colSpan: normalized.colSpan,
            rowSpan: normalized.rowSpan,
        };
        const placedSlot = findFirstOpenSlot(placed, desired, cols, allowHorizontalScroll);
        const resolved = {
            ...normalized,
            colStart: placedSlot.colStart,
            rowStart: placedSlot.rowStart,
        };
        nextLayouts[id] = resolved;
        placed.push(resolved);
    });

    return nextLayouts;
};

const LAYOUT_VIEWPORT_OPTIONS = [
    { id: 'desktop', label: 'Desktop', previewWidth: 1280 },
    { id: 'tablet', label: 'Tablet', previewWidth: 900 },
    { id: 'mobile', label: 'Mobile', previewWidth: 420 },
];

const getLayoutViewportFromWidth = (width) => {
    const safeWidth = Math.max(0, Number.parseInt(width, 10) || 0);
    if (safeWidth > 0 && safeWidth <= 600) return 'mobile';
    if (safeWidth > 0 && safeWidth <= 1024) return 'tablet';
    return 'desktop';
};

const layoutValueEqual = (a, b) => {
    const av = normalizeComponentLayout(a);
    const bv = normalizeComponentLayout(b);
    return (
        av.colStart === bv.colStart &&
        av.rowStart === bv.rowStart &&
        av.colSpan === bv.colSpan &&
        av.rowSpan === bv.rowSpan
    );
};

const extractViewportOverrides = (baseLayouts = {}, resolvedLayouts = {}) => {
    const nextOverrides = {};
    Object.keys(resolvedLayouts || {}).forEach((id) => {
        if (!layoutValueEqual(resolvedLayouts[id], baseLayouts[id])) {
            nextOverrides[id] = normalizeComponentLayout(resolvedLayouts[id]);
        }
    });
    return nextOverrides;
};

const getLayoutColumnCount = (layouts = {}, components = []) => {
    const validIds = new Set((components || []).map((comp, idx) => String(comp?.id ?? idx)));
    let maxColumn = 1;

    Object.entries(layouts || {}).forEach(([id, value]) => {
        if (validIds.size > 0 && !validIds.has(String(id)) && String(id) !== INLINE_ADD_DRAFT_COMPONENT_ID) {
            return;
        }
        const layout = normalizeComponentLayout(value);
        const rightEdge = layout.colStart != null
            ? layout.colStart + layout.colSpan - 1
            : layout.colSpan;
        maxColumn = Math.max(maxColumn, rightEdge);
    });

    return Math.max(1, maxColumn);
};


const TEMPLATES = [
    {
        id: 'frame1',
        label: 'Artist Template',
        description: 'Wide layout (min 1080px), light theme',
        frame: 'frame1',
        layout: {columnsCount: 'auto-fill', minWidthPx: 1080},
        themeMode: 'light',
        containerFrame: {
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: '#E5E7EB',
            borderRadius: 12,
            paddingUnit: 'px',
            paddingTop: 24,
            paddingRight: 24,
            paddingBottom: 24,
            paddingLeft: 24,
        }
    },
    {
        id: 'frame2',
        label: 'Creator Template',
        description: 'Cards layout (min 350px), dark theme',
        frame: 'frame2',
        layout: {columnsCount: 'auto-fill', minWidthPx: 350},
        themeMode: 'dark',
        containerFrame: {
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: '#E5E7EB',
            borderRadius: 12,
            paddingUnit: 'px',
            paddingValue: 24,
        }
    },
    {
        id: 'frame3',
        label: 'Builder Template',
        description: 'Two columns (min 700px), light theme',
        frame: 'frame3',
        layout: {columnsCount: 2, minWidthPx: 700},
        themeMode: 'light',
        containerFrame: {
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: '#E5E7EB',
            borderRadius: 12,
            paddingUnit: 'px',
            paddingValue: 24,
        }
    }
];

const ADD_COMPONENT_OPTIONS = [
    { type: 'text', label: 'Text' },
    { type: 'link', label: 'Link' },
    { type: 'social', label: 'Social Links' },
    { type: 'video', label: 'Video' },
    { type: 'image', label: 'Image' },
    { type: 'audio', label: 'Audio' },
    { type: 'donation', label: 'Donation' },
    { type: 'ad', label: 'Ad Panel' },
    { type: 'html', label: 'HTML' },
    { type: 'mint', label: 'Mint NFT' },
];

const LAYOUT_PRESETS = [
    {
        id: 'balanced',
        label: 'Balanced',
        columnsCount: 'auto-fill',
        minWidthPx: 360,
        gridGap: 20,
        gridRowHeightPx: 120,
        canvasMaxWidthPx: 1280,
    },
    {
        id: 'balanced-filled',
        label: 'Balanced Filled',
        columnsCount: 'auto-fill',
        minWidthPx: 360,
        gridGap: 20,
        gridRowHeightPx: 120,
        canvasMaxWidthPx: 1280,
        fillGaps: true,
    },
    {
        id: 'wide',
        label: 'Wide',
        columnsCount: 12,
        minWidthPx: 120,
        gridGap: 16,
        gridRowHeightPx: 100,
        canvasMaxWidthPx: 1600,
    },
    {
        id: 'wide-filled',
        label: 'Wide Filled',
        columnsCount: 12,
        minWidthPx: 120,
        gridGap: 16,
        gridRowHeightPx: 100,
        canvasMaxWidthPx: 1600,
        fillGaps: true,
    },
    {
        id: 'stacked',
        label: 'Stacked',
        columnsCount: 1,
        minWidthPx: 320,
        gridGap: 24,
        gridRowHeightPx: 140,
        canvasMaxWidthPx: 980,
    },
];

const GRID_LINE_COLOR_PRESETS = [
    {
        id: 'cyan',
        label: 'Cyan',
        strong: 'rgba(14, 165, 233, 0.28)',
        soft: 'rgba(14, 165, 233, 0.2)',
        outline: 'rgba(14, 165, 233, 0.26)',
    },
    {
        id: 'amber',
        label: 'Amber',
        strong: 'rgba(245, 158, 11, 0.3)',
        soft: 'rgba(245, 158, 11, 0.22)',
        outline: 'rgba(245, 158, 11, 0.28)',
    },
    {
        id: 'fuchsia',
        label: 'Fuchsia',
        strong: 'rgba(217, 70, 239, 0.3)',
        soft: 'rgba(217, 70, 239, 0.22)',
        outline: 'rgba(217, 70, 239, 0.28)',
    },
];


export default function MuultiPage() {
    // const {publicKey, connected, signMessage} = useWallet();
    const wallet = useWallet();

    const publicKey = wallet.publicKey;
    const connected = wallet.connected;
    const signMessage = wallet.signMessage;
    const selectedWalletName = wallet.wallet?.adapter?.name || '';
    const isEmailWallet = selectedWalletName === NATIVE_EMAIL_WALLET_NAME;

    const [step, setStep] = useState(0);
    const [userDocRef, setUserDocRef] = useState(null);
    const [muultiName, setMuultiName] = useState('');
    const [template, setTemplate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [pageTitle, setPageTitle] = useState('');
    const [pageDescription, setPageDescription] = useState('');
    const [alignment, setAlignment] = useState('left');
    const [profilePicFile, setProfilePicFile] = useState(null);
    const [profilePicUrl, setProfilePicUrl] = useState('');
    const [dockPosition, setDockPosition] = useState('top');
    const [columnsCount, setColumnsCount] = useState('auto-fill');
    const [minWidthPx, setMinWidthPx] = useState(400);
    // const [maxWidthPx,   setMaxWidthPx]   = useState(1000);

    const [components, setComponents] = useState([]);
    const [userIntendsEmpty, setUserIntendsEmpty] = useState(false);

    const [isAdding, setIsAdding] = useState(false);

    const [editingIndex, setEditingIndex] = useState(null);
    const [editingComp, setEditingComp] = useState(null);
    const [editingType, setEditingType] = useState('');

    const [addingType, setAddingType] = useState('text');
    const [newSections, setNewSections] = useState([]);
    const [newTextVariant, setNewTextVariant] = useState('text');
    const [newDisplayMode, setNewDisplayMode] = useState('full');
    const [newTitle, setNewTitle] = useState('');
    const [newUrl, setNewUrl] = useState('');

    const [newSocialLinks, setNewSocialLinks] = useState(buildEmptySocialLinks);
    const [newCustomSocialLinks, setNewCustomSocialLinks] = useState([]);

    const [newGateType, setNewGateType] = useState('public');
    const [newTokenIds, setNewTokenIds] = useState([]);
    const [newCollectionIds, setNewCollectionIds] = useState([]);
    const [newCompressedCollectionIds, setNewCompressedCollectionIds] = useState([]);
    const [newComponentStyle, setNewComponentStyle] = useState({});
    const [newComponentExtraGates, setNewComponentExtraGates] = useState([]);
    const [newComponentGateLogic, setNewComponentGateLogic] = useState('any');
    const [newComponentTimestampDisplay, setNewComponentTimestampDisplay] = useState('none');
    const [newComponentCoverUrl, setNewComponentCoverUrl] = useState('');
    const [newMediaMint, setNewMediaMint] = useState(null);
    const [newMediaCount, setNewMediaCount] = useState(null);
    const [newGateDetails, setNewGateDetails] = useState(buildDefaultNewGateDetails);

    const [newRpcEndpoint, setNewRpcEndpoint] = useState(process.env.REACT_APP_RPC_ENDPOINT || '');
    const [newMasterMint, setNewMasterMint] = useState('');
    const [newUpdateAuth, setNewUpdateAuth] = useState('');
    const [newDelegateRecord, setNewDelegateRecord] = useState('');
    const [newEditionRecipient, setNewEditionRecipient] = useState('');
    const [newDelegateKey, setNewDelegateKey] = useState('');
    const [newComputeUnits, setNewComputeUnits] = useState(800000);

    const [newVideoUrl, setNewVideoUrl] = useState('');
    const [featureImageUrl, setFeatureImageUrl] = useState('');
    const [newVideoTitle, setNewVideoTitle] = useState('');
    const [newVideoDescription, setNewVideoDescription] = useState('');

    const [newImageUrl, setNewImageUrl] = useState('');
    const [newImageAlt, setNewImageAlt] = useState('');
    const [newImageCaption, setNewImageCaption] = useState('');
    const [newImageItems, setNewImageItems] = useState([]);
    const [background, setBackground] = useState({       // default
        type: 'single',
        color: '#ffffff',
    });

    // at the top, alongside your other `useState` calls:
    const [newAudioUrl, setNewAudioUrl] = useState('');
    const [newAudioCover, setNewAudioCover] = useState('');
    const [newAudioTitle, setNewAudioTitle] = useState('');
    const [newAudioDescription, setNewAudioDescription] = useState('');

    const [newLegacyMasterMint, setNewLegacyMasterMint] = useState('');
    const [newLegacyUpdateAuthority, setNewLegacyUpdateAuthority] = useState('');
    const [newDelegateRecordPda, setNewDelegateRecordPda] = useState('');
    const [newDelegatePublicKey, setNewDelegatePublicKey] = useState('');
    const [newDelegatePrivateKey, setNewDelegatePrivateKey] = useState('');
    const [newLegacyPaymentPrice, setNewLegacyPaymentPrice] = useState('');
    const [newLegacyPaymentRecipient, setNewLegacyPaymentRecipient] = useState('');
    const [hasUnpublishedUpdates, sethasUnpublishedUpdates] = useState(false);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const [layout, setLayout] = useState(''); // which layout preset was chosen
    const [themeMode, setThemeMode] = useState('light'); // 'light' | 'dark'
    const [gridGap, setGridGap] = useState(null);
    const [advancedGridLayout, setAdvancedGridLayout] = useState(false);
    const [allowHorizontalScroll, setAllowHorizontalScroll] = useState(false);
    const [lockContainerWidth, setLockContainerWidth] = useState(true);
    const [showEditorGridLines, setShowEditorGridLines] = useState(false);
    const [gridLineColorPreset, setGridLineColorPreset] = useState('cyan');
    const [gridRowHeightPx, setGridRowHeightPx] = useState(120);
    const [canvasMaxWidthPx, setCanvasMaxWidthPx] = useState(0);
    const [componentLayouts, setComponentLayouts] = useState({});
    const [componentLayoutsTablet, setComponentLayoutsTablet] = useState({});
    const [componentLayoutsMobile, setComponentLayoutsMobile] = useState({});
    const [layoutEditViewport, setLayoutEditViewport] = useState('desktop');
    const [selectedComponentId, setSelectedComponentId] = useState(null);
    const [panelInteractionPreview, setPanelInteractionPreview] = useState(null);
    const [gridViewportWidth, setGridViewportWidth] = useState(0);
    const [directPriceByKey, setDirectPriceByKey] = useState({});
    const directPaymentDraftRef = useRef({});



// header live style states
    const [headerBackground, setHeaderBackground] = useState({
        type: 'color',
        color: '#ffffff',
        colors: ['#ffffff', '#000000'],
        direction: 'to right',
        imageUrl: '',
        sizing: 'cover',
        position: 'center',
        fontColor: '#000000',
        borderStyle: 'none',
        borderWidth: 0,
        borderColor: 'transparent',
        borderRadius: 0,
    });
    const [headerPadding, setHeaderPadding] = useState({
        mode: 'per-side',
        unit: 'px',
        all: 24,
        top: 24, right: 24, bottom: 24, left: 24,
    });
    const [componentListSettings, setComponentListSettings] = useState(() => normalizeComponentListSettings());
    const [showFollowerCount, setShowFollowerCount] = useState(false);

    const [activeFormIndex, setActiveFormIndex] = useState(null);
    const [componentEditorPanelOpen, setComponentEditorPanelOpen] = useState(false);
    const componentEditorPanelUndocked = true;
    const [editorViewportWidth, setEditorViewportWidth] = useState(() => {
        if (typeof window === 'undefined') return 0;
        return Math.max(0, Number.parseInt(window.innerWidth, 10) || 0);
    });
    const [mobileEditorView, setMobileEditorView] = useState('panel');

    const [gateLists, setGateLists] = useState([]);

    const [typography, setTypography] = useState(null);
    const [containerFrame, setContainerFrame] = useState({
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        borderRadius: 12,

        paddingUnit: 'px',
        paddingTop: 24,
        paddingRight: 24,
        paddingBottom: 24,
        paddingLeft: 24,
    });
    const [frameMenuOpen, setFrameMenuOpen] = useState(false);
    const [newMintName, setNewMintName] = useState('');
    const [newMintDescription, setNewMintDescription] = useState('');
    const [newMintCollectionId, setNewMintCollectionId] = useState(undefined);
    // Track which panel to scroll to after render
    const [scrollToId, setScrollToId] = useState(null);
    const [newImageNsfa, setNewImageNsfa] = useState(false);
    const [authUid, setAuthUid] = useState(null);
    const emailAuthReadyKey = isEmailWallet ? authUid : '';

    useEffect(() => {
        const unsub = auth.onAuthStateChanged((u) => setAuthUid(u?.uid || null));
        return () => unsub();
    }, []);

    const isVerifiedEditor = useMemo(() => {
        const pk = publicKey?.toString?.() || '';
        return !!pk && !!authUid && authUid === pk;
    }, [authUid, publicKey]);
    const canUsePanelLayoutControls = useMemo(
        () => !!connected && !!publicKey,
        [connected, publicKey]
    );



    const bgStyle = getBgStyle(background);
    const nowMs = () => Date.now();

    const componentsRef = useRef(components);
    const inlineDraftControlsRef = useRef(null);
    const componentsGridRef = useRef(null);
    const editorStageRef = useRef(null);
    const editorShellRef = useRef(null);
    const pinnedStageScrollRef = useRef(null);
    const scrollSyncingRef = useRef(false);
    const panelDragRef = useRef(null);
    const floatingEditorDragRef = useRef(null);
    const previousPanelRectsRef = useRef({});
    const [editorStageScrollMetrics, setEditorStageScrollMetrics] = useState({ clientWidth: 0, scrollWidth: 0 });
    const [componentEditorPanelViewportOffset, setComponentEditorPanelViewportOffset] = useState({ left: 8, top: 8 });
    const [componentEditorPanelUserMoved, setComponentEditorPanelUserMoved] = useState(false);
    const setComponentsSafe = (updater) => {
        setComponents((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            componentsRef.current = next;
            return next;
        });
    };

    useEffect(() => {
        componentsRef.current = components;
    }, [components]);

    // Loading replaces the grid DOM, so measurements must reattach when it returns.
    useLayoutEffect(() => {
        if (step !== 4 || loading) return;
        const updateGridWidth = () => {
            const gridEl = componentsGridRef.current;
            const nextWidth = gridEl
                ? Math.max(0, gridEl.getBoundingClientRect()?.width || 0)
                : 0;
            setGridViewportWidth((prev) => (prev === nextWidth ? prev : nextWidth));
        };

        updateGridWidth();
        let frame = null;
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            frame = window.requestAnimationFrame(updateGridWidth);
        }
        const gridEl = componentsGridRef.current;
        let observer = null;
        if (gridEl && typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => updateGridWidth());
            observer.observe(gridEl);
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', updateGridWidth);
        }
        return () => {
            if (frame != null && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
                window.cancelAnimationFrame(frame);
            }
            if (observer) observer.disconnect();
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', updateGridWidth);
            }
        };
    }, [
        allowHorizontalScroll,
        canvasMaxWidthPx,
        componentEditorPanelOpen,
        isAdding,
        layoutEditViewport,
        loading,
        lockContainerWidth,
        mobileEditorView,
        step,
    ]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const updateEditorViewportWidth = () => {
            const nextWidth = Math.max(0, Number.parseInt(window.innerWidth, 10) || 0);
            setEditorViewportWidth((prev) => (prev === nextWidth ? prev : nextWidth));
        };
        updateEditorViewportWidth();
        window.addEventListener('resize', updateEditorViewportWidth);
        return () => window.removeEventListener('resize', updateEditorViewportWidth);
    }, []);
    const syncPinnedScrollbarFromStage = useCallback(() => {
        const stageEl = editorStageRef.current;
        const pinnedEl = pinnedStageScrollRef.current;
        if (!stageEl || !pinnedEl) return;
        if (scrollSyncingRef.current) return;
        scrollSyncingRef.current = true;
        pinnedEl.scrollLeft = stageEl.scrollLeft;
        scrollSyncingRef.current = false;
    }, []);
    const syncStageFromPinnedScrollbar = useCallback(() => {
        const stageEl = editorStageRef.current;
        const pinnedEl = pinnedStageScrollRef.current;
        if (!stageEl || !pinnedEl) return;
        if (scrollSyncingRef.current) return;
        scrollSyncingRef.current = true;
        stageEl.scrollLeft = pinnedEl.scrollLeft;
        scrollSyncingRef.current = false;
    }, []);
    useLayoutEffect(() => {
        if (step !== 4 || loading) return;
        const stageEl = editorStageRef.current;
        if (!stageEl) return;

        const updateMetrics = () => {
            const stageTrackEl = stageEl.querySelector('.muulti-editor-stage-track');
            const nextClient = Math.max(0, Math.round(stageEl.clientWidth || 0));
            const nextScroll = Math.max(
                0,
                Math.round(Math.max(
                    stageEl.scrollWidth || 1,
                    stageTrackEl?.scrollWidth || 1,
                )),
            );
            setEditorStageScrollMetrics((prev) => (
                prev.clientWidth === nextClient && prev.scrollWidth === nextScroll
                    ? prev
                    : { clientWidth: nextClient, scrollWidth: nextScroll }
            ));
        };

        const handleStageScroll = () => {
            syncPinnedScrollbarFromStage();
            updateMetrics();
        };

        updateMetrics();
        syncPinnedScrollbarFromStage();
        stageEl.addEventListener('scroll', handleStageScroll, { passive: true });
        let observer = null;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => updateMetrics());
            observer.observe(stageEl);
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', updateMetrics);
        }
        return () => {
            stageEl.removeEventListener('scroll', handleStageScroll);
            if (observer) observer.disconnect();
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', updateMetrics);
            }
        };
    }, [step, loading, syncPinnedScrollbarFromStage, componentEditorPanelOpen, componentEditorPanelUndocked]);
    useLayoutEffect(() => {
        if (step !== 4 || loading) return;
        if (!componentEditorPanelUndocked) return;
        if (componentEditorPanelUserMoved) return;
        const shellEl = editorShellRef.current;
        if (!shellEl) return;

        const updateEditorPanelViewportOffset = () => {
            const rect = shellEl.getBoundingClientRect();
            const nextLeft = Math.max(8, Math.round(rect.left || 0));
            const nextTop = 8;
            setComponentEditorPanelViewportOffset((prev) => (
                prev.left === nextLeft && prev.top === nextTop
                    ? prev
                    : { left: nextLeft, top: nextTop }
            ));
        };

        updateEditorPanelViewportOffset();
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', updateEditorPanelViewportOffset);
        }
        let observer = null;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(updateEditorPanelViewportOffset);
            observer.observe(shellEl);
        }
        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', updateEditorPanelViewportOffset);
            }
            if (observer) observer.disconnect();
        };
    }, [componentEditorPanelUndocked, componentEditorPanelUserMoved, loading, step]);

    const detectedLayoutViewport = useMemo(() => {
        const fallbackWidth = typeof window !== 'undefined'
            ? Math.max(0, Number.parseInt(window.innerWidth, 10) || 0)
            : 0;
        return getLayoutViewportFromWidth(gridViewportWidth || fallbackWidth);
    }, [gridViewportWidth]);
    const activeViewportOverrideLayouts = useMemo(() => {
        if (layoutEditViewport === 'tablet') return componentLayoutsTablet || {};
        if (layoutEditViewport === 'mobile') return componentLayoutsMobile || {};
        return {};
    }, [componentLayoutsMobile, componentLayoutsTablet, layoutEditViewport]);
    const isMobileEditorViewport = useMemo(
        () => editorViewportWidth > 0 && editorViewportWidth <= 900,
        [editorViewportWidth]
    );
    useEffect(() => {
        if (!isMobileEditorViewport) return;
        setMobileEditorView('panel');
    }, [isMobileEditorViewport]);
    const activeViewportLayouts = useMemo(() => {
        if (layoutEditViewport === 'desktop') return componentLayouts || {};
        return {
            ...(componentLayouts || {}),
            ...(activeViewportOverrideLayouts || {}),
        };
    }, [activeViewportOverrideLayouts, componentLayouts, layoutEditViewport]);
    const activeEditViewportOption = useMemo(() => {
        return LAYOUT_VIEWPORT_OPTIONS.find((option) => option.id === layoutEditViewport) || LAYOUT_VIEWPORT_OPTIONS[0];
    }, [layoutEditViewport]);
    const isMobilePreviewMode = isMobileEditorViewport && mobileEditorView === 'preview';
    const hasComponentLayouts = useMemo(
        () =>
            Object.keys(componentLayouts || {}).length > 0 ||
            Object.keys(componentLayoutsTablet || {}).length > 0 ||
            Object.keys(componentLayoutsMobile || {}).length > 0,
        [componentLayouts, componentLayoutsMobile, componentLayoutsTablet]
    );
    const shouldUseAdvancedLayout = advancedGridLayout || hasComponentLayouts;
    const layoutColumnCountFallback = useMemo(
        () => getLayoutColumnCount(activeViewportLayouts || {}, components),
        [activeViewportLayouts, components]
    );
    const editorPreviewCanvasWidthPx = useMemo(() => {
        if (!isMobilePreviewMode) return 0;
        const viewportPreviewWidth = toPositiveInt(activeEditViewportOption?.previewWidth, 0);
        const canvasWidth = toPositiveInt(canvasMaxWidthPx, 0);
        if (viewportPreviewWidth > 0 && canvasWidth > 0) {
            return Math.min(viewportPreviewWidth, canvasWidth);
        }
        return Math.max(viewportPreviewWidth, canvasWidth);
    }, [activeEditViewportOption?.previewWidth, canvasMaxWidthPx, isMobilePreviewMode]);
    const explicitColumnsCount = useMemo(() => {
        const parsed = Number.parseInt(columnsCount, 10);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
        return null;
    }, [columnsCount]);
    const safeMinColumnWidthPx = Math.max(80, toPositiveInt(minWidthPx, 400));
    const responsiveMinColumnWidthPx = useMemo(() => {
        if (!isMobileEditorViewport || isMobilePreviewMode) return safeMinColumnWidthPx;
        const measuredWidth = Math.max(
            0,
            editorStageScrollMetrics.clientWidth || gridViewportWidth || editorViewportWidth || 0
        );
        if (measuredWidth <= 0) return Math.min(safeMinColumnWidthPx, 280);
        // Keep one-column mobile preview from pushing the stage wider than the viewport.
        const availableWidth = Math.max(120, measuredWidth - 32);
        return Math.min(safeMinColumnWidthPx, availableWidth);
    }, [editorStageScrollMetrics.clientWidth, editorViewportWidth, gridViewportWidth, isMobileEditorViewport, isMobilePreviewMode, safeMinColumnWidthPx]);
    const componentGridTemplateColumns = useMemo(() => {
        if (isMobileEditorViewport && !isMobilePreviewMode && !allowHorizontalScroll) {
            return 'minmax(0, 1fr)';
        }
        if (explicitColumnsCount != null) {
            if (shouldUseAdvancedLayout && !allowHorizontalScroll) {
                return `repeat(${explicitColumnsCount}, minmax(0, 1fr))`;
            }
            return `repeat(${explicitColumnsCount}, minmax(${responsiveMinColumnWidthPx}px, 1fr))`;
        }
        return `repeat(auto-fill, minmax(${responsiveMinColumnWidthPx}px, 1fr))`;
    }, [
        allowHorizontalScroll,
        explicitColumnsCount,
        isMobileEditorViewport,
        isMobilePreviewMode,
        responsiveMinColumnWidthPx,
        shouldUseAdvancedLayout,
    ]);
    const activeGridLineColors = useMemo(() => {
        return GRID_LINE_COLOR_PRESETS.find((preset) => preset.id === gridLineColorPreset) || GRID_LINE_COLOR_PRESETS[0];
    }, [gridLineColorPreset]);

    const getRenderedColumnCount = useCallback(() => {
        if (isMobileEditorViewport && !isMobilePreviewMode && !allowHorizontalScroll) return 1;
        const parsed = Number.parseInt(columnsCount, 10);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
        const gridEl = componentsGridRef.current;
        const observedWidth = Math.max(0, gridViewportWidth || 0);
        if (gridEl) {
            const rect = gridEl.getBoundingClientRect();
            const width = observedWidth || rect?.width || 0;
            const gap = Math.max(0, Number.parseInt(gridGap, 10) || 0);
            const minCol = responsiveMinColumnWidthPx;
            if (width > 0) {
                return Math.max(1, Math.floor((width + gap) / (minCol + gap)));
            }
        } else if (observedWidth > 0) {
            const gap = Math.max(0, Number.parseInt(gridGap, 10) || 0);
            const minCol = responsiveMinColumnWidthPx;
            return Math.max(1, Math.floor((observedWidth + gap) / (minCol + gap)));
        }
        return shouldUseAdvancedLayout ? layoutColumnCountFallback : 1;
    }, [
        allowHorizontalScroll,
        columnsCount,
        gridGap,
        gridViewportWidth,
        isMobileEditorViewport,
        isMobilePreviewMode,
        layoutColumnCountFallback,
        responsiveMinColumnWidthPx,
        shouldUseAdvancedLayout,
    ]);

    const getPlacementColumnLimit = useCallback((renderedColumns) => {
        const visibleCols = Math.max(1, toPositiveInt(renderedColumns, 1));
        if (!allowHorizontalScroll) return visibleCols;
        const explicit = Number.parseInt(columnsCount, 10);
        if (Number.isFinite(explicit) && explicit > 0) {
            return Math.max(visibleCols, explicit);
        }
        return Math.max(visibleCols, 48);
    }, [allowHorizontalScroll, columnsCount]);

    const getGridMetrics = useCallback(() => {
        const renderedColumns = getRenderedColumnCount();
        const safeGap = Math.max(0, Number.parseInt(gridGap, 10) || 0);
        const safeRow = Math.max(40, toPositiveInt(gridRowHeightPx, 120));
        const gridEl = componentsGridRef.current;
        if (!gridEl) {
            return {
                renderedColumns,
                gapPx: safeGap,
                cellWidth: Math.max(1, minWidthPx || 1),
                rowStepPx: safeRow + safeGap,
            };
        }
        const rect = gridEl.getBoundingClientRect();
        const totalGap = safeGap * Math.max(0, renderedColumns - 1);
        const cellWidth = Math.max(1, (rect.width - totalGap) / renderedColumns);
        return {
            renderedColumns,
            gapPx: safeGap,
            cellWidth,
            rowStepPx: safeRow + safeGap,
        };
    }, [getRenderedColumnCount, gridGap, gridRowHeightPx, minWidthPx]);

    const getComponentLayout = useCallback((compId) => {
        return normalizeComponentLayout(activeViewportLayouts?.[String(compId)]);
    }, [activeViewportLayouts]);

    const resolveViewportLayouts = useCallback((inputLayouts, placementColumns, priorityCompId = null) => {
        return resolveCollisionSafeLayouts(
            components,
            inputLayouts || {},
            placementColumns,
            priorityCompId,
            allowHorizontalScroll
        );
    }, [allowHorizontalScroll, components]);

    const collisionSafeComponentLayouts = useMemo(() => {
        if (!shouldUseAdvancedLayout) return {};
        const renderedColumns = getRenderedColumnCount();
        const placementColumns = getPlacementColumnLimit(renderedColumns);
        return resolveCollisionSafeLayouts(
            components,
            activeViewportLayouts || {},
            placementColumns,
            null,
            allowHorizontalScroll
        );
    }, [
        activeViewportLayouts,
        allowHorizontalScroll,
        components,
        getPlacementColumnLimit,
        getRenderedColumnCount,
        shouldUseAdvancedLayout,
    ]);

    const upsertComponentLayout = useCallback((compId, updater) => {
        if (compId == null) return;
        const key = String(compId);
        const renderedColumns = getRenderedColumnCount();
        const placementColumns = getPlacementColumnLimit(renderedColumns);
        if (layoutEditViewport === 'desktop') {
            setComponentLayouts((prev) => {
                const current = normalizeComponentLayout(prev?.[key]);
                const draft = typeof updater === 'function'
                    ? updater(current)
                    : { ...current, ...(updater || {}) };
                const next = clampComponentLayout(draft, placementColumns, allowHorizontalScroll);
                const merged = { ...(prev || {}), [key]: next };
                const resolved = resolveViewportLayouts(merged, placementColumns, key);
                return layoutsEqual(prev, resolved) ? prev : resolved;
            });
        } else {
            const setOverrides = layoutEditViewport === 'tablet'
                ? setComponentLayoutsTablet
                : setComponentLayoutsMobile;
            const baseLayouts = componentLayouts || {};
            setOverrides((prevOverride) => {
                const mergedLayouts = { ...baseLayouts, ...(prevOverride || {}) };
                const current = normalizeComponentLayout(mergedLayouts?.[key]);
                const draft = typeof updater === 'function'
                    ? updater(current)
                    : { ...current, ...(updater || {}) };
                const next = clampComponentLayout(draft, placementColumns, allowHorizontalScroll);
                const merged = { ...mergedLayouts, [key]: next };
                const resolvedMerged = resolveViewportLayouts(merged, placementColumns, key);
                const nextOverride = extractViewportOverrides(baseLayouts, resolvedMerged);
                return layoutsEqual(prevOverride || {}, nextOverride) ? prevOverride : nextOverride;
            });
        }
        setAdvancedGridLayout(true);
        sethasUnpublishedUpdates(true);
    }, [
        allowHorizontalScroll,
        componentLayouts,
        getPlacementColumnLimit,
        getRenderedColumnCount,
        layoutEditViewport,
        resolveViewportLayouts,
    ]);

    const beginPanelInteraction = useCallback((event, compId, mode = 'move') => {
        if (!canUsePanelLayoutControls || compId == null) return;
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();

        const key = String(compId);
        const metrics = getGridMetrics();
        const placementColumns = getPlacementColumnLimit(metrics.renderedColumns);
        const startX = event.clientX;
        const startY = event.clientY;
        const panelEl = typeof document !== 'undefined'
            ? document.getElementById(`panel-${key}`)
            : null;
        const computed = panelEl && typeof window !== 'undefined'
            ? window.getComputedStyle(panelEl)
            : null;
        const renderedColStart = toOptionalPositiveInt(computed?.gridColumnStart);
        const renderedRowStart = toOptionalPositiveInt(computed?.gridRowStart);
        const initialLayout = normalizeComponentLayout(activeViewportLayouts?.[key]);
        const initial = {
            ...initialLayout,
            colStart: initialLayout.colStart ?? renderedColStart ?? 1,
            rowStart: initialLayout.rowStart ?? renderedRowStart ?? 1,
        };
        setPanelInteractionPreview({
            compId: key,
            mode,
            layout: {
                colStart: initial.colStart ?? 1,
                rowStart: initial.rowStart ?? 1,
                colSpan: Math.max(1, initial.colSpan || 1),
                rowSpan: Math.max(1, initial.rowSpan || 1),
            },
        });

        const toDeltaCols = (dx) => Math.round(dx / Math.max(1, metrics.cellWidth + metrics.gapPx));
        const toDeltaRows = (dy) => Math.round(dy / Math.max(1, metrics.rowStepPx));
        let lastAppliedCol = initial.colStart ?? 1;
        let lastAppliedRow = initial.rowStart ?? 1;

        const handleMove = (moveEvent) => {
            const dx = moveEvent.clientX - startX;
            const dy = moveEvent.clientY - startY;

            const deltaCols = toDeltaCols(dx);
            const deltaRows = toDeltaRows(dy);
            let draft = { ...initial };

            if (mode === 'move') {
                const startCol = draft.colStart ?? 1;
                const startRow = draft.rowStart ?? 1;
                draft.colStart = startCol + deltaCols;
                draft.rowStart = startRow + deltaRows;
            }
            if (mode === 'resize-x' || mode === 'resize-both') {
                const nextColSpan = Math.max(1, initial.colSpan + deltaCols);
                draft.colSpan = nextColSpan;
            }
            if (mode === 'resize-y' || mode === 'resize-both') {
                const nextRowSpan = Math.max(1, initial.rowSpan + deltaRows);
                draft.rowSpan = nextRowSpan;
            }

            if (!allowHorizontalScroll) {
                const anchoredColStart = Math.max(1, draft.colStart ?? initial.colStart ?? 1);
                const maxVisibleSpan = Math.max(1, placementColumns - anchoredColStart + 1);
                if (draft.colSpan > maxVisibleSpan) {
                    draft.colSpan = maxVisibleSpan;
                }
            }

            if (draft.rowStart != null) draft.rowStart = Math.max(1, draft.rowStart);
            draft = clampComponentLayout(draft, placementColumns, allowHorizontalScroll);

            if (mode === 'move') {
                const nextCol = draft.colStart ?? 1;
                const nextRow = draft.rowStart ?? 1;
                if (nextCol === lastAppliedCol && nextRow === lastAppliedRow) {
                    return;
                }
                lastAppliedCol = nextCol;
                lastAppliedRow = nextRow;
            }

            let previewLayout = null;
            if (layoutEditViewport === 'desktop') {
                setComponentLayouts((prev) => {
                    const merged = { ...(prev || {}), [key]: draft };
                    const resolved = resolveViewportLayouts(merged, placementColumns, key);
                    previewLayout = normalizeComponentLayout(resolved?.[key]);
                    return layoutsEqual(prev, resolved) ? prev : resolved;
                });
            } else {
                const setOverrides = layoutEditViewport === 'tablet'
                    ? setComponentLayoutsTablet
                    : setComponentLayoutsMobile;
                const baseLayouts = componentLayouts || {};
                setOverrides((prevOverride) => {
                    const mergedLayouts = { ...baseLayouts, ...(prevOverride || {}) };
                    const merged = { ...mergedLayouts, [key]: draft };
                    const resolvedMerged = resolveViewportLayouts(merged, placementColumns, key);
                    previewLayout = normalizeComponentLayout(resolvedMerged?.[key]);
                    const nextOverride = extractViewportOverrides(baseLayouts, resolvedMerged);
                    return layoutsEqual(prevOverride || {}, nextOverride) ? prevOverride : nextOverride;
                });
            }
            if (previewLayout) {
                setPanelInteractionPreview({
                    compId: key,
                    mode,
                    layout: {
                        colStart: previewLayout.colStart ?? 1,
                        rowStart: previewLayout.rowStart ?? 1,
                        colSpan: Math.max(1, previewLayout.colSpan || 1),
                        rowSpan: Math.max(1, previewLayout.rowSpan || 1),
                    },
                });
            }
        };

        const handleUp = () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
            panelDragRef.current = null;
            setPanelInteractionPreview(null);
            setAdvancedGridLayout(true);
            sethasUnpublishedUpdates(true);
        };

        if (panelDragRef.current?.cleanup) {
            panelDragRef.current.cleanup();
        }
        panelDragRef.current = {
            cleanup: () => {
                window.removeEventListener('mousemove', handleMove);
                window.removeEventListener('mouseup', handleUp);
            },
        };
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
    }, [
        activeViewportLayouts,
        allowHorizontalScroll,
        canUsePanelLayoutControls,
        componentLayouts,
        getGridMetrics,
        getPlacementColumnLimit,
        layoutEditViewport,
        resolveViewportLayouts,
    ]);

    const getComponentWrapperStyle = useCallback((compId) => {
        if (!shouldUseAdvancedLayout || compId == null) return {};
        const renderedColumns = getRenderedColumnCount();
        const placementColumns = getPlacementColumnLimit(renderedColumns);
        const layoutValue = collisionSafeComponentLayouts?.[String(compId)] || clampComponentLayout(
            normalizeComponentLayout(activeViewportLayouts?.[String(compId)]),
            placementColumns,
            allowHorizontalScroll
        );
        const rowHeight = Math.max(40, toPositiveInt(gridRowHeightPx, 120));
        const style = {
            gridColumn: layoutValue.colStart != null
                ? `${layoutValue.colStart} / span ${layoutValue.colSpan}`
                : `span ${layoutValue.colSpan}`,
            gridRow: layoutValue.rowStart != null
                ? `${layoutValue.rowStart} / span ${layoutValue.rowSpan}`
                : `span ${layoutValue.rowSpan}`,
            minHeight: `${layoutValue.rowSpan * rowHeight}px`,
            position: 'relative',
        };
        return style;
    }, [
        activeViewportLayouts,
        allowHorizontalScroll,
        collisionSafeComponentLayouts,
        getPlacementColumnLimit,
        getRenderedColumnCount,
        gridRowHeightPx,
        shouldUseAdvancedLayout,
    ]);

    const applyLayoutPreset = useCallback((preset) => {
        if (!preset) return;
        setColumnsCount(preset.columnsCount);
        setMinWidthPx(preset.minWidthPx);
        setGridGap(preset.gridGap);
        setGridRowHeightPx(preset.gridRowHeightPx);
        setCanvasMaxWidthPx(preset.canvasMaxWidthPx);
        if (preset.fillGaps) {
            const parsedPresetColumns = Number.parseInt(preset.columnsCount, 10);
            const targetColumns = Number.isFinite(parsedPresetColumns) && parsedPresetColumns > 0
                ? parsedPresetColumns
                : getRenderedColumnCount();
            const safeTargetColumns = Math.max(1, toPositiveInt(targetColumns, 1));
            if (layoutEditViewport === 'desktop') {
                setComponentLayouts((prev) => {
                    const safeLayouts = resolveViewportLayouts(prev || {}, safeTargetColumns);
                    const compacted = compactLayoutsToTopLeft(
                        components,
                        safeLayouts,
                        safeTargetColumns,
                        allowHorizontalScroll
                    );
                    return layoutsEqual(prev, compacted) ? prev : compacted;
                });
            } else {
                const setOverrides = layoutEditViewport === 'tablet'
                    ? setComponentLayoutsTablet
                    : setComponentLayoutsMobile;
                const baseLayouts = componentLayouts || {};
                setOverrides((prevOverride) => {
                    const mergedLayouts = { ...baseLayouts, ...(prevOverride || {}) };
                    const safeLayouts = resolveViewportLayouts(mergedLayouts, safeTargetColumns);
                    const compacted = compactLayoutsToTopLeft(
                        components,
                        safeLayouts,
                        safeTargetColumns,
                        allowHorizontalScroll
                    );
                    const nextOverride = extractViewportOverrides(baseLayouts, compacted);
                    return layoutsEqual(prevOverride || {}, nextOverride) ? prevOverride : nextOverride;
                });
            }
        }
        setAdvancedGridLayout(true);
        sethasUnpublishedUpdates(true);
    }, [
        allowHorizontalScroll,
        componentLayouts,
        components,
        getRenderedColumnCount,
        layoutEditViewport,
        resolveViewportLayouts,
    ]);

    const applyPanelArrowAction = useCallback((compId, deltaCol, deltaRow, resize = false) => {
        if (!canUsePanelLayoutControls || compId == null) return;
        upsertComponentLayout(compId, (current) => {
            if (resize) {
                return {
                    ...current,
                    colSpan: Math.max(1, (current.colSpan || 1) + deltaCol),
                    rowSpan: Math.max(1, (current.rowSpan || 1) + deltaRow),
                };
            }
            return {
                ...current,
                colStart: (current.colStart ?? 1) + deltaCol,
                rowStart: (current.rowStart ?? 1) + deltaRow,
            };
        });
    }, [canUsePanelLayoutControls, upsertComponentLayout]);

    useEffect(() => {
        if (!canUsePanelLayoutControls || selectedComponentId == null) return;
        const handleKeyDown = (event) => {
            if (event.defaultPrevented) return;
            const target = event.target;
            const tag = target?.tagName?.toLowerCase?.() || '';
            if (tag === 'input' || tag === 'textarea' || tag === 'select' || target?.isContentEditable) return;

            let deltaCol = 0;
            let deltaRow = 0;
            if (event.key === 'ArrowLeft') deltaCol = -1;
            if (event.key === 'ArrowRight') deltaCol = 1;
            if (event.key === 'ArrowUp') deltaRow = -1;
            if (event.key === 'ArrowDown') deltaRow = 1;
            if (!deltaCol && !deltaRow) return;

            event.preventDefault();
            if (event.shiftKey) {
                applyPanelArrowAction(selectedComponentId, deltaCol, deltaRow, true);
                return;
            }
            applyPanelArrowAction(selectedComponentId, deltaCol, deltaRow, false);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [applyPanelArrowAction, canUsePanelLayoutControls, selectedComponentId]);

    useEffect(() => {
        if (loading) return;
        const validIds = new Set((components || []).map((comp, idx) => String(comp?.id ?? idx)));
        setComponentLayouts((prev) => {
            const next = {};
            Object.entries(prev || {}).forEach(([key, value]) => {
                if (validIds.has(key)) {
                    next[key] = value;
                }
            });
            const hadAnyPrev = Object.keys(prev || {}).length > 0;
            const hasAnyNext = Object.keys(next).length > 0;
            if (!hasAnyNext) {
                return hadAnyPrev ? {} : prev;
            }
            return layoutsEqual(prev, next) ? prev : next;
        });
        setComponentLayoutsTablet((prevOverride) => {
            const nextOverride = {};
            Object.entries(prevOverride || {}).forEach(([key, value]) => {
                if (validIds.has(key)) {
                    nextOverride[key] = value;
                }
            });
            return layoutsEqual(prevOverride || {}, nextOverride) ? prevOverride : nextOverride;
        });
        setComponentLayoutsMobile((prevOverride) => {
            const nextOverride = {};
            Object.entries(prevOverride || {}).forEach(([key, value]) => {
                if (validIds.has(key)) {
                    nextOverride[key] = value;
                }
            });
            return layoutsEqual(prevOverride || {}, nextOverride) ? prevOverride : nextOverride;
        });
        if (selectedComponentId != null && !validIds.has(String(selectedComponentId))) {
            setSelectedComponentId(null);
        }
    }, [
        components,
        loading,
        selectedComponentId,
    ]);

    useEffect(() => {
        return () => {
            if (panelDragRef.current?.cleanup) {
                panelDragRef.current.cleanup();
            }
            if (floatingEditorDragRef.current?.cleanup) {
                floatingEditorDragRef.current.cleanup();
            }
        };
    }, []);

    useLayoutEffect(() => {
        if (step !== 4 || loading || !shouldUseAdvancedLayout) {
            previousPanelRectsRef.current = {};
            return;
        }
        const nextRects = {};
        (components || []).forEach((comp, idx) => {
            const id = String(comp?.id ?? idx);
            const el = typeof document !== 'undefined'
                ? document.getElementById(`panel-${id}`)
                : null;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            nextRects[id] = rect;
            const prevRect = previousPanelRectsRef.current[id];
            if (!prevRect) return;
            const dx = prevRect.left - rect.left;
            const dy = prevRect.top - rect.top;
            if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

            el.style.transition = 'none';
            el.style.transform = `translate(${dx}px, ${dy}px)`;
            if (typeof window !== 'undefined') {
                window.requestAnimationFrame(() => {
                    el.style.transition = 'transform 180ms ease';
                    el.style.transform = '';
                });
            }
        });
        previousPanelRectsRef.current = nextRects;
    }, [activeViewportLayouts, collisionSafeComponentLayouts, components, loading, shouldUseAdvancedLayout, step]);

    const addSlotGridRowStart = useMemo(() => {
        if (!shouldUseAdvancedLayout) return null;
        const layoutSource = Object.keys(collisionSafeComponentLayouts || {}).length > 0
            ? collisionSafeComponentLayouts
            : activeViewportLayouts;
        let maxRowEnd = 0;
        (components || []).forEach((comp, idx) => {
            const id = String(comp?.id ?? idx);
            const layoutValue = normalizeComponentLayout(layoutSource?.[id]);
            const rowStart = layoutValue.rowStart ?? 1;
            const rowEnd = rowStart + layoutValue.rowSpan - 1;
            if (rowEnd > maxRowEnd) maxRowEnd = rowEnd;
        });
        return Math.max(1, maxRowEnd + 1);
    }, [activeViewportLayouts, collisionSafeComponentLayouts, components, shouldUseAdvancedLayout]);
    const liveGridMetrics = shouldUseAdvancedLayout ? getGridMetrics() : null;
    const snapPreviewStyle = useMemo(() => {
        if (!shouldUseAdvancedLayout || !liveGridMetrics || !panelInteractionPreview?.layout) return null;
        const preview = panelInteractionPreview.layout;
        const colStart = Math.max(1, preview.colStart || 1);
        const rowStart = Math.max(1, preview.rowStart || 1);
        const colSpan = Math.max(1, preview.colSpan || 1);
        const rowSpan = Math.max(1, preview.rowSpan || 1);
        const gapPx = Math.max(0, liveGridMetrics.gapPx || 0);
        const cellWidth = Math.max(1, liveGridMetrics.cellWidth || 1);
        const rowHeight = Math.max(40, toPositiveInt(gridRowHeightPx, 120));
        const colStep = cellWidth + gapPx;
        const rowStep = rowHeight + gapPx;
        return {
            left: `${Math.max(0, (colStart - 1) * colStep)}px`,
            top: `${Math.max(0, (rowStart - 1) * rowStep)}px`,
            width: `${Math.max(12, (colSpan * cellWidth) + ((colSpan - 1) * gapPx))}px`,
            height: `${Math.max(12, (rowSpan * rowHeight) + ((rowSpan - 1) * gapPx))}px`,
        };
    }, [gridRowHeightPx, liveGridMetrics, panelInteractionPreview, shouldUseAdvancedLayout]);

    const handleDirectPaymentDraftChange = useCallback((payload) => {
        const { compId, index, scope, extraIndex, priceSol } = payload || {};
        const key = `${String(compId ?? index ?? "")}:${scope || "primary"}:${extraIndex ?? ""}`;
        directPaymentDraftRef.current[key] = Number(priceSol || 0);
    }, []);



    const requireDeterministicAuth = useCallback(async () => {
        if (!connected || !publicKey) throw new Error("Wallet not connected");

        const walletUid = publicKey.toBase58();

        // already correct identity
        if (auth?.currentUser?.uid === walletUid) return walletUid;

        await openPublishSessionForWallet(wallet);

        if (auth?.currentUser?.uid !== walletUid) {
            throw new Error("Auth UID does not match wallet public key after publish session.");
        }

        return walletUid;
    }, [connected, publicKey, signMessage, wallet?.adapter]);

    const [selectedGateListId, setSelectedGateListId] = useState('');

    // Upload callbacks and preview share the latest Controls metadata.
    const inlineDraftControls = useMemo(() => ({
        gate: buildDraftGate({
            type: newGateType || 'public', tokenIds: newTokenIds, collectionIds: newCollectionIds,
            compressedCollectionIds: newCompressedCollectionIds, listId: selectedGateListId || '',
            details: newGateDetails,
        }, publicKey?.toBase58?.() || String(auth?.currentUser?.uid || ''), INLINE_ADD_DRAFT_COMPONENT_ID),
        extraGates: newComponentExtraGates || [],
        gateLogic: newComponentGateLogic || 'any',
        style: newComponentStyle || {},
        coverUrl: newComponentCoverUrl || '',
        mediaMint: newMediaMint,
        timestampDisplay: newComponentTimestampDisplay || 'none',
        displayMode: newDisplayMode || 'full',
    }), [
        newGateType, newTokenIds, newCollectionIds, newCompressedCollectionIds,
        selectedGateListId, newGateDetails, publicKey, newComponentExtraGates,
        newComponentGateLogic, newComponentStyle, newComponentCoverUrl,
        newComponentTimestampDisplay, newDisplayMode, newMediaMint,
    ]);
    inlineDraftControlsRef.current = inlineDraftControls;

    const scheduleScrollTo = useCallback((elementId) => {
        if (!elementId) return;
        setScrollToId(elementId);
    }, []);
    const [activeEditor, setActiveEditor] = useState(null);
    const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
    const openHeaderEditor = () => setActiveEditor('header');
    const openContainerEditor = () => setActiveEditor('container');
    const openBackgroundEditor = () => setActiveEditor('background');
    const openLayoutEditor = () => setActiveEditor('layout');
    const closeWorkbench = () => setActiveEditor(null);

    // Workbench state
    // const [wbOpen, setWbOpen] = useState(false);
    // const [wbTitle, setWbTitle] = useState('');
    // const [wbBody, setWbBody]   = useState(null);

// open helpers
    const openBackgroundWB = () => {


        openHeaderEditor()
    };

    // const openHeaderWB = () => {
    //     setWbTitle('Header');
    //     setWbBody(
    //         <HeaderEditor
    //             key="wb-header"
    //             profilePicUrl={profilePicUrl}
    //             pageTitle={pageTitle}
    //             pageDescription={pageDescription}
    //             alignment={alignment}
    //             headerBackground={headerBackground}          // ✅ pass current value
    //             onHeaderBgChange={setHeaderBackground}       // ✅ pass setter so UI switches
    //             onFileChange={file => setProfilePicFile(file)}
    //             onTitleChange={setPageTitle}
    //             onDescriptionChange={setPageDescription}
    //             onAlignmentChange={setAlignment}
    //             onSave={async () => { await handleSaveHeader(); setWbOpen(false); }}
    //         />
    //     );
    //     setWbOpen(true);
    // };


    useEffect(() => {
        if (!scrollToId) return;
        const doScroll = () => {
            const node = document.getElementById(scrollToId);
            if (node) node.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
            setScrollToId(null);
        };
        requestAnimationFrame(doScroll);
    }, [components, scrollToId]);


    useEffect(() => {
        getDocs(collection(db, 'gateLists'))
            .then(snap => setGateLists(snap.docs.map(d => ({id: d.id, ...d.data()}))));


    }, []);

    const handleTimestampDisplayChange = (idx, value) => {
        setComponents(cs => {
            const copy = [...cs];
            copy[idx] = {...copy[idx], timestampDisplay: value, updatedAt: Date.now()};
            return copy;
        });
        sethasUnpublishedUpdates(true);
    };

// // After components render, if we requested a scroll, do it smoothly
//     useEffect(() => {
//         if (scrollToId == null) return;
//
//         // Use the wrapper id we render below: panel-<id>
//         const el = document.getElementById(`panel-${scrollToId}`);
//         if (el) {
//             // Give React a tick to paint
//             requestAnimationFrame(() => {
//                 el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
//                 setScrollToId(null); // reset
//             });
//         } else {
//             // If not yet in DOM, try one more tick (very rare)
//             requestAnimationFrame(() => {
//                 const el2 = document.getElementById(`panel-${scrollToId}`);
//                 if (el2) el2.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
//                 setScrollToId(null);
//             });
//         }
//     }, [components, scrollToId]);


    // 1) Fetch or create user doc on connect
    useEffect(() => {

        if (!connected || !publicKey) {
            setStep(0);
            setUserDocRef(null);
            setLoading(false);
            return;
        }

        const walletUid = publicKey.toString();
        if (isEmailWallet && emailAuthReadyKey !== walletUid) {
            return;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {

// ✅ Always target deterministic doc id
                const deterministicRef = doc(db, "users", walletUid);
                let ref = deterministicRef;

// 1) Prefer deterministic doc if it exists
                const deterministicSnap = await getDoc(deterministicRef);
                if (cancelled) return;
                let data = deterministicSnap.exists() ? (deterministicSnap.data() || {}) : {};

                if (!deterministicSnap.exists()) {
                    // 2) If not found, look for legacy doc(s) by publicKey (existing users)
                    const usersRef = collection(db, "users");
                    const legacySnap = await getDocs(
                        query(usersRef, where("publicKey", "==", walletUid))
                    );
                    if (cancelled) return;

                    if (!legacySnap.empty) {
                        const legacyDoc = legacySnap.docs[0];
                        const legacyData = legacyDoc.data() || {};

                        // ✅ We are about to WRITE -> ensure auth uid becomes wallet pubkey
                        await requireDeterministicAuth();
                        if (cancelled) return;

                        // Copy legacy -> deterministic (do NOT delete legacy yet)
                        await setDoc(
                            deterministicRef,
                            {
                                ...legacyData,
                                publicKey: walletUid,
                                legacyUserId: legacyDoc.id,
                                migratedAt: serverTimestamp(),
                                updatedAt: serverTimestamp(),
                            },
                            { merge: true }
                        );

                        const migratedSnap = await getDoc(deterministicRef);
                        data = migratedSnap.data() || {};
                    } else {
                        // 3) Brand new user
                        await requireDeterministicAuth();
                        if (cancelled) return;
                        await setDoc(
                            deterministicRef,
                            {
                                publicKey: walletUid,
                                createdAt: serverTimestamp(),
                                updatedAt: serverTimestamp(),
                            },
                            { merge: true }
                        );

                        const freshSnap = await getDoc(deterministicRef);
                        data = freshSnap.data() || {};
                    }
                }

                if (cancelled) return;
                const verifiedUid = await requireDeterministicAuth();
                if (cancelled) return;
                setAuthUid(verifiedUid);

// ✅ From this point on, userDocRef is ALWAYS deterministic
                setUserDocRef(ref);

                /** --- keep the rest of your existing "apply data to state" logic --- **/
                if (data.containerFrame) {
                    const cf = data.containerFrame || {};
                    const hasPct = (v) => typeof v === 'string' && /%$/.test(v);
                    const isNum = (v) => typeof v === 'number' && isFinite(v);
                    const legacyPx =
                        isNum(cf.paddingValue) ? cf.paddingValue
                            : isNum(cf.paddingPx) ? cf.paddingPx
                            : 24;
                    const readPad = (v) => (hasPct(v) || isNum(v)) ? v : legacyPx;
                    const unitHint =
                        hasPct(cf.paddingTop) || hasPct(cf.paddingRight) ||
                        hasPct(cf.paddingBottom) || hasPct(cf.paddingLeft) ? '%' : 'px';

                    setContainerFrame(prev => ({
                        borderStyle: cf.borderStyle ?? prev.borderStyle,
                        borderWidth: isNum(cf.borderWidth) ? cf.borderWidth : (prev.borderWidth ?? 0),
                        borderColor: cf.borderColor ?? prev.borderColor,
                        borderRadius: isNum(cf.borderRadius) ? cf.borderRadius : (prev.borderRadius ?? 0),
                        paddingUnit: cf.paddingUnit ?? prev.paddingUnit ?? unitHint,
                        paddingTop: readPad(cf.paddingTop),
                        paddingRight: readPad(cf.paddingRight),
                        paddingBottom: readPad(cf.paddingBottom),
                        paddingLeft: readPad(cf.paddingLeft),
                    }));
                }

                if (data.background) setBackground(data.background);
                if (data.pageTitle) setPageTitle(data.pageTitle);
                if (data.pageDescription) setPageDescription(data.pageDescription);
                if (data.alignment) setAlignment(data.alignment);
                if (data.profilePicUrl) setProfilePicUrl(data.profilePicUrl);
                if (data.columnsCount) setColumnsCount(data.columnsCount);
                if (data.minWidthPx) setMinWidthPx(data.minWidthPx);
                if (data.headerBackground) setHeaderBackground(data.headerBackground);
                if (data.headerPadding) setHeaderPadding(data.headerPadding);
                setComponentListSettings(normalizeComponentListSettings(data.componentListSettings));
                setShowFollowerCount(data.showFollowerCount === true);
                if (data.typography) setTypography(sanitizeTypography(data.typography));
                if (data.gridGap != null) setGridGap(data.gridGap);
                let shouldEnableAdvancedFromData = data.advancedGridLayout != null
                    ? !!data.advancedGridLayout
                    : false;
                if (data.allowHorizontalScroll != null) setAllowHorizontalScroll(!!data.allowHorizontalScroll);
                if (data.lockContainerWidth != null) setLockContainerWidth(!!data.lockContainerWidth);
                if (data.gridRowHeightPx != null) setGridRowHeightPx(toPositiveInt(data.gridRowHeightPx, 120));
                if (data.canvasMaxWidthPx != null) setCanvasMaxWidthPx(toPositiveInt(data.canvasMaxWidthPx, 0));
                if (data.componentLayouts && typeof data.componentLayouts === 'object') {
                    setComponentLayouts(data.componentLayouts);
                    if (Object.keys(data.componentLayouts).length > 0) {
                        shouldEnableAdvancedFromData = true;
                    }
                }
                if (data.componentLayoutsTablet && typeof data.componentLayoutsTablet === 'object') {
                    setComponentLayoutsTablet(data.componentLayoutsTablet);
                    if (Object.keys(data.componentLayoutsTablet).length > 0) {
                        shouldEnableAdvancedFromData = true;
                    }
                } else {
                    setComponentLayoutsTablet({});
                }
                if (data.componentLayoutsMobile && typeof data.componentLayoutsMobile === 'object') {
                    setComponentLayoutsMobile(data.componentLayoutsMobile);
                    if (Object.keys(data.componentLayoutsMobile).length > 0) {
                        shouldEnableAdvancedFromData = true;
                    }
                } else {
                    setComponentLayoutsMobile({});
                }
                setAdvancedGridLayout(shouldEnableAdvancedFromData);

                if (data.themeMode) setThemeMode(data.themeMode);
                if (data.layout) setLayout(data.layout);

                if (data.muultiName) {
                    setMuultiName(data.muultiName);
                    if (data.template) setTemplate(data.template);
                    setComponents(data.components || []);
                    setStep(4);
                } else {
                    setStep(2);
                }

            } catch (e) {
                if (cancelled) return;
                console.error(e);
                setError('Error initializing profile.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [connected, publicKey, emailAuthReadyKey, isEmailWallet, requireDeterministicAuth]);


    useEffect(() => {
        const handleBeforeUnload = e => {
            if (!hasUnpublishedUpdates) return;
            // Chrome requires returnValue to be set
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnpublishedUpdates]);

    useEffect(() => {
        if (!profilePicFile) return;
        const obj = URL.createObjectURL(profilePicFile);
        setProfilePicUrl(obj);
        return () => URL.revokeObjectURL(obj);
    }, [profilePicFile]);


    // background save handler
    const handleBackgroundSave = async bgConfig => {
        try {
            setLoading(true);
            const walletUid = await requireDeterministicAuth();
            if (!auth?.currentUser?.uid || auth.currentUser.uid !== walletUid) {
                setError('You must be signed in to save.');
                return;
            }
            const deterministicRef = doc(db, "users", walletUid);
            await setDoc(deterministicRef, {
                publicKey: walletUid,
                background: bgConfig,
                updatedAt: serverTimestamp()
            }, { merge: true });

            setUserDocRef(deterministicRef);
            setBackground(bgConfig);
            setError('');
        } catch (e) {
            console.error(e);
            setError('Background save failed.');
        } finally {
            setLoading(false);
        }
    };

    const headerStylePreview = (() => {
        const hb = headerBackground || {};
        let base = {};
        if (hb.type === 'color') {
            base.backgroundColor = hb.color || '#ffffff';
        } else if (hb.type === 'gradient') {
            const c0 = (hb.colors && hb.colors[0]) || '#ffffff';
            const c1 = (hb.colors && hb.colors[1]) || '#000000';
            base.backgroundImage = `linear-gradient(${hb.direction || 'to right'}, ${c0}, ${c1})`;
        } else if (hb.type === 'image') {
            base.backgroundImage = hb.imageUrl ? `url(${hb.imageUrl})` : undefined;
            base.backgroundSize = hb.sizing || 'cover';
            base.backgroundPosition = hb.position || 'center';
            base.backgroundRepeat = 'no-repeat';
        }
        return {
            ...base,
            color: hb.fontColor || '#000000',
            borderStyle: hb.borderStyle || 'none',
            borderWidth: hb.borderWidth ?? 0,
            borderColor: hb.borderColor || 'transparent',
            borderRadius: hb.borderRadius ?? 0,
            boxSizing: 'border-box',
            backgroundOrigin: 'border-box',
            backgroundClip: 'border-box',
        };
    })();


// 2) In your save handler
    const handleSaveHeader = async () => {
        if (!userDocRef) return;

        setLoading(true);
        try {
            // ✅ Ensure Firebase auth uid matches wallet pubkey before ANY write/upload
            const walletUid = await requireDeterministicAuth();

            // safety: auth must now be present and match
            if (!auth?.currentUser?.uid || auth.currentUser.uid !== walletUid) {
                setError('You must be signed in to save.');
                return;
            }

            let finalBg = headerBackground || {};
            let finalPicUrl = profilePicUrl || '';

            // --- Upload BG image if needed ---
            const needsBgUpload =
                finalBg?.type === 'image' &&
                finalBg?.imageFile &&
                typeof finalBg?.imageUrl === 'string' &&
                finalBg.imageUrl.startsWith('blob:');

            if (needsBgUpload) {
                const path = `headerBackgrounds/${walletUid}/${Date.now()}_${finalBg.imageFile.name}`;
                const fileRef = ref(storage, path);
                const task = uploadBytesResumable(fileRef, finalBg.imageFile);

                await new Promise((resolve, reject) => {
                    task.on('state_changed', null, reject, () => resolve());
                });

                const downloadUrl = await getDownloadURL(task.snapshot.ref);

                finalBg = {
                    ...finalBg,
                    imageUrl: downloadUrl,
                    imageFile: null, // clear local file after upload
                };
            }

            // --- Upload profile pic if needed ---
            const needsPicUpload =
                profilePicFile &&
                typeof profilePicUrl === 'string' &&
                profilePicUrl.startsWith('blob:');

            if (needsPicUpload) {
                const path = `profilePics/${walletUid}/${Date.now()}_${profilePicFile.name}`;
                const fileRef = ref(storage, path);
                const task = uploadBytesResumable(fileRef, profilePicFile);

                await new Promise((resolve, reject) => {
                    task.on('state_changed', null, reject, () => resolve());
                });

                finalPicUrl = await getDownloadURL(task.snapshot.ref);
            }

            const cleanComponentListSettings = normalizeComponentListSettings(componentListSettings);

            // --- Persist to Firestore ---
            await updateDoc(userDocRef, {
                headerBackground: finalBg,
                headerPadding,            // keep padding in sync
                componentListSettings: cleanComponentListSettings,
                profilePicUrl: finalPicUrl,
                pageTitle,
                pageDescription,
                alignment,
                showFollowerCount,
                updatedAt: serverTimestamp(),
            });

            // --- Local state sync ---
            setHeaderBackground(finalBg);
            setComponentListSettings(cleanComponentListSettings);
            setProfilePicUrl(finalPicUrl);
            setError('');
        } catch (err) {
            console.error(err);
            setError("Couldn't save header background or feature image.");
        } finally {
            setLoading(false);
        }
    };

// multi-gate: per-panel extras + logic
    const handleExtraGatesChange = (idx, gates) => {
        setComponentsSafe((prev) => {
            const next = [...prev];
            const prevComp = next?.[idx] || {};
            const restrictedGates = (Array.isArray(gates) ? gates : [])
                .filter((g) => g && g.type && g.type !== 'public');
            next[idx] = {
                ...prevComp,
                extraGates: restrictedGates,
                updatedAt: Date.now(),
            };
            return next;
        });
        sethasUnpublishedUpdates(true);
    };

    const handleGateLogicChange = (idx, logic) => {
        setComponentsSafe((prev) => {
            const next = [...prev];
            next[idx] = {...next[idx], gateLogic: logic === 'all' ? 'all' : 'any'};
            return next;
        });
        sethasUnpublishedUpdates(true);
    };
    // dropdown: pick a saved gate list
    // function handleGateListChange(idx, listId) {
    //     setComponents(cs => {
    //         const copy = [...cs];
    //         copy[idx].gate = {
    //             ...copy[idx].gate,
    //             listId
    //         };
    //         return copy;
    //     });
    // }


    function handleTokenIdsChange(idx, ids) {
        setComponents(cs => {
            const copy = [...cs];
            copy[idx] = {...copy[idx], gate: {...copy[idx].gate, tokenIds: ids}};
            return copy;
        });
    }


    function handleCollectionIdsChange(idx, ids) {
        setComponents(cs => {
            const copy = [...cs];
            copy[idx] = {...copy[idx], gate: {...copy[idx].gate, collectionIds: ids}};
            return copy;
        });
    }

    // Called when you click public/token/collection radio
    function handleGateTypeChange(idx, type) {
        const ownerPayToUid = publicKey?.toBase58?.() || String(auth?.currentUser?.uid || "");
        setComponentsSafe((cs) => {
            const copy = [...cs];
            const prevComp = copy[idx] || {};
            const prevGate = prevComp.gate || {};
            copy[idx] = {
                ...prevComp,
                gate: {
                    ...prevGate,
                    type,
                    listId:
                        (type === "public" || type === "tokensHeld" || type === "donation" || type === "directPayment" || type === "password" || type === "scheduledAccess")
                            ? ""
                            : (prevGate?.type === type ? (prevGate?.listId || "") : ""),
                    ...(type === "tokensHeld"
                        ? {
                            tokenThreshold: typeof prevGate?.tokenThreshold === "number" ? prevGate.tokenThreshold : 0,
                            currency: prevGate?.currency || "SOL",
                            tokenMint: prevGate?.tokenMint || "",
                        }
                        : {}),
                    ...(type === "donation" ? { donationCompId: "", donationThreshold: 0 } : {}),
                    ...(type === "scheduledAccess"
                        ? {
                            scheduleName: String(prevGate?.scheduleName || ""),
                            startAt: String(prevGate?.startAt || ""),
                            endAt: String(prevGate?.endAt || ""),
                        }
                        : {}),
                    ...(type === "password"
                        ? {
                            password: String(prevGate?.password || ""),
                            passwordPrompt: String(prevGate?.passwordPrompt || ""),
                            targetId: String(prevComp?.id || ""),
                        }
                        : {}),
                    ...(type === "directPayment"
                        ? {
                            priceSol: typeof prevGate?.priceSol === "number" ? prevGate.priceSol : 0,
                            payToUid: ownerPayToUid,
                            targetId: String(prevComp?.id || ""),
                            currency: prevGate?.currency || "SOL",
                            tokenMint: prevGate?.tokenMint || "",
                        }
                        : {}),
                },
                updatedAt: Date.now(),
            };
            return copy;
        });

        sethasUnpublishedUpdates(true);
    }
    function handleDonationGateChange(idx, {donationCompId, donationThreshold, campaignName, currency, tokenMint}) {
        setComponentsSafe((cs) => {
            const copy = [...cs];
            const prevComp = copy[idx] || {};
            copy[idx] = {
                ...prevComp,
                gate: {
                    ...(prevComp.gate || {}),
                    donationCompId: donationCompId ?? prevComp.gate?.donationCompId ?? '',
                    donationThreshold: typeof donationThreshold === 'number'
                        ? donationThreshold
                        : Number(donationThreshold || 0),
                    campaignName: campaignName ?? prevComp.gate?.campaignName ?? '',
                    currency: currency ?? prevComp.gate?.currency ?? '',
                    tokenMint: tokenMint ?? prevComp.gate?.tokenMint ?? ''
                },
                updatedAt: Date.now(),
            };
            return copy;
        });
        sethasUnpublishedUpdates(true);
    }

    const handleSaveTypography = async (next) => {
           if (!userDocRef) return;
           try {
                 setLoading(true);
                 await updateDoc(userDocRef, { typography: next, updatedAt: serverTimestamp() });
                 setTypography(next);
                 setError('');
               } catch (e) {
                 console.error(e);
                 setError('Saving typography failed.');
               } finally {
                 setLoading(false);
               }
         };


// Called when you pick one of the saved lists from the dropdown
    function handleGateListChange(idx, listId, patch = null) {
        setComponentsSafe((cs) => {
            const copy = [...cs];
            const prevComp = copy[idx] || {};
            copy[idx] = {
                ...prevComp,
                gate: {
                    ...prevComp.gate,
                    listId,
                    ...(patch && typeof patch === 'object' ? patch : {}),
                },
                updatedAt: Date.now(),
            };
            return copy;
        });
        sethasUnpublishedUpdates(true);
    }

    // inside MuultiPage()
    const handleMintCollectionChange = (idx, collectionId) => {
        setComponentsSafe(cs => cs.map((comp, i) => i === idx && comp.type === 'mint'
            ? { ...comp, collectionId, updatedAt: Date.now() } : comp));
        sethasUnpublishedUpdates(true);
    };
    const handleMediaMintChange = (idx, mediaMint) => {
        setComponentsSafe(cs => cs.map((comp, i) => i === idx && ['image', 'video', 'audio'].includes(comp.type)
            ? { ...comp, mediaMint, updatedAt: Date.now() } : comp));
        sethasUnpublishedUpdates(true);
    };
    const handleStyleChange = (idx, newStyle) => {
        setComponentsSafe((cs) => {
            sethasUnpublishedUpdates(true);
            const copy = [...cs];
            copy[idx] = {
                ...copy[idx],
                style: newStyle,
                updatedAt: Date.now()
            };
            return copy;
        });
    };

    const ownerPayToUid = publicKey?.toBase58?.() || String(auth?.currentUser?.uid || "");
    function buildGate({
                           type,
                           tokenIds,
                           collectionIds,
                           selectedListId,
                           donationGateExtras,
                           tokensHeldGateExtras,
                           directPaymentExtras,
                           scheduledAccessExtras,
                           passwordGateExtras,
                       }) {
        const base = {
            type: type || 'public',
            tokenIds: Array.isArray(tokenIds) ? tokenIds : [],
            collectionIds: Array.isArray(collectionIds) ? collectionIds : [],
            listId:
                (type === 'public' || type === 'tokensHeld' || type === 'donation' || type === 'directPayment' || type === 'password' || type === 'scheduledAccess')
                    ? ''
                    : (selectedListId || ''),
        };

        if (type === 'tokensHeld') {
            const { tokenThreshold, currency, tokenMint } = tokensHeldGateExtras || {};
            return {
                ...base,
                listId: '',
                tokenThreshold: Number(tokenThreshold || 0),
                currency: currency || 'SOL',
                tokenMint: tokenMint || '',
            };
        }

        if (type === 'donation' && donationGateExtras) {
            const { donationCompId, donationThreshold } = donationGateExtras;
            return {
                ...base,
                donationCompId: donationCompId || '',
                donationThreshold: typeof donationThreshold === 'number' ? donationThreshold : 0,
            };
        }

        if (type === 'directPayment' && directPaymentExtras) {
            const { priceSol, targetId, currency, tokenMint } = directPaymentExtras;
            return {
                ...base,
                listId: '', // direct payment is not list-backed
                priceSol: Number(priceSol || 0),
                payToUid: String(ownerPayToUid || ''),
                targetId: String(targetId || ''),
                currency: currency || 'SOL',
                tokenMint: tokenMint || '',
            };
        }

        if (type === 'scheduledAccess') {
            const { scheduleName, startAt, endAt } = scheduledAccessExtras || {};
            return {
                ...base,
                listId: '',
                scheduleName: String(scheduleName || ''),
                startAt: String(startAt || ''),
                endAt: String(endAt || ''),
            };
        }

        if (type === 'password') {
            const { password, passwordPrompt, targetId } = passwordGateExtras || {};
            return {
                ...base,
                listId: '',
                password: String(password || ''),
                passwordPrompt: String(passwordPrompt || ''),
                targetId: String(targetId || ''),
            };
        }

        return base;
    }

    function withGateFields(compBase, {
        type, tokenIds, collectionIds, selectedListId,
        extraGates, gateLogic,
        donationGateExtras,
        tokensHeldGateExtras,
        directPaymentExtras,
        scheduledAccessExtras,
        passwordGateExtras,
    }) {
        return {
            ...compBase,
            gate: buildGate({
                type,
                tokenIds,
                collectionIds,
                selectedListId,
                donationGateExtras,
                tokensHeldGateExtras,
                directPaymentExtras,
                scheduledAccessExtras,
                passwordGateExtras,
            }),
            extraGates: Array.isArray(extraGates) ? extraGates : [],
            gateLogic: gateLogic === 'all' ? 'all' : 'any',
        };
    }

    function handleDirectPaymentGateChange(idx, { priceSol, currency, tokenMint }) {
        setComponentsSafe((cs) => {
            const copy = [...cs];
            const prevComp = copy?.[idx] || {};
            const compId = String(prevComp?.id ?? '');
            const payToUid = publicKey?.toBase58?.() || String(auth?.currentUser?.uid || '');

            const nextGate = {
                ...(prevComp.gate || {}),
                type: 'directPayment',
                listId: '',
                priceSol: Number(priceSol || 0),
                payToUid,
                currency: currency ?? prevComp.gate?.currency ?? 'SOL',
                tokenMint: tokenMint ?? prevComp.gate?.tokenMint ?? '',
                targetId: String(prevComp.gate?.targetId || compId),
            };

            copy[idx] = {
                ...prevComp,
                gate: nextGate,
                updatedAt: Date.now(),
            };

            return copy;
        });
        sethasUnpublishedUpdates(true);
    }


    // upload and save a “cover” image for token-gated panels
    const handleUploadCover = useCallback(async (idx, file) => {
        if (!file) return;
        setLoading(true);
        try {
            // 1) push the file up
            const uid = auth.currentUser.uid;
            const ts = Date.now();
            const sRef = ref(storage, `componentCovers/${uid}/${ts}_${file.name}`);
            await new Promise((res, rej) => {
                const task = uploadBytesResumable(sRef, file);
                task.on('state_changed', null, rej, res);
            });
            // 2) grab the real URL
            const url = await getDownloadURL(sRef);

            const hasTargetIndex = Number.isInteger(idx) && idx >= 0 && idx < components.length;
            if (!hasTargetIndex) {
                setNewComponentCoverUrl(url);
                setNewComponentStyle((prevStyle) => ({
                    ...(prevStyle || {}),
                    backgroundType: 'cover',
                    backgroundImage: `url(${url})`,
                    backgroundPosition: prevStyle?.backgroundPosition || 'center',
                    backgroundSize: prevStyle?.backgroundSize || 'cover',
                    repeatMode: prevStyle?.repeatMode || 'no-repeat',
                }));
                sethasUnpublishedUpdates(true);
                return url;
            }

            // 3) build a fresh components array, overwriting ANY old blob URL
            const next = components.map((c, i) =>
                i === idx
                    ? {
                        ...c,
                        coverUrl: url,
                        style: {
                            ...c.style,
                            backgroundType: 'cover',
                            backgroundImage: `url(${url})`,
                            backgroundPosition: c.style.backgroundPosition || 'center',
                            backgroundSize: c.style.backgroundSize || 'cover',
                            repeatMode: c.style.repeatMode || 'no-repeat',
                        },
                    }
                    : c
            );
            setComponents(next);

            // 4) persist to your user doc
            await updateDoc(userDocRef, {components: next});
            return url;
        } catch (err) {
            console.error('Cover upload failed:', err);
            setError('Cover upload failed.');
        } finally {
            setLoading(false);
        }
    }, [components, userDocRef]);

    const handleUploadPanelBackground = useCallback(async (idx, file) => {
        if (!file) return;
        setLoading(true);
        try {
            const uid = auth.currentUser.uid;
            const ts = Date.now();
            const bgRef = ref(storage, `componentBackgrounds/${uid}/${ts}_${file.name}`);
            await new Promise((res, rej) => {
                const task = uploadBytesResumable(bgRef, file);
                task.on('state_changed', null, rej, res);
            });
            const url = await getDownloadURL(bgRef);

            const hasTargetIndex = Number.isInteger(idx) && idx >= 0 && idx < components.length;
            if (!hasTargetIndex) {
                setNewComponentStyle((prevStyle) => ({
                    ...(prevStyle || {}),
                    backgroundType: 'image',
                    backgroundImage: `url(${url})`,
                    backgroundSize: prevStyle?.backgroundSize || 'cover',
                    backgroundPosition: prevStyle?.backgroundPosition || 'center',
                    repeatMode: prevStyle?.repeatMode || 'no-repeat',
                }));
                sethasUnpublishedUpdates(true);
                return url;
            }

            // update BOTH style and persist
            const next = components.map((c, i) =>
                i === idx
                    ? {
                        ...c,
                        style: {
                            ...c.style,
                            backgroundType: 'image',
                            backgroundImage: `url(${url})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            repeatMode: 'no-repeat'
                        }
                    }
                    : c
            );
            setComponents(next);
            await updateDoc(userDocRef, {components: next});
            return url;
        } catch (e) {
            console.error(e);
            setError('Background upload failed.');
        } finally {
            setLoading(false);
        }
    }, [components, userDocRef]);

    const handleDeleteAccount = async () => {
        if (!window.confirm("Are you sure? This will permanently delete your profile."))
            return;

        try {
            setLoading(true);

            await requireDeterministicAuth();
            // remove the Firestore doc
            await deleteDoc(userDocRef);
            // reset editor
            setUserDocRef(null);
            setComponents([]);
            setStep(0);
        } catch (e) {
            console.error(e);
            setError("Failed to delete account.");
        } finally {
            setLoading(false);
        }
    };


    // 2) Username & signature
    const handleNameSubmit = async e => {
        e.preventDefault();
        const name = muultiName.trim();

        // must match letters/numbers/underscore/dash only
        if (!/^[A-Za-z0-9_-]+$/.test(name)) {
            setError('Username can only contain letters, numbers, _ and -');
            return;
        }
        // basic length check
        if (name.length < 3 || name.length > 27) {
            setError('Username must be 3–27 characters');
            return;
        }
        try {
            setLoading(true);
            setError('');
            await requireDeterministicAuth();
            const result = await setWalletUsername(wallet, name);
            setMuultiName(result.muultiName);
            setStep(4);
            setActiveEditor('header');
        } catch (err) {
            setError(err?.message || 'Unable to save username.');
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateSubmit = async () => {
        if (!template) return;
        try {
            setLoading(true);
            setError('');

            const chosen = TEMPLATES.find(t => t.id === template);
            const nextColumns = chosen?.layout?.columnsCount ?? columnsCount;
            const nextMin = chosen?.layout?.minWidthPx ?? minWidthPx;
            const nextFrame = chosen?.frame ?? template;
            const nextTheme = chosen?.themeMode;

            await updateDoc(userDocRef, {
                template: nextFrame,
                columnsCount: nextColumns,
                minWidthPx: nextMin,
                ...(nextTheme ? {themeMode: nextTheme} : {}),
                updatedAt: serverTimestamp()
            });

            setColumnsCount(nextColumns);
            setMinWidthPx(nextMin);
            setStep(4);
        } catch {
            setError('Save template failed.');
        } finally {
            setLoading(false);
        }
    };

    const resetInlineDraftControlState = useCallback(() => {
        setNewGateType('public');
        setNewTokenIds([]);
        setNewCollectionIds([]);
        setNewCompressedCollectionIds([]);
        setNewDisplayMode('full');
        setNewComponentStyle({});
        setNewComponentExtraGates([]);
        setNewComponentGateLogic('any');
        setNewComponentTimestampDisplay('none');
        setNewComponentCoverUrl('');
        setNewMediaMint(null);
        setNewMediaCount(null);
        setNewGateDetails(buildDefaultNewGateDetails());
    }, []);

    const clearInlineDraftLayouts = useCallback(() => {
        const draftKey = INLINE_ADD_DRAFT_COMPONENT_ID;
        const clearByKey = (prevLayouts) => {
            if (!prevLayouts || !Object.prototype.hasOwnProperty.call(prevLayouts, draftKey)) return prevLayouts;
            const next = { ...prevLayouts };
            delete next[draftKey];
            return next;
        };
        setComponentLayouts((prev) => clearByKey(prev));
        setComponentLayoutsTablet((prev) => clearByKey(prev));
        setComponentLayoutsMobile((prev) => clearByKey(prev));
    }, []);

    const migrateInlineDraftLayoutsToComponent = useCallback((nextCompId) => {
        if (nextCompId == null) return;
        const draftKey = INLINE_ADD_DRAFT_COMPONENT_ID;
        const targetKey = String(nextCompId);
        if (!targetKey) return;
        const migrateByKey = (prevLayouts) => {
            if (!prevLayouts || !Object.prototype.hasOwnProperty.call(prevLayouts, draftKey)) return prevLayouts;
            const next = { ...prevLayouts, [targetKey]: prevLayouts[draftKey] };
            delete next[draftKey];
            return next;
        };
        setComponentLayouts((prev) => migrateByKey(prev));
        setComponentLayoutsTablet((prev) => migrateByKey(prev));
        setComponentLayoutsMobile((prev) => migrateByKey(prev));
    }, []);

    const openInlineAddForm = (type) => {
        setNewVideoUrl('');
        setFeatureImageUrl('');
        setNewImageUrl('');
        setNewImageAlt('');
        setNewImageCaption('');
        setNewImageItems([]);
        setNewImageNsfa(false);
        setEditingIndex(null);
        setEditingComp(null);
        setEditingType(null);
        setSelectedComponentId(null);
        setAddingType(type);
        setNewMintCollectionId(undefined);
        setIsAdding(true);
        setActiveFormIndex(components.length);
        setComponentEditorPanelOpen(true);
        clearInlineDraftLayouts();
        resetInlineDraftControlState();
    };

    const moveComponentByIndex = (idx, direction = -1) => {
        const currentComponents = Array.isArray(componentsRef.current) ? [...componentsRef.current] : [];
        const from = Number.parseInt(idx, 10);
        const stepDelta = direction < 0 ? -1 : 1;
        const to = from + stepDelta;
        if (
            !Number.isFinite(from) ||
            from < 0 ||
            to < 0 ||
            from >= currentComponents.length ||
            to >= currentComponents.length
        ) {
            return;
        }

        const sourceComp = currentComponents[from];
        const targetComp = currentComponents[to];
        const sourceId = String(sourceComp?.id ?? from);
        const targetId = String(targetComp?.id ?? to);
        const sourceNextId = String(sourceComp?.id ?? to);
        const targetNextId = String(targetComp?.id ?? from);

        [currentComponents[from], currentComponents[to]] = [currentComponents[to], currentComponents[from]];
        setComponentsSafe(currentComponents);

        setEditingIndex((prev) => {
            if (prev == null) return prev;
            if (prev === from) return to;
            if (prev === to) return from;
            return prev;
        });
        setActiveFormIndex((prev) => {
            if (prev == null) return prev;
            if (prev === from) return to;
            if (prev === to) return from;
            return prev;
        });
        setSelectedComponentId((prev) => {
            if (prev == null) return prev;
            if (prev === sourceId) return sourceNextId;
            if (prev === targetId) return targetNextId;
            return prev;
        });
        sethasUnpublishedUpdates(true);
    };
    const moveUpWithPublishState = (idx) => {
        moveComponentByIndex(idx, -1);
    };
    const moveDownWithPublishState = (idx) => {
        moveComponentByIndex(idx, 1);
    };
    const removeComponent = (idx) =>
        setComponents((prev) => {
            const next = prev.filter((_, i) => i !== idx);
            setUserIntendsEmpty(next.length === 0);
            return next;
        });


    const now = Date.now();
// list of the creator's donation components to pick from
    const donationCampaignOptions = (components || [])
        .filter(c => c?.type === 'donation' && c?.id)
        .map(c => ({
            id: String(c.id),
            label: c.campaignName ? `${c.campaignName} (${c.currency || 'SOL'})` : `Campaign #${c.id}`,
            campaignName: c.campaignName || '',
            currency: c.currency || 'SOL',
            tokenMint: c.tokenMint || '',
        }));


    const flushActiveInput = () => {
        try {
            // If the user is typing in a gate price input (or any input), force blur so onBlur commits.
            const el = document?.activeElement;
            if (el && typeof el.blur === "function") el.blur();
        } catch {}
    };


    const handleSaveMuulti = async (overrideComponents) => {
        try {
            setLoading(true);
            setError("");

            // Ensure any focused inputs (e.g., direct payment price) commit onBlur before saving.
            flushActiveInput();
            await new Promise((r) => setTimeout(r, 0));

            // ✅ MUST be first: ensures request.auth.uid === wallet pubkey (doc id)
            await requireDeterministicAuth();

            const walletUid = publicKey?.toBase58?.() || String(publicKey);

            // Wait one microtask + one auth state tick
            if (auth?.currentUser?.uid !== walletUid) {
                await new Promise((r) => setTimeout(r, 0));
            }

            if (auth?.currentUser?.uid !== walletUid) {
                throw new Error(`Auth UID mismatch: ${auth.currentUser?.uid} !== ${walletUid}`);
            }

            const deterministicRef = doc(db, "users", walletUid);

            let compsToSave = Array.isArray(overrideComponents)
                ? overrideComponents
                : (componentsRef?.current || components);

            // Apply any draft direct-payment values captured while typing.
            compsToSave = (compsToSave || []).map((c, idx) => {
                if (!c) return c;
                const compId = String(c.id ?? idx);

                const patchGate = (gate) => {
                    const base = gate || {};
                    if (base?.type !== "directPayment") return base;
                    const key = `${compId}:primary:`;
                    const draft = directPaymentDraftRef.current[key];
                    const nextPrice = Number.isFinite(draft) && draft > 0 ? draft : base.priceSol;
                    return {
                        ...base,
                        priceSol: Number(nextPrice || 0),
                        payToUid: String(walletUid || ""),
                        targetId: base.targetId || compId,
                    };
                };

                const patchExtra = (g, gi) => {
                    if (!g || g.type !== "directPayment") return g;
                    const key = `${compId}:extra:${gi}`;
                    const draft = directPaymentDraftRef.current[key];
                    const nextPrice = Number.isFinite(draft) && draft > 0 ? draft : g.priceSol;
                    return {
                        ...g,
                        priceSol: Number(nextPrice || 0),
                        payToUid: String(walletUid || ""),
                        targetId: g.targetId || compId,
                    };
                };

                return {
                    ...c,
                    gate: patchGate(c.gate),
                    extraGates: Array.isArray(c.extraGates)
                        ? c.extraGates.map((g, gi) => patchExtra(g, gi))
                        : c.extraGates,
                };
            });


            const overrideIsArray = Array.isArray(overrideComponents);
            const intentionalEmpty =
                (overrideIsArray && overrideComponents.length === 0) || userIntendsEmpty;

// 1) Type safety
            if (!Array.isArray(compsToSave)) {
                throw new Error("Publish blocked: components is not an array.");
            }

// 2) Empty safety
            if (compsToSave.length === 0) {
                const snap = await getDoc(deterministicRef);
                const dbComps = snap.exists() ? (snap.data()?.components || []) : [];
                const dbHasComponents = Array.isArray(dbComps) && dbComps.length > 0;

                // DB has content, editor wants to save empty -> only allow if intentional
                if (dbHasComponents && !intentionalEmpty) {
                    throw new Error(
                        "Publish blocked: editor has 0 components, but your saved page has components. " +
                        "This would wipe your page. Please reload and try again."
                    );
                }
            }

            console.log("PUBLISH DEBUG", {
                walletUid,
                authUid: auth?.currentUser?.uid,
                userDocRefPath: userDocRef?.path,
                compsLen: compsToSave.length,
                usedOverride: overrideComponents != null,
                userIntendsEmpty,
            });

            const sanitizeForFirestore = (value, inArray = false) => {
                if (value === undefined) return inArray ? null : undefined;
                if (Array.isArray(value)) {
                    return value.map((v) => sanitizeForFirestore(v, true));
                }
                if (value && typeof value === "object") {
                    const out = {};
                    Object.entries(value).forEach(([k, v]) => {
                        const next = sanitizeForFirestore(v, false);
                        if (next !== undefined) out[k] = next;
                    });
                    return out;
                }
                return value;
            };

            const invalidMediaMint = compsToSave.find(comp => comp?.mediaMint?.enabled && mediaMintEligibility(comp));
            if (invalidMediaMint) throw new Error(mediaMintEligibility(invalidMediaMint));
            const cleanComponents = sanitizeForFirestore(compsToSave);
            const cleanComponentLayouts = sanitizeForFirestore(componentLayouts || {});
            const cleanComponentLayoutsTablet = sanitizeForFirestore(componentLayoutsTablet || {});
            const cleanComponentLayoutsMobile = sanitizeForFirestore(componentLayoutsMobile || {});
            const cleanComponentListSettings = sanitizeForFirestore(
                normalizeComponentListSettings(componentListSettings)
            );

            await updateDoc(deterministicRef, {
                components: cleanComponents,
                columnsCount,
                minWidthPx,
                containerFrame,
                background,
                gridGap,
                advancedGridLayout: !!shouldUseAdvancedLayout,
                allowHorizontalScroll: !!allowHorizontalScroll,
                lockContainerWidth: !!lockContainerWidth,
                gridRowHeightPx: toPositiveInt(gridRowHeightPx, 120),
                canvasMaxWidthPx: toPositiveInt(canvasMaxWidthPx, 0),
                componentLayouts: cleanComponentLayouts,
                componentLayoutsTablet: cleanComponentLayoutsTablet,
                componentLayoutsMobile: cleanComponentLayoutsMobile,
                componentListSettings: cleanComponentListSettings,
                savedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });

            sethasUnpublishedUpdates(false);
            setUserIntendsEmpty(compsToSave.length === 0);
        } catch (e) {
            console.error("Publish/save failed:", e);
            setError(e?.message || "Save failed");
        } finally {
            setLoading(false);
        }
    };


    const handleDisplayModeChange = (idx, mode) => {
        setComponents(cs => {
            const copy = [...cs];
            copy[idx] = {
                ...copy[idx],
                displayMode: (mode === 'compressed' ? 'compressed' : 'full'),
                updatedAt: Date.now()
            };
            return copy;
        });
        sethasUnpublishedUpdates(true);
    };

    // const bgStyle = getBgStyle(background);

    function renderInlineForm(index) {
        if (!isAdding || activeFormIndex !== index) return null;
        const isEditing = editingIndex === index;
        const editingCompId = isEditing ? (editingComp?.id ?? components[index]?.id) : null;
        const existingComponent = isEditing
            ? (components.find((comp) => String(comp.id) === String(editingCompId)) || {})
            : {};
        const cancelInline = () => {
            setIsAdding(false);
            setEditingIndex(null);
            setEditingComp(null);
            setEditingType(null);
            setActiveFormIndex(null);
            setNewMintCollectionId(undefined);
            setNewVideoUrl('');
            setFeatureImageUrl('');
            setNewImageUrl('');
            setNewImageAlt('');
            setNewImageCaption('');
            setNewImageItems([]);
            setNewImageNsfa(false);
            clearInlineDraftLayouts();
            resetInlineDraftControlState();
        };
        const composeComponentForSubmit = (draft = {}) => {
            const current = isEditing
                ? componentsRef.current.find((comp) => String(comp.id) === String(editingCompId))
                : null;
            if (isEditing && !current) {
                setError('This panel was removed while its content was being saved.');
                return null;
            }
            const comp = composeComponentContent(draft, current, inlineDraftControlsRef.current);
            if (['image', 'video', 'audio'].includes(comp.type)) {
                const mediaMint = (current || inlineDraftControlsRef.current)?.mediaMint;
                if (mediaMint) comp.mediaMint = mediaMint;
                if (mediaMint?.enabled && mediaMintEligibility(comp)) {
                    setError(mediaMintEligibility(comp));
                    return null;
                }
            }
            if (!isEditing) migrateInlineDraftLayoutsToComponent(comp.id);
            return comp;
        };
        const commitInlineComponent = (comp) => {
            const next = placeInlineComponent(componentsRef.current, comp, editingCompId, index);
            if (!next) return null;
            setComponentsSafe(next);
            sethasUnpublishedUpdates(true);
            if (!isEditing) {
                setUserIntendsEmpty(false);
                setTimeout(() => scheduleScrollTo(comp.id), 0);
            }
            return next;
        };

        const renderInlineLayoutControls = () => {
            const layoutCompId = isEditing ? editingCompId : INLINE_ADD_DRAFT_COMPONENT_ID;
            if (layoutCompId == null) return null;
            const hasStoredLayout = Object.prototype.hasOwnProperty.call(
                activeViewportLayouts || {},
                String(layoutCompId)
            );
            const placementColumns = getPlacementColumnLimit(getRenderedColumnCount());
            const defaultColSpan = Math.max(1, placementColumns);
            const currentLayout = hasStoredLayout
                ? getComponentLayout(layoutCompId)
                : {
                    colStart: null,
                    rowStart: shouldUseAdvancedLayout ? Math.max(1, addSlotGridRowStart || 1) : null,
                    colSpan: defaultColSpan,
                    rowSpan: 1,
                };
            return (
                <div
                    style={{
                        marginBottom: 12,
                        padding: 10,
                        border: '1px solid var(--stroke-light)',
                        borderRadius: 10,
                        background: 'var(--paper-white)',
                        color: 'var(--text-header)',
                    }}
                >
                    <h5 style={{ margin: '0 0 4px 0' }}>Component Layout</h5>
                    <p style={{ margin: '0 0 8px 0', fontSize: 12, opacity: 0.85 }}>
                        Start values choose where the panel begins. Width/Height choose how much grid space it uses.
                    </p>
                    {!isEditing && (
                        <p style={{ margin: '0 0 8px 0', fontSize: 12, opacity: 0.85 }}>
                            These values update the unsaved component preview immediately.
                        </p>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                        <label style={{ display: 'grid', gap: 4 }}>
                            <span title="Where this panel starts from the left side of the grid">Start Column</span>
                            <input
                                type="number"
                                min="1"
                                placeholder="auto"
                                value={currentLayout.colStart ?? ''}
                                onChange={(e) => {
                                    upsertComponentLayout(layoutCompId, {
                                        ...currentLayout,
                                        colStart: e.target.value === '' ? null : toOptionalPositiveInt(e.target.value),
                                    });
                                }}
                            />
                        </label>
                        <label style={{ display: 'grid', gap: 4 }}>
                            <span title="Where this panel starts from the top of the grid">Start Row</span>
                            <input
                                type="number"
                                min="1"
                                placeholder="auto"
                                value={currentLayout.rowStart ?? ''}
                                onChange={(e) => {
                                    upsertComponentLayout(layoutCompId, {
                                        ...currentLayout,
                                        rowStart: e.target.value === '' ? null : toOptionalPositiveInt(e.target.value),
                                    });
                                }}
                            />
                        </label>
                        <label style={{ display: 'grid', gap: 4 }}>
                            <span title="How many columns wide this panel should be">Width (Columns)</span>
                            <input
                                type="number"
                                min="1"
                                value={currentLayout.colSpan}
                                onChange={(e) => {
                                    upsertComponentLayout(layoutCompId, {
                                        ...currentLayout,
                                        colSpan: toPositiveInt(e.target.value, 1),
                                    });
                                }}
                            />
                        </label>
                        <label style={{ display: 'grid', gap: 4 }}>
                            <span title="How many rows tall this panel should be">Height (Rows)</span>
                            <input
                                type="number"
                                min="1"
                                value={currentLayout.rowSpan}
                                onChange={(e) => {
                                    upsertComponentLayout(layoutCompId, {
                                        ...currentLayout,
                                        rowSpan: toPositiveInt(e.target.value, 1),
                                    });
                                }}
                            />
                        </label>
                    </div>
                </div>
            );
        };

        const withInlineLayoutControls = (node) => (
            <div id="panel-new" className="component-item">
                {node}
                {renderInlineLayoutControls()}
            </div>
        );

        switch (addingType) {
            case 'text': {
                const handleSubmit = async (sectionsFromForm) => {
                    setLoading(true);
                    try {
                        const uploadedSections = await Promise.all(
                            (sectionsFromForm || []).map(async (section) => {
                                // --- Upload feature image if needed ---
                                let featureImageUrl = section.featureImageUrl || '';
                                if (section.featureImageFile && !featureImageUrl) {
                                    const uid = auth.currentUser.uid;
                                    const ts = Date.now();
                                    const fileRef = ref(
                                        storage,
                                        `textFeatureImages/${uid}/${ts}_${section.featureImageFile.name}`
                                    );
                                    await new Promise((res, rej) =>
                                        uploadBytesResumable(fileRef, section.featureImageFile)
                                            .on('state_changed', null, rej, res)
                                    );
                                    featureImageUrl = await getDownloadURL(fileRef);
                                }

                                // --- VIDEO section (uploads to new /textVideo/ folder) ---
                                if (section.type === 'video') {
                                    let videoUrl = section.videoUrl || '';
                                    if (section.videoFile && !videoUrl) {
                                        if (section.videoFile.size > MAX_VIDEO_BYTES) {
                                            throw new Error('Video is over 100 MB. Please choose a smaller file.');
                                        }
                                        const uid = auth.currentUser.uid;
                                        const ts = Date.now();
                                        const safeName = section.videoFile.name.replace(/[^\w.\-]/g, '_');

                                        // 🔹 NEW FOLDER
                                        const fileRef = ref(storage, `videos/${uid}/textVideo/${ts}_${safeName}`);
                                        await new Promise((res, rej) =>
                                            uploadBytesResumable(fileRef, section.videoFile)
                                                .on('state_changed', null, rej, res)
                                        );
                                        videoUrl = await getDownloadURL(fileRef);
                                    }

                                    return {
                                        type: 'video',
                                        title: (section.title || '').trim(),
                                        description: (section.description || '').trim(),
                                        videoUrl,
                                    };
                                }

                                // --- Non-video (text / list / link) ---
                                const items = Array.isArray(section.items)
                                    ? section.items.map(s => (s || '').trim()).filter(Boolean)
                                    : [];

                                return {
                                    heading: (section.heading || '').trim(),
                                    subheading: (section.subheading || '').trim(),
                                    text: (section.text || '').trim(),
                                    items,
                                    linkTitle: (section.linkTitle || '').trim(),
                                    linkUrl: (section.linkUrl || '').trim(),
                                    featureImageUrl
                                };
                            })
                        );

                        // --- Filter out empty sections ---
                        const compacted = uploadedSections.filter(s =>
                            (s.type === 'video' && s.videoUrl) ||
                            s.text ||
                            (s.items && s.items.length) ||
                            s.linkUrl ||
                            s.featureImageUrl
                        );

                        if (!compacted.length) {
                            setLoading(false);
                            setError('Please add some text or an image before saving this panel.');
                            return;
                        }

                        const comp = composeComponentForSubmit({
                            type: 'text',
                            sections: compacted,
                            displayMode: isEditing ? (existingComponent.displayMode ?? 'full') : 'full',
                        });

                        const next = commitInlineComponent(comp);
                        if (!next) return;

                        cancelInline();
                    } catch (err) {
                        console.error(err);
                        setError('Failed to upload feature images or video.');
                    } finally {
                        setLoading(false);
                    }
                };

                const handleSubmitAndPublish = async (sectionsFromForm) => {
                    setLoading(true);
                    try {
                        const uploadedSections = await Promise.all(
                            (sectionsFromForm || []).map(async (section) => {
                                // --- Upload feature image if needed ---
                                let featureImageUrl = section.featureImageUrl || '';
                                if (section.featureImageFile && !featureImageUrl) {
                                    const uid = auth.currentUser.uid;
                                    const ts = Date.now();
                                    const fileRef = ref(
                                        storage,
                                        `textFeatureImages/${uid}/${ts}_${section.featureImageFile.name}`
                                    );
                                    await new Promise((res, rej) =>
                                        uploadBytesResumable(fileRef, section.featureImageFile)
                                            .on('state_changed', null, rej, res)
                                    );
                                    featureImageUrl = await getDownloadURL(fileRef);
                                }

                                // --- VIDEO section (same upload as Save) ---
                                if (section.type === 'video') {
                                    let videoUrl = section.videoUrl || '';
                                    if (section.videoFile && !videoUrl) {
                                        if (section.videoFile.size > MAX_VIDEO_BYTES) {
                                            throw new Error('Video is over 100 MB. Please choose a smaller file.');
                                        }
                                        const uid = auth.currentUser.uid;
                                        const ts = Date.now();
                                        const safeName = section.videoFile.name.replace(/[^\w.\-]/g, '_');

                                        // 🔹 NEW FOLDER
                                        const fileRef = ref(storage, `videos/${uid}/textVideo/${ts}_${safeName}`);
                                        await new Promise((res, rej) =>
                                            uploadBytesResumable(fileRef, section.videoFile)
                                                .on('state_changed', null, rej, res)
                                        );
                                        videoUrl = await getDownloadURL(fileRef);
                                    }

                                    return {
                                        type: 'video',
                                        title: (section.title || '').trim(),
                                        description: (section.description || '').trim(),
                                        videoUrl,
                                    };
                                }

                                // --- Non-video ---
                                const items = Array.isArray(section.items)
                                    ? section.items.map(s => (s || '').trim()).filter(Boolean)
                                    : [];

                                return {
                                    heading: (section.heading || '').trim(),
                                    subheading: (section.subheading || '').trim(),
                                    text: (section.text || '').trim(),
                                    items,
                                    linkTitle: (section.linkTitle || '').trim(),
                                    linkUrl: (section.linkUrl || '').trim(),
                                    featureImageUrl
                                };
                            })
                        );

                        const compacted = uploadedSections.filter(s =>
                            (s.type === 'video' && s.videoUrl) ||
                            s.text ||
                            (s.items && s.items.length) ||
                            s.linkUrl ||
                            s.featureImageUrl
                        );

                        if (!compacted.length) {
                            setLoading(false);
                            setError('Please add some text or an image before saving this panel.');
                            return;
                        }

                        const comp = composeComponentForSubmit({
                            type: 'text',
                            sections: compacted,
                            displayMode: isEditing ? (existingComponent.displayMode ?? 'full') : 'full',
                        });

                        const next = commitInlineComponent(comp);
                        if (!next) return;
                        flushActiveInput();
                        await handleSaveMuulti(next); // publish to Firestore
                        cancelInline();
                    } catch (err) {
                        console.error(err);
                        setError('Failed to upload feature images or video.');
                    } finally {
                        setLoading(false);
                    }
                };

// ...and render the form:
                return withInlineLayoutControls(
                    <NewTextComponentForm
                        sections={newSections}
                        onSectionsChange={setNewSections}
                        gateType={newGateType}
                        tokenIds={newTokenIds}
                        collectionIds={newCollectionIds}
                        onGateTypeChange={setNewGateType}
                        onTokenIdsChange={setNewTokenIds}
                        onCollectionIdsChange={setNewCollectionIds}
                        compressedCollectionIds={newCompressedCollectionIds}
                        onCompressedCollectionIdsChange={setNewCompressedCollectionIds}
                        listId={selectedGateListId}
                        onListIdChange={setSelectedGateListId}

                        donationCampaignOptions={donationCampaignOptions}
                        displayMode={newDisplayMode}                    // NEW
                        onDisplayModeChange={setNewDisplayMode}
                        onSubmit={handleSubmit}     // important: gets merged sections
                        onCancel={cancelInline}
                        enablePublishButton={true}
                        onSubmitAndPublish={handleSubmitAndPublish}
                    />
                );

            }

            case 'mint': {
                const ensureMintComponentTokenList = async () => {
                    if (isEditing ? existingComponent.collectionId : newMintCollectionId) return;
                    const baseName = String(newMintName || '').trim();
                    if (!baseName) return;
                    const listName = `${baseName}TokenList`;
                    const gateListsRef = collection(db, 'gateLists');
                    const qList = query(gateListsRef, where('name', '==', listName), where('gateType', '==', 'token'));
                    const snap = await getDocs(qList);
                    if (snap.empty) {
                        await addDoc(gateListsRef, {
                            name: listName,
                            gateType: 'token',
                            hashList: [],
                            createdAt: serverTimestamp(),
                            auto: true,
                        });
                    }
                };

                // form now calls onUploadComplete(templateId, creatorFeeSol)
                const handleSubmit = async (templateId, creatorFeeSol, royalties) => {
                    try {
                        await ensureMintComponentTokenList();
                    } catch (listErr) {
                        console.warn('Could not ensure mint token list on save:', listErr);
                    }
                    const comp = composeComponentForSubmit({
                        type: 'mint',
                        templateId,
                        creatorFeeSol, // ✅ pass it through so MintNFTComponent can pay it
                        royalties,
                        collectionId: isEditing ? (existingComponent.collectionId || '') : (newMintCollectionId || ''),
                        style: isEditing ? (existingComponent.style || {}) : {}
                    });
                    const next = commitInlineComponent(comp);
                    if (!next) return;

                    cancelInline();
                };

                const handleSubmitAndPublish = async (templateId, creatorFeeSol, royalties) => {
                    try {
                        await ensureMintComponentTokenList();
                    } catch (listErr) {
                        console.warn('Could not ensure mint token list on publish:', listErr);
                    }
                    const comp = composeComponentForSubmit({
                        type: 'mint',
                        templateId,
                        creatorFeeSol,
                        royalties, // if your Mint component consumes it
                        collectionId: isEditing ? (existingComponent.collectionId || '') : (newMintCollectionId || ''),
                        style: isEditing ? (existingComponent.style || {}) : {}
                    });
                    const next = commitInlineComponent(comp);
                    if (!next) return;

                    flushActiveInput();
                    await handleSaveMuulti(next);
                    cancelInline();
                };

                return withInlineLayoutControls(
                    <NewMintNFTComponentForm
                        collectionId={isEditing ? (existingComponent.collectionId || '') : (newMintCollectionId || '')}
                        initialTemplateId={isEditing ? existingComponent.templateId : ''}
                        initialCreatorFeeSol={isEditing ? existingComponent.creatorFeeSol : 0}
                        initialRoyalties={isEditing ? existingComponent.royalties : null}
                        name={newMintName}
                        description={newMintDescription}
                        userId={auth?.currentUser?.uid}
                        ensureAuthUid={requireDeterministicAuth}
                        onNameChange={setNewMintName}
                        onDescriptionChange={setNewMintDescription}
                        gateType={newGateType}
                        tokenIds={newTokenIds}
                        collectionIds={newCollectionIds}
                        compressedCollectionIds={newCompressedCollectionIds}
                        onCompressedCollectionIdsChange={setNewCompressedCollectionIds}
                        listId={selectedGateListId}
                        onGateTypeChange={setNewGateType}
                        onTokenIdsChange={setNewTokenIds}
                        onCollectionIdsChange={setNewCollectionIds}
                        onListIdChange={setSelectedGateListId}
                        onUploadComplete={handleSubmit} // now receives (id, fee) or your existing payload
                        onCancel={cancelInline}
                        enablePublishButton={true}
                        onSubmitAndPublish={handleSubmitAndPublish}
                        error={error}
                    />
                );

            }

            case 'donation': {
                const isEditing = editingIndex === index;
                const current = existingComponent;
                const initial = isEditing
                    ? {
                        campaignName: current.campaignName ?? '',
                        description: current.description ?? '',
                        imageUrl: current.imageUrl ?? '',
                        goalAmount: typeof current.goalAmount === 'number' ? current.goalAmount : null,
                        currency: current.currency ?? 'SOL',
                        tokenMint: current.tokenMint ?? '',
                    }
                    : null;

                const buildDonationComponent = (values) => composeComponentForSubmit({
                    type: 'donation', ...values,
                    recipientPublicKey: current.recipientPublicKey || publicKey.toString(),
                });
                const handleSubmit = (values) => {
                    const comp = buildDonationComponent(values);
                    const next = commitInlineComponent(comp);
                    if (!next) return;
                    cancelInline();
                };
                const handleSubmitAndPublish = (values) => {
                    const comp = buildDonationComponent(values);
                    const next = commitInlineComponent(comp);
                    if (!next) return;
                    flushActiveInput();
                    handleSaveMuulti(next);
                    cancelInline();
                };

                return withInlineLayoutControls(
                    <NewDonationComponentForm
                        userId={auth.currentUser.uid}
                        initial={initial}
                        gateType={newGateType}
                        tokenIds={newTokenIds}
                        collectionIds={newCollectionIds}
                        listId={selectedGateListId}
                        onGateTypeChange={setNewGateType}
                        onTokenIdsChange={setNewTokenIds}
                        onCollectionIdsChange={setNewCollectionIds}
                        compressedCollectionIds={newCompressedCollectionIds}
                        onCompressedCollectionIdsChange={setNewCompressedCollectionIds}
                        onListIdChange={setSelectedGateListId}
                        onSubmit={handleSubmit}
                        onCancel={cancelInline}
                        enablePublishButton={true}
                        onSubmitAndPublish={handleSubmitAndPublish}
                        error={error}
                    />
                );
            }

            case 'link': {
                const handleSubmit = imageUrl => {
                    const comp = composeComponentForSubmit({
                        type: 'link',
                        title: newTitle.trim(),
                        url: newUrl.trim(),
                        imageUrl,
                    });
                    const next = commitInlineComponent(comp);
                    if (!next) return;

                    cancelInline();
                };

                const handleSubmitAndPublish = imageUrl => {
                    const comp = composeComponentForSubmit({
                        type: 'link',
                        title: newTitle.trim(),
                        url: newUrl.trim(),
                        imageUrl,
                    });
                    const next = commitInlineComponent(comp);
                    if (!next) return;
                    flushActiveInput();
                    handleSaveMuulti(next);
                    cancelInline();
                };

                return withInlineLayoutControls(
                    <NewLinkComponentForm
                        userId={auth.currentUser.uid}
                        title={newTitle}
                        url={newUrl}
                        gateType={newGateType}
                        tokenIds={newTokenIds}
                        collectionIds={newCollectionIds}
                        compressedCollectionIds={newCompressedCollectionIds}
                        onCompressedCollectionIdsChange={setNewCompressedCollectionIds}
                        listId={selectedGateListId}
                        onListIdChange={setSelectedGateListId}
                        onTitleChange={setNewTitle}
                        onUrlChange={setNewUrl}
                        onGateTypeChange={setNewGateType}
                        onTokenIdsChange={setNewTokenIds}
                        onCollectionIdsChange={setNewCollectionIds}
                        onSubmit={handleSubmit}
                        onCancel={cancelInline}
                        enablePublishButton={true}
                        onSubmitAndPublish={handleSubmitAndPublish}
                    />
                );
            }

            case 'video': {
                const handleComplete = (url, title, description, gate, featureImageUrl) => {
                    const comp = composeComponentForSubmit({
                        type: 'video',
                        url,
                        title,
                        description,
                        gate,
                        featureImageUrl: featureImageUrl || '',
                    });
                    const next = commitInlineComponent(comp);
                    if (!next) return;

                    cancelInline();
                };
                const handleCompleteAndPublish = (url, title, description, gate, featureImageUrl) => {
                    const comp = composeComponentForSubmit({
                        type: 'video',
                        url,
                        title,
                        description,
                        gate,
                        featureImageUrl: featureImageUrl || '',
                    });
                    const next = commitInlineComponent(comp);
                    if (!next) return;
                    flushActiveInput();
                    handleSaveMuulti(next);
                    cancelInline();
                };

                return withInlineLayoutControls(
                    <NewVideoComponentForm
                            userId={auth.currentUser.uid}

                            gateType={newGateType}
                            tokenIds={newTokenIds}
                            collectionIds={newCollectionIds}
                            onGateTypeChange={setNewGateType}
                            onTokenIdsChange={setNewTokenIds}
                            onCollectionIdsChange={setNewCollectionIds}

                            title={newVideoTitle}
                            description={newVideoDescription}
                            onTitleChange={setNewVideoTitle}
                            onDescriptionChange={setNewVideoDescription}
                            existingVideoUrl={newVideoUrl}
                            existingFeatureImageUrl={featureImageUrl}

                            onUploadComplete={handleComplete}
                            enablePublishButton={true}
                            onSubmitAndPublish={handleCompleteAndPublish}

                            onCancel={() => {
                                setIsAdding(false);
                                setEditingIndex(null);
                            }}
                            error={error}
                        />
                );
            }

            case 'ad': {
                const current = isEditing ? existingComponent : null;
                const buildAdComponent = (values) => composeComponentForSubmit({
                    type: 'ad',
                    title: values.title || 'Ad Panel',
                    description: values.description || '',
                    allowedMediaTypes: Array.isArray(values.allowedMediaTypes)
                        ? values.allowedMediaTypes
                        : ['text', 'image', 'video', 'audio', 'link'],
                    adPlacementId: values.adPlacementId || current?.adPlacementId || '',
                    campaignId: values.campaignId || current?.campaignId || '',
                    style: isEditing ? (current?.style || {}) : {},
                });
                const handleSubmit = (values) => {
                    const comp = buildAdComponent(values);
                    const next = commitInlineComponent(comp);
                    if (!next) return;
                    cancelInline();
                };
                const handleSubmitAndPublish = (values) => {
                    const comp = buildAdComponent(values);
                    const next = commitInlineComponent(comp);
                    if (!next) return;
                    flushActiveInput();
                    handleSaveMuulti(next);
                    cancelInline();
                };

                return withInlineLayoutControls(
                    <NewAdComponentForm
                        initial={current}
                        onSubmit={handleSubmit}
                        onCancel={cancelInline}
                        enablePublishButton={true}
                        onSubmitAndPublish={handleSubmitAndPublish}
                        error={error}
                    />
                );
            }

            case 'image': {
                const handleComplete = (url, alt, caption, gate, nsfa, galleryImages) => {
                    const normalizedImages = normalizeImageGallery(
                        { images: galleryImages, url, alt, caption, nsfa },
                        { alt, caption, nsfa }
                    );
                    const comp = composeComponentForSubmit({
                        type: 'image',
                        url: normalizedImages[0]?.url || url || '',
                        alt,
                        caption,
                        nsfa: !!nsfa,
                        images: normalizedImages,
                    });
                    setNewImageItems(normalizedImages);
                    setNewImageUrl(normalizedImages[0]?.url || url || '');
                    const next = commitInlineComponent(comp);
                    if (!next) return;

                    cancelInline();
                };

                const handleCompleteAndPublish = (url, alt, caption, gate, nsfa, galleryImages) => {
                    const normalizedImages = normalizeImageGallery(
                        { images: galleryImages, url, alt, caption, nsfa },
                        { alt, caption, nsfa }
                    );
                    const comp = composeComponentForSubmit({
                        type: 'image',
                        url: normalizedImages[0]?.url || url || '',
                        alt,
                        caption,
                        nsfa: !!nsfa,
                        images: normalizedImages,
                    });
                    setNewImageItems(normalizedImages);
                    setNewImageUrl(normalizedImages[0]?.url || url || '');
                    const next = commitInlineComponent(comp);
                    if (!next) return;
                    flushActiveInput();
                    handleSaveMuulti(next);
                    cancelInline();
                };

                return withInlineLayoutControls(
                    <NewImageComponentForm
                            userId={auth.currentUser?.uid}
                            gateType={newGateType}
                            tokenIds={newTokenIds}
                            collectionIds={newCollectionIds}
                            onGateTypeChange={setNewGateType}
                            onTokenIdsChange={setNewTokenIds}
                            onCollectionIdsChange={setNewCollectionIds}
                            compressedCollectionIds={newCompressedCollectionIds}
                            onCompressedCollectionIdsChange={setNewCompressedCollectionIds}
                            listId={selectedGateListId}
                            onListIdChange={setSelectedGateListId}
                            alt={newImageAlt}
                            caption={newImageCaption}
                            initialImages={newImageItems}
                            onMediaCountChange={setNewMediaCount}
                            initialNsfa={newImageNsfa}             // (optional) if you add support in the form to prefill
                            onNsfaChange={setNewImageNsfa}
                            onAltChange={setNewImageAlt}
                            onCaptionChange={setNewImageCaption}
                            onUploadComplete={handleComplete}
                            onCancel={cancelInline}
                            enablePublishButton={true}
                            onSubmitAndPublish={handleCompleteAndPublish}
                            error={error}
                        />
                );
            }

            case 'audio': {
                const handleComplete = (url, cover, title, description, gate) => {
                    const comp = composeComponentForSubmit({
                        type: 'audio', url, cover, title, description, gate,
                    });
                    const next = commitInlineComponent(comp);
                    if (!next) return;

                    cancelInline();
                };

                const handleCompleteAndPublish = (url, cover, title, description, gate) => {
                    const comp = composeComponentForSubmit({
                        type: 'audio', url, cover, title, description, gate,
                    });
                    const next = commitInlineComponent(comp);
                    if (!next) return;
                    flushActiveInput();
                    handleSaveMuulti(next);
                    cancelInline();
                };

                return withInlineLayoutControls(
                    <NewAudioComponentForm
                            userId={auth.currentUser.uid}
                            gateType={newGateType}
                            tokenIds={newTokenIds}
                            collectionIds={newCollectionIds}
                            onGateTypeChange={setNewGateType}
                            onTokenIdsChange={setNewTokenIds}
                            onCollectionIdsChange={setNewCollectionIds}
                            compressedCollectionIds={newCompressedCollectionIds}
                            onCompressedCollectionIdsChange={setNewCompressedCollectionIds}
                            listId={selectedGateListId}
                            onListIdChange={setSelectedGateListId}
                            title={newAudioTitle}
                            description={newAudioDescription}
                            onTitleChange={setNewAudioTitle}
                            onDescriptionChange={setNewAudioDescription}
                            onUploadComplete={handleComplete}
                            enablePublishButton={true}
                            onSubmitAndPublish={handleCompleteAndPublish}
                            onCancel={cancelInline}
                            error={error}
                        />
                );
            }

            case 'html': {
                const isEditing = editingIndex === index;

                const handleSubmit = (rawHtml) => {
                    const comp = composeComponentForSubmit({
                        type: 'html',
                        html: (rawHtml || '').trim(),
                        style: isEditing ? (existingComponent.style || {}) : {}
                    });

                    const next = commitInlineComponent(comp);
                    if (!next) return;

                    cancelInline();
                };

                const handleSubmitAndPublish = (rawHtml) => {
                    const comp = composeComponentForSubmit({
                        type: 'html',
                        html: (rawHtml || '').trim(),
                        style: isEditing ? (existingComponent.style || {}) : {}
                    });
                    const next = commitInlineComponent(comp);
                    if (!next) return;
                    flushActiveInput();
                    handleSaveMuulti(next);
                    cancelInline();
                };

                return withInlineLayoutControls(
                    <NewHtmlComponentForm
                        initialHtml={isEditing ? (existingComponent.html || '') : ''}
                        gateType={newGateType}
                        tokenIds={newTokenIds}
                        collectionIds={newCollectionIds}
                        listId={selectedGateListId}
                        onGateTypeChange={setNewGateType}
                        onTokenIdsChange={setNewTokenIds}
                        onCollectionIdsChange={setNewCollectionIds}
                        compressedCollectionIds={newCompressedCollectionIds}
                        onCompressedCollectionIdsChange={setNewCompressedCollectionIds}
                        onListIdChange={setSelectedGateListId}
                        onSubmit={handleSubmit}
                        onCancel={cancelInline}
                        enablePublishButton={true}
                        onSubmitAndPublish={handleSubmitAndPublish}
                    />
                );
            }

            case 'social': {
                const handleSubmit = () => {
                    const comp = composeComponentForSubmit({
                        type: 'social',
                        links: newSocialLinks,
                        customLinks: newCustomSocialLinks,
                    });
                    const next = commitInlineComponent(comp);
                    if (!next) return;

                    cancelInline();
                };

                const handleSubmitAndPublish = () => {
                    const comp = composeComponentForSubmit({
                        type: 'social',
                        links: newSocialLinks,
                        customLinks: newCustomSocialLinks,
                    });
                    const next = commitInlineComponent(comp);
                    if (!next) return;
                    flushActiveInput();
                    handleSaveMuulti(next);
                    cancelInline();
                };
                return withInlineLayoutControls(
                    <NewSocialLinksForm
                        links={newSocialLinks}
                        customLinks={newCustomSocialLinks}
                        gateType={newGateType}
                        tokenIds={newTokenIds}
                        collectionIds={newCollectionIds}
                        listId={selectedGateListId}
                        onChange={setNewSocialLinks}
                        onCustomLinksChange={setNewCustomSocialLinks}
                        onGateTypeChange={setNewGateType}
                        onTokenIdsChange={setNewTokenIds}
                        onCollectionIdsChange={setNewCollectionIds}
                        compressedCollectionIds={newCompressedCollectionIds}
                        onCompressedCollectionIdsChange={setNewCompressedCollectionIds}
                        onListIdChange={setSelectedGateListId}
                        onSubmit={handleSubmit}
                        onCancel={cancelInline}
                        enablePublishButton={true}
                        onSubmitAndPublish={handleSubmitAndPublish}
                        error={error}
                    />
                );
            }

            default:
                return null;
        }
    }

    function handleEdit(index) {
        const comp = components[index];
        if (!comp) return;

        setSelectedComponentId(String(comp.id ?? index));
        setComponentEditorPanelOpen(true);

        setEditingIndex(index);
        setEditingComp(comp);
        setEditingType(comp.type);

        // open the “new/edit” form and switch it to this type
        setAddingType(comp.type);
        setIsAdding(true);
        setActiveFormIndex(index);
        // Reset all “new…” fields first (optional but helps avoid leftover values)
        setNewSections([]);
        setNewTitle('');
        setNewUrl('');
        setNewVideoTitle('');
        setNewVideoDescription('');
        setNewVideoUrl('');
        setFeatureImageUrl('');
        setNewImageUrl('');
        setNewImageAlt('');
        setNewImageCaption('');
        setNewImageItems([]);
        setNewImageNsfa(false);
        setNewAudioTitle('');
        setNewAudioDescription('');
        setNewMediaCount(comp.type === 'image' ? normalizeImageGallery(comp).length : null);

        // gate fields (define gate FIRST, then read from it)
        const gate = comp.gate || { type: 'public', tokenIds: [], collectionIds: [], listId: '' };
        setNewGateType(gate.type || 'public');
        setNewTokenIds(Array.isArray(gate.tokenIds) ? gate.tokenIds : []);
        setNewCollectionIds(Array.isArray(gate.collectionIds) ? gate.collectionIds : []);
        setNewCompressedCollectionIds(Array.isArray(gate.compressedCollectionIds) ? gate.compressedCollectionIds : []);
        setSelectedGateListId(gate.listId || '');
        setNewGateDetails({
            ...buildDefaultNewGateDetails(),
            donationCompId: String(gate.donationCompId || ''),
            donationThreshold: Number(gate.donationThreshold || 0),
            campaignName: String(gate.campaignName || ''),
            currency: gate.currency || 'SOL',
            tokenMint: gate.tokenMint || '',
            tokenThreshold: Number(gate.tokenThreshold || 0),
            priceSol: Number(gate.priceSol || 0),
            password: String(gate.password || ''),
            passwordPrompt: String(gate.passwordPrompt || ''),
            scheduleName: String(gate.scheduleName || ''),
            startAt: String(gate.startAt || ''),
            endAt: String(gate.endAt || ''),
            targetId: String(gate.targetId || comp.id || index),
        });


        // const gate = comp.gate || { type: 'public', tokenIds: [], collectionIds: [] };

        // Now populate based on type
                switch (comp.type) {
                    case 'text': {
                        // Normalize sections (supports legacy flat fields and mixed section types)
                        const normalizedSections = Array.isArray(comp.sections) && comp.sections.length
                            ? comp.sections.map((s) => {
                                if (s?.type === 'video') {
                                    return {
                                        type: 'video',
                                        title: s.title || '',
                                        description: s.description || '',
                                        videoUrl: s.videoUrl || '',
                                    };
                                }
                                return {
                                    heading: s.heading || '',
                                    subheading: s.subheading || '',
                                    text: s.text || '',
                                    items: Array.isArray(s.items)
                                        ? s.items.map(x => String(x || '').trim()).filter(Boolean)
                                        : [],
                                    linkTitle: s.linkTitle || '',
                                    linkUrl: s.linkUrl || '',
                                    featureImageUrl: s.featureImageUrl || '',
                                };
                            })
                            : [{
                                // Legacy single-text panel fallback
                                heading: comp.heading || '',
                                subheading: comp.subheading || '',
                                text: comp.text || '',
                                items: Array.isArray(comp.items)
                                    ? comp.items.map(x => String(x || '').trim()).filter(Boolean)
                                    : [],
                                linkTitle: comp.linkTitle || '',
                                linkUrl: comp.linkUrl || '',
                                featureImageUrl: comp.featureImageUrl || '',
                            }];

                        setNewSections(normalizedSections);

                        // Make sure the toggle reflects current saved mode
                        setNewDisplayMode(comp.displayMode || 'full');

                        break;
                    }


            case 'link':
                setNewTitle(comp.title);
                setNewUrl(comp.url);
                setNewGateType(gate.type);
                setNewTokenIds(gate.tokenIds);
                setNewCollectionIds(gate.collectionIds);
                break;


            case 'video':
                setNewVideoTitle(comp.title);
                setNewVideoDescription(comp.description);
                setNewVideoUrl(comp.url || '');
                setFeatureImageUrl(comp.featureImageUrl || '');
                setNewGateType(gate.type);
                setNewTokenIds(gate.tokenIds);
                setNewCollectionIds(gate.collectionIds);
                break;

            case 'image':
                {
                    const normalizedImages = normalizeImageGallery(comp, {
                        alt: comp.alt,
                        caption: comp.caption,
                        nsfa: comp.nsfa,
                    });
                    setNewImageItems(normalizedImages);
                    setNewImageUrl(normalizedImages[0]?.url || comp.url || '');
                    setNewImageNsfa(!!comp.nsfa || normalizedImages.some((item) => !!item.nsfa));
                }
                setNewImageAlt(comp.alt || '');
                setNewImageCaption(comp.caption || '');
                setNewGateType(gate.type);
                setNewTokenIds(gate.tokenIds);
                setNewCollectionIds(gate.collectionIds);
                break;

            case 'audio':
                setNewAudioTitle(comp.title);
                setNewAudioDescription(comp.description);
                setNewGateType(gate.type);
                setNewTokenIds(gate.tokenIds);
                setNewCollectionIds(gate.collectionIds);
                break;

            case 'social':
                // populate the Social Links form
                setNewSocialLinks(normalizeSocialLinks(comp.links));
                setNewCustomSocialLinks(Array.isArray(comp.customLinks) ? comp.customLinks : []);
                setNewGateType(gate.type);
                setNewTokenIds(gate.tokenIds);
                setNewCollectionIds(gate.collectionIds);
                break;

            case 'mint':
                setNewMintName(comp.name || '');
                setNewMintDescription(comp.description || '');
                break;

            case 'donation':
            case 'html':
                break;

            // …add other types as needed
        }
    }

    // const bgStyle = getBgStyle(background);

    const headerBgStyle = (() => {
        let base = {};

        switch (headerBackground.type) {
            case 'color':
                base = {backgroundColor: headerBackground.color};
                break;
            case 'gradient': {
                const [c0, c1] = headerBackground.colors || ['#ffffff', '#000000'];
                base = {
                    backgroundImage: `linear-gradient(${headerBackground.direction || 'to right'}, ${c0}, ${c1})`
                };
                break;
            }
            case 'image':
                base = {
                    backgroundColor: headerBackground.fallback || '#ffffff',
                    backgroundImage: headerBackground.imageUrl ? `url(${headerBackground.imageUrl})` : undefined,
                    backgroundSize: headerBackground.sizing,
                    backgroundPosition: headerBackground.position,
                    backgroundRepeat: 'no-repeat'
                };
                break;
            default:
                break;
        }

        return {
            ...base,
            color: headerBackground.fontColor,
            borderStyle: headerBackground.borderStyle || 'none',
            borderWidth: `${headerBackground.borderWidth || 0}px`,
            borderColor: headerBackground.borderColor || 'transparent',
            // padding: `${headerBackground.padding || 0}px`,
            boxSizing: 'border-box',
            backgroundOrigin: 'border-box',
            backgroundClip: 'border-box',
        };
    })();

    function getBgStyle(background) {
        const parseCssUrl = (value) => {
            if (!value || typeof value !== 'string') return '';
            const trimmed = value.trim();
            const match = trimmed.match(/url\((['"]?)(.*?)\1\)/i);
            if (match?.[2]) return match[2].trim();
            return '';
        };
        const normalizeImageUrl = (value) => {
            if (!value || typeof value !== 'string') return '';
            const trimmed = value.trim();
            if (!trimmed) return '';
            if (/^url\(/i.test(trimmed)) return parseCssUrl(trimmed);
            return trimmed;
        };
        const base = {
            backgroundColor: undefined,
            backgroundImage: undefined,
            backgroundSize: undefined,
            backgroundRepeat: undefined,
            backgroundPosition: undefined,
        };
        if (!background) return base;

        const imageUrl =
            normalizeImageUrl(background.imageUrl) ||
            normalizeImageUrl(background.url) ||
            normalizeImageUrl(background.image) ||
            normalizeImageUrl(background.src) ||
            parseCssUrl(background.backgroundImage) ||
            parseCssUrl(background.background) ||
            '';
        const normalizedType = (() => {
            const rawType = typeof background.type === 'string' ? background.type.toLowerCase() : background.type;
            if (rawType === 'cover') return 'image';
            if (rawType) return rawType;
            if (imageUrl) return 'image';
            if (Array.isArray(background.colors) && background.colors.length >= 2) return 'gradient';
            if (background.color || background.backgroundColor) return 'single';
            return 'none';
        })();

        if (normalizedType === 'single') {
            return {
                ...base,
                backgroundColor: background.color || background.backgroundColor || 'transparent',
            };
        }
        if (normalizedType === 'gradient') {
            const [c1, c2] = background.colors ?? [];
            const dir = background.direction || 'to bottom';
            return {
                ...base,
                backgroundImage: `linear-gradient(${dir}, ${c1}, ${c2})`,
            };
        }
        if (normalizedType === 'image') {
            return {
                ...base,
                backgroundImage: imageUrl ? `url("${imageUrl}")` : undefined,
                backgroundSize: background.sizing || background.backgroundSize || 'cover',
                backgroundRepeat: background.repeatMode || background.backgroundRepeat || 'no-repeat',
                backgroundPosition: background.position || background.backgroundPosition || 'center',
                backgroundColor: background.backgroundColor || background.color || 'transparent',
            };
        }
        return base;
    }


    const containerStyle = {
        borderStyle: containerFrame.borderStyle || 'solid',
        borderWidth: containerFrame.borderWidth ?? 0,     // number → px (React)
        borderColor: containerFrame.borderColor || '#E5E7EB',
        borderRadius: containerFrame.borderRadius ?? 0,   // number → px (React)
        paddingTop: containerFrame.paddingTop ?? 0, // number or "10%"
        paddingRight: containerFrame.paddingRight ?? 0,
        paddingBottom: containerFrame.paddingBottom ?? 0,
        paddingLeft: containerFrame.paddingLeft ?? 0,
    };

    const isInlineAddActive = isAdding && activeFormIndex === components.length;
    const effectiveAddSlotGridRowStart = useMemo(() => {
        if (!shouldUseAdvancedLayout) return null;
        const baseRow = Math.max(1, addSlotGridRowStart || 1);
        return isInlineAddActive ? (baseRow + 1) : baseRow;
    }, [addSlotGridRowStart, isInlineAddActive, shouldUseAdvancedLayout]);
    const buildLivePreviewComponentFromFormState = useCallback((baseComp = null, forcedType = null) => {
        const nextType = forcedType || baseComp?.type || addingType;
        if (!nextType) return baseComp;
        const gateMetadata = getComponentGateMetadata(
            baseComp || inlineDraftControls,
            baseComp?.id ?? INLINE_ADD_DRAFT_COMPONENT_ID
        );
        const base = {
            ...(baseComp || {}),
            id: baseComp?.id ?? INLINE_ADD_DRAFT_COMPONENT_ID,
            type: nextType,
            ...gateMetadata,
            ...(['image', 'video', 'audio'].includes(nextType)
                ? { mediaMint: baseComp?.mediaMint || newMediaMint } : {}),
            style: isInlineAddActive && !baseComp ? (newComponentStyle || {}) : (baseComp?.style || {}),
            coverUrl: isInlineAddActive && !baseComp ? (newComponentCoverUrl || '') : (baseComp?.coverUrl || ''),
            timestampDisplay: baseComp?.timestampDisplay ?? (isInlineAddActive ? (newComponentTimestampDisplay || 'none') : 'none'),
            createdAt: baseComp?.createdAt || Date.now(),
            updatedAt: Date.now(),
        };
        switch (nextType) {
            case 'text':
                return {
                    ...base,
                    sections: Array.isArray(newSections) ? newSections : (Array.isArray(baseComp?.sections) ? baseComp.sections : []),
                    displayMode: newDisplayMode || baseComp?.displayMode || 'full',
                };
            case 'link':
                return {
                    ...base,
                    title: newTitle || '',
                    url: newUrl || '',
                    imageUrl: featureImageUrl || baseComp?.imageUrl || '',
                };
            case 'video':
                return {
                    ...base,
                    title: newVideoTitle || '',
                    description: newVideoDescription || '',
                    url: newVideoUrl || baseComp?.url || '',
                    featureImageUrl: featureImageUrl || baseComp?.featureImageUrl || '',
                };
            case 'image':
                {
                    const previewImages = Array.isArray(newImageItems) && newImageItems.length > 0
                        ? normalizeImageGallery(
                            { images: newImageItems, nsfa: newImageNsfa, alt: newImageAlt, caption: newImageCaption },
                            { nsfa: newImageNsfa, alt: newImageAlt, caption: newImageCaption }
                        )
                        : normalizeImageGallery(
                            { images: baseComp?.images, url: baseComp?.url, alt: baseComp?.alt, caption: baseComp?.caption, nsfa: baseComp?.nsfa },
                            { nsfa: newImageNsfa, alt: newImageAlt, caption: newImageCaption }
                        );
                    return {
                        ...base,
                        url: newImageUrl || previewImages[0]?.url || baseComp?.url || '',
                        alt: newImageAlt || baseComp?.alt || '',
                        caption: newImageCaption || baseComp?.caption || '',
                        nsfa: !!newImageNsfa,
                        images: previewImages,
                    };
                }
            case 'audio':
                return {
                    ...base,
                    url: newAudioUrl || baseComp?.url || '',
                    cover: newAudioCover || baseComp?.cover || '',
                    title: newAudioTitle || '',
                    description: newAudioDescription || '',
                };
            case 'social':
                return {
                    ...base,
                    links: newSocialLinks || buildEmptySocialLinks(),
                    customLinks: Array.isArray(newCustomSocialLinks) ? newCustomSocialLinks : [],
                };
            case 'html':
                return { ...base, html: '' };
            case 'donation':
                return {
                    ...base,
                    campaignName: baseComp?.campaignName || newGateDetails?.campaignName || '',
                    description: '',
                    imageUrl: '',
                    goalAmount: null,
                    currency: newGateDetails?.currency || baseComp?.currency || 'SOL',
                    tokenMint: newGateDetails?.tokenMint || baseComp?.tokenMint || '',
                    recipientPublicKey: publicKey?.toString?.() || '',
                };
            case 'mint':
                return {
                    ...base,
                    templateId: 'Mint',
                    name: newMintName || '',
                    description: newMintDescription || '',
                    collectionId: newMintCollectionId,
                    creatorFeeSol: null,
                };
            default:
                return base;
        }
    }, [
        addingType,
        inlineDraftControls,
        featureImageUrl,
        newAudioCover,
        newAudioDescription,
        newAudioTitle,
        newAudioUrl,
        newComponentCoverUrl,
        newComponentStyle,
        newComponentTimestampDisplay,
        newCustomSocialLinks,
        newDisplayMode,
        newGateDetails,
        newImageAlt,
        newImageCaption,
        newImageItems,
        newImageNsfa,
        newImageUrl,
        newMintDescription,
        newMintName,
        newMintCollectionId,
        newMediaMint,
        newSections,
        newSocialLinks,
        newTitle,
        newUrl,
        newVideoDescription,
        newVideoTitle,
        newVideoUrl,
        isInlineAddActive,
        publicKey,
    ]);
    const liveDraftPreview = useMemo(() => {
        if (!isInlineAddActive || !addingType) return null;
        return buildLivePreviewComponentFromFormState(null, addingType);
    }, [addingType, buildLivePreviewComponentFromFormState, isInlineAddActive]);
    const liveEditingPreviewComp = useMemo(() => {
        if (!isAdding || editingIndex == null || activeFormIndex !== editingIndex) return null;
        const current = components?.[editingIndex];
        if (!current) return null;
        return buildLivePreviewComponentFromFormState(current, current.type);
    }, [
        activeFormIndex,
        buildLivePreviewComponentFromFormState,
        components,
        editingIndex,
        isAdding,
    ]);
    const selectedComponentIndex = useMemo(() => {
        if (selectedComponentId == null) return -1;
        return (components || []).findIndex((comp, idx) => String(comp?.id ?? idx) === String(selectedComponentId));
    }, [components, selectedComponentId]);
    const selectedComponent = selectedComponentIndex >= 0 ? components[selectedComponentIndex] : null;
    const componentTypeLabel = useCallback((type) => {
        const option = ADD_COMPONENT_OPTIONS.find((item) => item.type === type);
        return option?.label || 'Component';
    }, []);
    const componentListCounts = useMemo(
        () => countListableComponents(components),
        [components]
    );
    const normalizedComponentListSettings = useMemo(
        () => normalizeComponentListSettings(componentListSettings),
        [componentListSettings]
    );
    const componentTypeListLinksPreview = (
        <ComponentTypeListLinks
            components={components}
            componentListSettings={normalizedComponentListSettings}
            username={muultiName}
        />
    );
    const componentTypeListLinksInHeader = normalizedComponentListSettings.placement === 'inHeader';
    const buildComponentCommonProps = (comp, idx) => {
        if (!comp || typeof idx !== 'number' || idx < 0) return null;
        return {
            comp,
            index: idx,
            moveUp: moveUpWithPublishState,
            moveDown: moveDownWithPublishState,
            removeComponent,
            onEdit: handleEdit,
            isEditor: isVerifiedEditor,
            gateType: comp.gate?.type ?? 'public',
            selectedListId: comp.gate?.listId ?? '',
            onGateTypeChange: handleGateTypeChange,
            onGateListChange: handleGateListChange,
            extraGates: comp.extraGates || [],
            gateLogic: comp.gateLogic || 'any',
            onExtraGatesChange: (i, gates) => handleExtraGatesChange(idx, gates),
            onGateLogicChange: (i, logic) => handleGateLogicChange(idx, logic),
            donationCampaignOptions,
            donationGate: comp.gate,
            onDonationGateChange: (payload) => handleDonationGateChange(idx, payload),
            directPaymentGate: comp.gate,
            onDirectPaymentGateChange: handleDirectPaymentGateChange,
            onDirectPaymentDraftChange: handleDirectPaymentDraftChange,
            style: comp.style || {},
            pageTypography: typography || null,
            onStyleChange: handleStyleChange,
            onMintCollectionChange: handleMintCollectionChange,
            onMediaMintChange: handleMediaMintChange,
            mediaMintMediaCount: isAdding && editingIndex === idx ? newMediaCount : undefined,
            coverUrl: comp.coverUrl || '',
            onCoverChange: (i, file) => handleUploadCover(i, file).then(() => {}),
            onPanelBackgroundImageChange: (i, file) => handleUploadPanelBackground(i, file),
            timestampDisplay: comp.timestampDisplay || 'none',
            onTimestampDisplayChange: (val) => handleTimestampDisplayChange(idx, val),
            displayMode: comp.displayMode || 'compressed',
            onDisplayModeChange: (i, mode) => handleDisplayModeChange(i, mode),
            ownerPublicKey: publicKey?.toBase58?.() || publicKey?.toString?.() || '',
        };
    };
    const hasActiveComponentEditor = isAdding && activeFormIndex != null;
    const componentEditorControlsIndex = useMemo(() => {
        if (editingIndex != null && editingIndex >= 0 && editingIndex < (components || []).length) return editingIndex;
        if (selectedComponentIndex >= 0) return selectedComponentIndex;
        return -1;
    }, [components, editingIndex, selectedComponentIndex]);
    const componentEditorControlsComp = componentEditorControlsIndex >= 0
        ? components[componentEditorControlsIndex]
        : null;
    const selectedComponentControlsProps = componentEditorControlsComp && componentEditorControlsIndex >= 0
        ? buildComponentCommonProps(componentEditorControlsComp, componentEditorControlsIndex)
        : null;
    const draftComponentControlsProps = useMemo(() => {
        if (!hasActiveComponentEditor || editingIndex != null || !liveDraftPreview) return null;
        const ownerKey = publicKey?.toBase58?.() || publicKey?.toString?.() || '';
        return {
            comp: liveDraftPreview,
            compId: INLINE_ADD_DRAFT_COMPONENT_ID,
            index: components.length,
            moveUp: () => {},
            moveDown: () => {},
            removeComponent: () => {},
            onEdit: () => {},
            isEditor: isVerifiedEditor,
            gateType: liveDraftPreview?.gate?.type ?? 'public',
            selectedListId: liveDraftPreview?.gate?.listId ?? '',
            onGateTypeChange: (_i, type) => {
                const nextType = type || 'public';
                const isListBackedNextType =
                    nextType === 'token' ||
                    nextType === 'collection' ||
                    nextType === 'compressedCollection';
                setNewGateType(nextType);
                if (
                    nextType === 'public' ||
                    nextType === 'tokensHeld' ||
                    nextType === 'donation' ||
                    nextType === 'directPayment' ||
                    nextType === 'scheduledAccess' ||
                    nextType === 'password'
                ) {
                    setSelectedGateListId('');
                } else if (isListBackedNextType && liveDraftPreview?.gate?.type !== nextType) {
                    setSelectedGateListId('');
                }
                if (nextType === 'scheduledAccess') {
                    setNewGateDetails((prev) => ({
                        ...(prev || buildDefaultNewGateDetails()),
                        scheduleName: String(prev?.scheduleName || ''),
                        startAt: String(prev?.startAt || ''),
                        endAt: String(prev?.endAt || ''),
                    }));
                }
                if (nextType === 'tokensHeld') {
                    setNewGateDetails((prev) => ({
                        ...(prev || buildDefaultNewGateDetails()),
                        tokenThreshold: Number(prev?.tokenThreshold || 0),
                        currency: prev?.currency || 'SOL',
                        tokenMint: prev?.tokenMint || '',
                    }));
                }
                if (nextType === 'password') {
                    setNewGateDetails((prev) => ({
                        ...(prev || buildDefaultNewGateDetails()),
                        targetId: String(liveDraftPreview?.id || INLINE_ADD_DRAFT_COMPONENT_ID),
                        password: String(prev?.password || ''),
                        passwordPrompt: String(prev?.passwordPrompt || ''),
                    }));
                }
            },
            onGateListChange: (_i, listId, patch) => {
                setSelectedGateListId(String(listId || ''));
                if (!patch || typeof patch !== 'object') return;
                setNewGateDetails((prev) => ({
                    ...(prev || buildDefaultNewGateDetails()),
                    ...(patch.password !== undefined ? { password: String(patch.password || '') } : {}),
                    ...(patch.passwordPrompt !== undefined ? { passwordPrompt: String(patch.passwordPrompt || '') } : {}),
                    ...(patch.scheduleName !== undefined ? { scheduleName: String(patch.scheduleName || '') } : {}),
                    ...(patch.startAt !== undefined ? { startAt: String(patch.startAt || '') } : {}),
                    ...(patch.endAt !== undefined ? { endAt: String(patch.endAt || '') } : {}),
                    ...(patch.targetId !== undefined ? { targetId: String(patch.targetId || '') } : {}),
                    ...(patch.tokenThreshold !== undefined ? { tokenThreshold: Number(patch.tokenThreshold || 0) } : {}),
                    ...(patch.currency !== undefined ? { currency: String(patch.currency || 'SOL') } : {}),
                    ...(patch.tokenMint !== undefined ? { tokenMint: String(patch.tokenMint || '') } : {}),
                }));
            },
            extraGates: Array.isArray(newComponentExtraGates) ? newComponentExtraGates : [],
            gateLogic: newComponentGateLogic || 'any',
            onExtraGatesChange: (_i, gates) => {
                const restrictedGates = (Array.isArray(gates) ? gates : [])
                    .filter((g) => g && g.type && g.type !== 'public');
                setNewComponentExtraGates(restrictedGates);
            },
            onGateLogicChange: (_i, logic) => {
                setNewComponentGateLogic(logic === 'all' ? 'all' : 'any');
            },
            donationCampaignOptions,
            donationGate: liveDraftPreview?.gate || {},
            onDonationGateChange: (payload) => {
                setNewGateType('donation');
                setNewGateDetails((prev) => ({
                    ...(prev || buildDefaultNewGateDetails()),
                    donationCompId: payload?.donationCompId ?? prev?.donationCompId ?? '',
                    donationThreshold: Number(payload?.donationThreshold ?? prev?.donationThreshold ?? 0),
                    campaignName: payload?.campaignName ?? prev?.campaignName ?? '',
                    currency: payload?.currency ?? prev?.currency ?? 'SOL',
                    tokenMint: payload?.tokenMint ?? prev?.tokenMint ?? '',
                }));
            },
            directPaymentGate: liveDraftPreview?.gate || {},
            onDirectPaymentGateChange: (_i, payload) => {
                setNewGateType('directPayment');
                setNewGateDetails((prev) => ({
                    ...(prev || buildDefaultNewGateDetails()),
                    priceSol: Number(payload?.priceSol ?? prev?.priceSol ?? 0),
                    currency: payload?.currency ?? prev?.currency ?? 'SOL',
                    tokenMint: payload?.tokenMint ?? prev?.tokenMint ?? '',
                }));
            },
            onDirectPaymentDraftChange: (payload) => {
                if (!payload || payload.scope !== 'primary') return;
                setNewGateType('directPayment');
                setNewGateDetails((prev) => ({
                    ...(prev || buildDefaultNewGateDetails()),
                    priceSol: Number(payload.priceSol || 0),
                }));
                handleDirectPaymentDraftChange(payload);
            },
            style: newComponentStyle || {},
            pageTypography: typography || null,
            onStyleChange: (_i, styleValue) => setNewComponentStyle(styleValue || {}),
            mediaMintIsNew: true,
            mediaMintMediaCount: newMediaCount,
            onMediaMintChange: (_i, value) => setNewMediaMint(value),
            mintCollectionIsNew: true,
            onMintCollectionChange: (_i, collectionId) => setNewMintCollectionId(collectionId),
            coverUrl: newComponentCoverUrl || '',
            onCoverChange: (_i, file) => handleUploadCover(null, file).then(() => {}),
            onPanelBackgroundImageChange: (_i, file) => handleUploadPanelBackground(null, file),
            timestampDisplay: newComponentTimestampDisplay || 'none',
            onTimestampDisplayChange: (val) => setNewComponentTimestampDisplay(val || 'none'),
            displayMode: newDisplayMode || 'full',
            onDisplayModeChange: (_i, mode) => setNewDisplayMode(mode === 'compressed' ? 'compressed' : 'full'),
            ownerPublicKey: ownerKey,
        };
    }, [
        components.length,
        donationCampaignOptions,
        editingIndex,
        handleDirectPaymentDraftChange,
        handleUploadCover,
        handleUploadPanelBackground,
        hasActiveComponentEditor,
        isVerifiedEditor,
        liveDraftPreview,
        newComponentCoverUrl,
        newComponentExtraGates,
        newComponentGateLogic,
        newComponentStyle,
        newComponentTimestampDisplay,
        newDisplayMode,
        newMediaCount,
        publicKey,
        typography,
    ]);
    const componentEditorControlsProps = hasActiveComponentEditor && editingIndex == null
        ? draftComponentControlsProps
        : selectedComponentControlsProps;
    const shouldShowComponentEditorControls = !!componentEditorControlsProps;
    const componentEditorAnalyticsComp = editingIndex != null && editingIndex >= 0 && editingIndex < (components || []).length
        ? components[editingIndex]
        : selectedComponent;
    useEffect(() => {
        if (!isMobileEditorViewport) return;
        if (hasActiveComponentEditor || shouldShowComponentEditorControls) {
            setMobileEditorView('panel');
        }
    }, [hasActiveComponentEditor, isMobileEditorViewport, shouldShowComponentEditorControls]);
    const componentEditorPanelWidthPx = useMemo(() => {
        const viewportWidth = Math.max(
            0,
            editorViewportWidth || gridViewportWidth || (typeof window !== 'undefined' ? window.innerWidth : 0) || 0
        );
        if (viewportWidth > 0 && viewportWidth <= 900) {
            return Math.max(280, Math.round(viewportWidth * 0.95));
        }
        return 420;
    }, [editorViewportWidth, gridViewportWidth]);
    const browserVerticalScrollbarWidthPx = (() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
        const docClientWidth = Number.parseInt(document.documentElement?.clientWidth, 10) || 0;
        const viewportWidth = Number.parseInt(window.innerWidth, 10) || 0;
        return Math.max(0, viewportWidth - docClientWidth);
    })();
    const shouldReservePanelWidthForScrollbar = componentEditorPanelOpen && !isMobileEditorViewport && !componentEditorPanelUndocked;
    const isPinnedStageScrollbarVisible = !isMobileEditorViewport && step === 4
        && (
            shouldReservePanelWidthForScrollbar ||
            editorStageScrollMetrics.scrollWidth > (editorStageScrollMetrics.clientWidth + 1)
        );
    const pinnedScrollbarReserveWidthPx = shouldReservePanelWidthForScrollbar
        ? Math.max(56, toPositiveInt(componentEditorPanelWidthPx, 420))
        : 56;
    const pinnedScrollbarFallbackClientWidthPx = (() => {
        if (editorStageScrollMetrics.clientWidth > 0) return editorStageScrollMetrics.clientWidth;
        if (typeof window === 'undefined') return 0;
        const viewportWidth = Math.max(0, Number.parseInt(window.innerWidth, 10) || 0);
        return Math.max(0, viewportWidth - browserVerticalScrollbarWidthPx - 16);
    })();
    const clampFloatingEditorPanelOffset = useCallback((offset, rect = null) => {
        if (typeof window === 'undefined') return offset;
        const margin = 8;
        const panelWidth = Math.max(
            132,
            Math.round(rect?.width || (componentEditorPanelOpen ? componentEditorPanelWidthPx : 132))
        );
        const panelHeight = Math.max(
            44,
            Math.round(rect?.height || (componentEditorPanelOpen ? window.innerHeight - 16 : 44))
        );
        const maxLeft = Math.max(margin, window.innerWidth - panelWidth - margin);
        const maxTop = Math.max(margin, window.innerHeight - panelHeight - margin);
        return {
            left: Math.min(Math.max(margin, Math.round(offset.left || margin)), maxLeft),
            top: Math.min(Math.max(margin, Math.round(offset.top || margin)), maxTop),
        };
    }, [componentEditorPanelOpen, componentEditorPanelWidthPx]);
    const getDefaultComponentEditorPanelOffset = useCallback(() => {
        const shellEl = editorShellRef.current;
        const rect = shellEl?.getBoundingClientRect?.();
        return {
            left: Math.max(8, Math.round(rect?.left || 0)),
            top: 8,
        };
    }, []);
    const resetComponentEditorPanelPosition = useCallback(() => {
        setComponentEditorPanelUserMoved(false);
        setComponentEditorPanelViewportOffset(
            clampFloatingEditorPanelOffset(getDefaultComponentEditorPanelOffset())
        );
    }, [clampFloatingEditorPanelOffset, getDefaultComponentEditorPanelOffset]);
    const beginFloatingEditorPanelDrag = useCallback((event) => {
        if (isMobileEditorViewport) return;
        if (event.button != null && event.button !== 0) return;
        const target = event.target;
        if (target?.closest?.('button, input, select, textarea, a')) return;

        const panelEl = event.currentTarget?.closest?.('.component-editor-panel');
        const rect = panelEl?.getBoundingClientRect?.();
        if (!rect) return;

        event.preventDefault();
        const pointerId = event.pointerId;
        try {
            event.currentTarget?.setPointerCapture?.(pointerId);
        } catch {}

        const startX = event.clientX;
        const startY = event.clientY;
        const startOffset = {
            left: rect.left,
            top: rect.top,
        };

        const handleMove = (moveEvent) => {
            const nextOffset = clampFloatingEditorPanelOffset({
                left: startOffset.left + (moveEvent.clientX - startX),
                top: startOffset.top + (moveEvent.clientY - startY),
            }, rect);
            setComponentEditorPanelUserMoved(true);
            setComponentEditorPanelViewportOffset(nextOffset);
        };

        const handleUp = () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            window.removeEventListener('pointercancel', handleUp);
            floatingEditorDragRef.current = null;
        };

        if (floatingEditorDragRef.current?.cleanup) {
            floatingEditorDragRef.current.cleanup();
        }
        floatingEditorDragRef.current = {
            cleanup: () => {
                window.removeEventListener('pointermove', handleMove);
                window.removeEventListener('pointerup', handleUp);
                window.removeEventListener('pointercancel', handleUp);
            },
        };
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
        window.addEventListener('pointercancel', handleUp);
    }, [clampFloatingEditorPanelOffset, isMobileEditorViewport]);
    const renderComponentNode = useCallback((renderedComp, commonProps) => {
        if (!renderedComp || !commonProps) return null;
        if (renderedComp.type === 'text') return <TextComponent {...commonProps} />;
        if (renderedComp.type === 'mint') return <MintNFTComponent {...commonProps} comp={renderedComp} />;
        if (renderedComp.type === 'donation') return <DonationComponent {...commonProps} />;
        if (renderedComp.type === 'ad') return <AdComponent {...commonProps} />;
        if (renderedComp.type === 'html') return <HtmlComponent {...commonProps} />;
        if (renderedComp.type === 'link') return <LinkComponent {...commonProps} />;
        if (renderedComp.type === 'video') return <VideoComponent {...commonProps} ownerUsername={muultiName} />;
        if (renderedComp.type === 'image') return <ImageComponent {...commonProps} ownerUsername={muultiName} />;
        if (renderedComp.type === 'audio') return <AudioComponent {...commonProps} />;
        return <SocialLinksComponent {...commonProps} />;
    }, [muultiName]);
    const liveDraftPreviewProps = liveDraftPreview && draftComponentControlsProps
        ? {
            ...draftComponentControlsProps,
            comp: liveDraftPreview,
            compId: String(liveDraftPreview.id ?? INLINE_ADD_DRAFT_COMPONENT_ID),
        }
        : null;
    const liveDraftPanelNode = liveDraftPreviewProps
        ? renderComponentNode(liveDraftPreview, liveDraftPreviewProps)
        : null;
    const liveDraftHasCustomLayout = Object.prototype.hasOwnProperty.call(
        activeViewportLayouts || {},
        INLINE_ADD_DRAFT_COMPONENT_ID
    );
    const liveDraftWrapperStyle = useMemo(() => {
        if (!shouldUseAdvancedLayout) return { position: 'relative' };
        if (liveDraftHasCustomLayout) {
            return {
                position: 'relative',
                ...getComponentWrapperStyle(INLINE_ADD_DRAFT_COMPONENT_ID),
            };
        }
        return {
            position: 'relative',
            gridColumn: '1 / -1',
            gridRow: addSlotGridRowStart != null ? `${Math.max(1, addSlotGridRowStart)}` : undefined,
            minHeight: `${Math.max(40, toPositiveInt(gridRowHeightPx, 120))}px`,
        };
    }, [
        addSlotGridRowStart,
        getComponentWrapperStyle,
        gridRowHeightPx,
        liveDraftHasCustomLayout,
        shouldUseAdvancedLayout,
    ]);

    const guestHomepageTiles = [
        {
            href: '/posts',
            title: 'Social Feed',
            description: 'See recent activity and updates from MuultiSpace creators.',
        },
        {
            href: '/3d/street',
            title: '3D Spaces',
            description: 'Walk through the 3D street and enter creator spaces.',
        },
        {
            href: '/muulti-directory',
            title: 'Directory',
            description: 'Browse and discover MuultiSpaces across the network.',
        },
    ];

    // RENDER
    if (loading) return <div className="muulti-container"><p>Loading…</p></div>;

    if (step === 0) return (
        <div className="onboard-shell">
            {/* Top: wallet + steps */}
            <div className='muulti-space-title-container'>
                <h1>MuultiSpace</h1>
                <p>A page builder where creators can customize how content is accessed and monetized.</p>
            </div>
            <section className="onboard-hero">
                <div className="setup-card setup-card--connect">
                    <div className="progress-bar-container">
                        <ProgressBar step={step}/>
                    </div>

                    <ConnectWalletStepComponent
                        advanceStep={setStep}
                        setError={setError}
                    />

                    <ul className="setup-steps">
                        <li className="inner-setup-step-title">1. Connect a Wallet</li>
                        <li className="inner-setup-step-title">2. Create a unique username or space name.</li>
                        <li className="inner-setup-step-title">4. Start Creating Your Muulti Space</li>
                        <li className="inner-setup-step-title">5. Always Click Publish Changes!</li>
                    </ul>
                </div>
            </section>

            {!connected && (
                <section className="homepage-shortcuts">
                    <h3 className="homepage-shortcuts-title">Explore MuultiSpace</h3>
                    <div className="homepage-shortcuts-grid">
                        {guestHomepageTiles.map((tile) => (
                            <a key={tile.href} href={tile.href} className="homepage-shortcut-tile">
                                <h4>{tile.title}</h4>
                                <p>{tile.description}</p>
                            </a>
                        ))}
                    </div>
                </section>
            )}

            {/* Bottom: carousel */}

            <div>
                <FeaturedMuultiSpaces/>
            </div>
            <section className="onboard-bottom">
                <MuultiSpacesCarousel
                    title="Explore Spaces"
                    itemWidth={280}
                    withArrows
                    autoScroll={false}     // set true if you want marquee
                    snap="center"
                />
            </section>



        </div>
    );


    if (step === 2) return (
        <>
            <div className='muulti-space-title-container'>
                <h1 className='muulti-space-title'>MuultiSpace</h1>
            </div>
            <div className="progress-bar-container">
                {step !== 4 && <ProgressBar step={step}/>}
            </div>
            <ChooseYourUsernameStepComponent
                muultiName={muultiName}
                onChange={setMuultiName}
                onSubmit={handleNameSubmit}
                error={error}
            />
        </>
    );


    return (
        <>
            <div className={`muulti-page dock-${dockPosition}`}>
                <div className={`muulti-editor-tools-container docked-${dockPosition}`}>
                    <div>
                    <h3>Space Editor</h3>
                    <div className="muulti-editor-tools-item">
                        <h5>Customize Title Section:</h5>
                        <button className="muulti-button" onClick={openHeaderEditor}>
                            Edit Page Title Section
                        </button>
                    </div>

                    <div className="muulti-editor-tools-item" id="add-component-button">
                        <h5>Add content:</h5>
                        <div className="muulti-container-button-container">
                            <button
                                onClick={() => {
                                    openInlineAddForm(addingType);
                                }
                                }
                                className="muulti-button">Add {
                                addingType === 'text' ? 'Text'
                                    : addingType === 'link' ? 'Link'
                                    : addingType === 'video' ? 'Video'
                                        : addingType === 'image' ? 'Image'
                                            : addingType === 'audio' ? 'Audio'
                                                : addingType === 'donation' ? 'Donation'
                                                    : addingType === 'mint' ? 'Mint NFT'
                                                        : addingType === 'html' ? 'HTML'
                                                            // :addingType==='legacy'?'Legacy NFT'
                                                            : 'Social Links'}
                                Component
                            </button>

                        </div>

                        <select value={addingType} onChange={e => setAddingType(e.target.value)}>
                            <option value="text">Text Component</option>
                            <option value="link">Link Component</option>
                            <option value="social">Social Links Component</option>
                            <option value="video">Video Component</option>
                            <option value="image">Image Component</option>
                            <option value="audio">Audio Component</option>
                            <option value="donation">Donation Component</option>
                            <option value="html">HTML Component</option>
                            <option value="mint">NFT Mint Component</option>
                            {/*<option value="legacy">Create Legacy Mint Component</option>*/}
                        </select>


                    </div>
                    </div>
                    <div>
                    <h5>Page Customization:</h5>
                    <div className="muulti-editor-tools-item">
                        <button className="muulti-button" onClick={openBackgroundEditor}>
                            Edit Background
                        </button>
                    </div>

                    <div className="muulti-editor-tools-item">
                        <button className="muulti-button" onClick={openContainerEditor}>
                            Edit Container
                        </button>
                    </div>
                     <div className="muulti-editor-tools-item">
                       <button className="muulti-button" onClick={() => setActiveEditor('typography')}>
                         Edit Global Typography
                       </button>
                    </div>


                    <div className="muulti-editor-tools-item">
                        <button className="muulti-button" onClick={openLayoutEditor}>
                            Edit Canvas and Grid
                        </button>
                    </div>
                    </div>
                    <div>
                    <h5>Space Controls:</h5>
                    <div className="muulti-editor-tools-item">
                        <RouterLink to="/wallet-detail" className="muulti-button">
                            Admin
                        </RouterLink>
                    </div>
                    <div className="muulti-editor-tools-item">
                        <button
                            className="muulti-button"
                            // style={{ marginLeft: '1rem' }}
                            onClick={() => setDockPosition(p => p === 'top' ? 'bottom' : 'top')}
                        >
                            {dockPosition === 'top' ? 'Dock to Bottom' : 'Dock to Top'}
                        </button>
                    </div>
                    <div className="muulti-editor-tools-item">
                        <button onClick={() => handleSaveMuulti()} className="muulti-button save-button">
                            Publish Updates
                        </button>
                    </div>

                    <div className="muulti-editor-tools-item">
                        <button
                            className="muulti-button delete-button"
                            onClick={handleDeleteAccount}
                        >
                            Permanently Delete This Space
                        </button>

                    </div>
                    </div>
            </div>

            <EditorWorkbench
                active={!!activeEditor}
                title={
                    activeEditor === 'header' ? 'Header Settings' :
                        activeEditor === 'container' ? 'Container Frame' :
                            activeEditor === 'background' ? 'Page Background' :
                                activeEditor === 'typography' ? 'Typography' :
                                    activeEditor === 'layout' ? 'Canvas and Grid' : ''
                }
                onClose={closeWorkbench}
            >
                {activeEditor === 'header' && (
                    <HeaderEditor
                        profilePicUrl={profilePicUrl}
                        pageTitle={pageTitle}
                        pageDescription={pageDescription}
                        alignment={alignment}

                        headerBackground={headerBackground}
                        onHeaderBackgroundChange={setHeaderBackground}

                        headerPadding={headerPadding}
                        onHeaderPaddingChange={setHeaderPadding}

                        onFileChange={setProfilePicFile}
                        onTitleChange={setPageTitle}
                        onDescriptionChange={setPageDescription}
                        onAlignmentChange={setAlignment}
                        componentListSettings={componentListSettings}
                        componentListCounts={componentListCounts}
                        onComponentListSettingsChange={setComponentListSettings}
                        showFollowerCount={showFollowerCount}
                        onShowFollowerCountChange={setShowFollowerCount}
                        onSave={handleSaveHeader}
                    />
                )}

                {activeEditor === 'container' && (
                    <ContainerFrameEditor
                        value={containerFrame}
                        onChange={setContainerFrame}
                        onSave={() => handleSaveMuulti()}
                    />
                )}

                {activeEditor === 'background' && (
                    <BackgroundPicker
                        forceOpen
                        userDocRef={userDocRef}
                        current={background}
                        onBackgroundChange={setBackground}
                        onSave={handleBackgroundSave}
                        ensureAuthUid={requireDeterministicAuth}
                        onError={setError}
                        onDone={closeWorkbench}
                    />
                )}
                   {activeEditor === 'typography' && (
                     <FontsStyleEditor
                       value={typography}
                       onChange={setTypography}
                       onSave={handleSaveTypography}
                     />
                   )}
                {activeEditor === 'layout' && (
                    <div style={{ display: 'grid', gap: 12 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={!!shouldUseAdvancedLayout}
                                onChange={(e) => {
                                    setAdvancedGridLayout(!!e.target.checked);
                                    sethasUnpublishedUpdates(true);
                                }}
                            />
                            Enable advanced component positioning
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={!!allowHorizontalScroll}
                                onChange={(e) => {
                                    setAllowHorizontalScroll(!!e.target.checked);
                                    sethasUnpublishedUpdates(true);
                                }}
                            />
                            Allow horizontal scroll / overflow
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={!!lockContainerWidth}
                                onChange={(e) => {
                                    setLockContainerWidth(!!e.target.checked);
                                    sethasUnpublishedUpdates(true);
                                }}
                            />
                            Lock container width at 100%
                        </label>
                        <label style={{ display: 'grid', gap: 4, maxWidth: 260 }}>
                            <span>Edit layout for screen size</span>
                            <select
                                value={layoutEditViewport}
                                onChange={(e) => setLayoutEditViewport(e.target.value)}
                            >
                                {LAYOUT_VIEWPORT_OPTIONS.map((option) => (
                                    <option key={option.id} value={option.id}>{option.label}</option>
                                ))}
                            </select>
                        </label>
                        <p style={{ margin: 0, fontSize: 12, opacity: 0.85 }}>
                            Desktop is the default layout. Tablet/Mobile only store overrides for panels you change.
                        </p>
                        <p style={{ margin: 0, fontSize: 12, opacity: 0.85 }}>
                            Viewing as: {activeEditViewportOption.label}. Current window matches: {detectedLayoutViewport}.
                        </p>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={!!showEditorGridLines}
                                onChange={(e) => {
                                    setShowEditorGridLines(!!e.target.checked);
                                }}
                            />
                            Show grid lines while editing
                        </label>
                        <label style={{ display: 'grid', gap: 4, maxWidth: 260 }}>
                            <span>Grid line color</span>
                            <select
                                value={gridLineColorPreset}
                                onChange={(e) => setGridLineColorPreset(e.target.value)}
                            >
                                {GRID_LINE_COLOR_PRESETS.map((preset) => (
                                    <option key={preset.id} value={preset.id}>{preset.label}</option>
                                ))}
                            </select>
                        </label>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                            <label style={{ display: 'grid', gap: 4 }}>
                                <span>Columns</span>
                                <select
                                    value={columnsCount}
                                    onChange={(e) => {
                                        setColumnsCount(e.target.value);
                                        sethasUnpublishedUpdates(true);
                                    }}
                                >
                                    <option value="auto-fill">auto-fill</option>
                                    {Array.from({ length: 16 }, (_, i) => i + 1).map((n) => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </label>
                            <label style={{ display: 'grid', gap: 4 }}>
                                <span>Min column width (px)</span>
                                <input
                                    type="number"
                                    min="80"
                                    value={minWidthPx}
                                    onChange={(e) => {
                                        setMinWidthPx(toPositiveInt(e.target.value, 100));
                                        sethasUnpublishedUpdates(true);
                                    }}
                                />
                            </label>
                            <label style={{ display: 'grid', gap: 4 }}>
                                <span>Gap (px)</span>
                                <input
                                    type="number"
                                    min="0"
                                    value={gridGap ?? 0}
                                    onChange={(e) => {
                                        setGridGap(Math.max(0, Number.parseInt(e.target.value, 10) || 0));
                                        sethasUnpublishedUpdates(true);
                                    }}
                                />
                            </label>
                            <label style={{ display: 'grid', gap: 4 }}>
                                <span>Row height (px)</span>
                                <input
                                    type="number"
                                    min="40"
                                    value={gridRowHeightPx}
                                    onChange={(e) => {
                                        setGridRowHeightPx(toPositiveInt(e.target.value, 120));
                                        setAdvancedGridLayout(true);
                                        sethasUnpublishedUpdates(true);
                                    }}
                                />
                            </label>
                            <label style={{ display: 'grid', gap: 4 }}>
                                <span>Canvas max width (px)</span>
                                <input
                                    type="number"
                                    min="0"
                                    value={canvasMaxWidthPx}
                                    onChange={(e) => {
                                        setCanvasMaxWidthPx(Math.max(0, Number.parseInt(e.target.value, 10) || 0));
                                        setAdvancedGridLayout(true);
                                        sethasUnpublishedUpdates(true);
                                    }}
                                />
                            </label>
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {LAYOUT_PRESETS.map((preset) => (
                                <button
                                    key={preset.id}
                                    type="button"
                                    className="muulti-button"
                                    style={{ width: 'auto' }}
                                    onClick={() => applyLayoutPreset(preset)}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>

                        {selectedComponentId != null && (
                            <div
                                style={{
                                    display: 'grid',
                                    gap: 8,
                                    padding: 10,
                                    borderRadius: 10,
                                    border: '1px solid var(--stroke-light)',
                                    background: 'var(--paper-white)',
                                    color: 'var(--text-header)',
                                }}
                            >
                                <h4 style={{ margin: '0 0 2px 0' }}>Selected Component Layout</h4>
                                <p style={{ margin: 0, fontSize: 12, opacity: 0.85 }}>
                                    Editing {activeEditViewportOption.label} layout values for this component.
                                </p>
                                <p style={{ margin: '0 0 6px 0', fontSize: 12, opacity: 0.85 }}>
                                    Left/Top place the panel in the grid. Width and Height are independent.
                                </p>
                                <p style={{ margin: '0 0 6px 0', fontSize: 12, opacity: 0.85 }}>
                                    Tip: use keyboard arrow keys to nudge the selected panel one grid cell at a time.
                                </p>
                                <p style={{ margin: '0 0 6px 0', fontSize: 12, opacity: 0.85 }}>
                                    Hold Shift + Arrow keys to resize width/height in grid cells.
                                </p>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <button
                                        type="button"
                                        className="muulti-button"
                                        style={{ width: 'auto', margin: 0, padding: '4px 8px' }}
                                        onClick={() => {
                                            const key = String(selectedComponentId);
                                            if (layoutEditViewport === 'desktop') {
                                                setComponentLayouts((prev) => {
                                                    if (!prev || !Object.prototype.hasOwnProperty.call(prev, key)) return prev;
                                                    const next = { ...prev };
                                                    delete next[key];
                                                    return next;
                                                });
                                            } else {
                                                const setOverrides = layoutEditViewport === 'tablet'
                                                    ? setComponentLayoutsTablet
                                                    : setComponentLayoutsMobile;
                                                setOverrides((prevOverride) => {
                                                    if (!prevOverride || !Object.prototype.hasOwnProperty.call(prevOverride, key)) {
                                                        return prevOverride;
                                                    }
                                                    const next = { ...prevOverride };
                                                    delete next[key];
                                                    return next;
                                                });
                                            }
                                            sethasUnpublishedUpdates(true);
                                        }}
                                    >
                                        Reset Selected Layout
                                    </button>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                                    <label style={{ display: 'grid', gap: 4 }}>
                                        <span title="How far from the left this panel begins">Left Position (columns)</span>
                                        <input
                                            type="number"
                                            min="1"
                                            placeholder="auto"
                                            value={getComponentLayout(selectedComponentId).colStart ?? ''}
                                            onChange={(e) => {
                                                upsertComponentLayout(selectedComponentId, {
                                                    colStart: e.target.value === '' ? null : toOptionalPositiveInt(e.target.value),
                                                });
                                            }}
                                        />
                                    </label>
                                    <label style={{ display: 'grid', gap: 4 }}>
                                        <span title="How far from the top this panel begins">Top Position (rows)</span>
                                        <input
                                            type="number"
                                            min="1"
                                            placeholder="auto"
                                            value={getComponentLayout(selectedComponentId).rowStart ?? ''}
                                            onChange={(e) => {
                                                upsertComponentLayout(selectedComponentId, {
                                                    rowStart: e.target.value === '' ? null : toOptionalPositiveInt(e.target.value),
                                                });
                                            }}
                                        />
                                    </label>
                                    <label style={{ display: 'grid', gap: 4 }}>
                                        <span title="How many grid columns wide this panel should be">Panel Width (columns)</span>
                                        <input
                                            type="number"
                                            min="1"
                                            value={getComponentLayout(selectedComponentId).colSpan}
                                            onChange={(e) => {
                                                upsertComponentLayout(selectedComponentId, {
                                                    colSpan: toPositiveInt(e.target.value, 1),
                                                });
                                            }}
                                        />
                                    </label>
                                    <label style={{ display: 'grid', gap: 4 }}>
                                        <span title="How many grid rows tall this panel should be">Panel Height (rows)</span>
                                        <input
                                            type="number"
                                            min="1"
                                            value={getComponentLayout(selectedComponentId).rowSpan}
                                            onChange={(e) => {
                                                upsertComponentLayout(selectedComponentId, {
                                                    rowSpan: toPositiveInt(e.target.value, 1),
                                                });
                                            }}
                                        />
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                )}

            </EditorWorkbench>
            <div
                className={`muulti-editor-shell ${componentEditorPanelUndocked ? 'editor-panel-undocked' : 'editor-panel-docked'} ${componentEditorPanelOpen ? 'editor-panel-open' : 'editor-panel-collapsed'} ${isMobileEditorViewport ? 'editor-mobile-layout' : ''} ${isMobileEditorViewport ? `mobile-view-${mobileEditorView}` : ''}`}
                ref={editorShellRef}
                style={{
                    '--component-editor-panel-width': `${componentEditorPanelWidthPx}px`,
                    '--component-editor-panel-reserve-width': `${componentEditorPanelOpen ? componentEditorPanelWidthPx : 56}px`,
                    '--component-editor-panel-undocked-left': `${componentEditorPanelViewportOffset.left}px`,
                    '--component-editor-panel-undocked-top': `${componentEditorPanelViewportOffset.top}px`,
                }}
            >
                {isMobileEditorViewport && (
                    <div className="muulti-editor-mobile-view-switch" role="tablist" aria-label="Mobile editor view">
                        <button
                            type="button"
                            className={`muulti-button ${mobileEditorView === 'panel' ? 'is-active' : ''}`}
                            style={{ width: 'auto', margin: 0, padding: '6px 10px' }}
                            onClick={() => setMobileEditorView('panel')}
                            aria-pressed={mobileEditorView === 'panel'}
                        >
                            Editor Controls
                        </button>
                        <button
                            type="button"
                            className={`muulti-button ${mobileEditorView === 'preview' ? 'is-active' : ''}`}
                            style={{ width: 'auto', margin: 0, padding: '6px 10px' }}
                            onClick={() => setMobileEditorView('preview')}
                            aria-pressed={mobileEditorView === 'preview'}
                        >
                            Live Preview
                        </button>
                    </div>
                )}
                <aside className={`component-editor-panel ${componentEditorPanelOpen ? 'is-open' : 'is-collapsed'} ${componentEditorPanelUndocked ? 'is-undocked' : 'is-docked'}`}>
                        <div
                            className="component-editor-panel-header"
                            onPointerDown={beginFloatingEditorPanelDrag}
                        >
                            <h4 style={{ margin: 0 }}>Component Editor</h4>
                            <div className="component-editor-panel-header-actions">
                                {!isMobileEditorViewport && (
                                    <button
                                        type="button"
                                        className="muulti-button component-editor-panel-header-button"
                                        aria-label="Reset component editor position"
                                        title="Reset position"
                                        onClick={resetComponentEditorPanelPosition}
                                    >
                                        Reset
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="muulti-button component-editor-panel-header-button"
                                    aria-label={componentEditorPanelOpen ? 'Minimize component editor' : 'Restore component editor'}
                                    title={componentEditorPanelOpen ? 'Minimize' : 'Restore'}
                                    onClick={() => setComponentEditorPanelOpen((prev) => !prev)}
                                >
                                    {componentEditorPanelOpen ? 'Minimize' : 'Open'}
                                </button>
                            </div>
                        </div>
                        {componentEditorPanelOpen && (
                            <div className="component-editor-panel-body">
                                {hasActiveComponentEditor ? (
                                    <>
                                        {renderInlineForm(activeFormIndex)}
                                        {(hasActiveComponentEditor || shouldShowComponentEditorControls) && (
                                            <div className="component-editor-panel-sticky">
                                                {hasActiveComponentEditor && (
                                                    <div className="component-editor-panel-meta">
                                                        {editingIndex != null
                                                            ? `Editing ${componentTypeLabel(components[editingIndex]?.type)} component`
                                                            : `Adding ${componentTypeLabel(addingType)} component`}
                                                    </div>
                                                )}
                                                {shouldShowComponentEditorControls && (
                                                    <div className="component-editor-panel-controls">
                                                        <ComponentControls {...componentEditorControlsProps} />
                                                    </div>
                                                )}
                                                {componentEditorAnalyticsComp && (
                                                    <ComponentAnalyticsSummary
                                                        comp={componentEditorAnalyticsComp}
                                                        ownerPublicKey={publicKey?.toBase58?.() || publicKey?.toString?.() || ''}
                                                        compact
                                                    />
                                                )}
                                                {hasActiveComponentEditor && editingIndex != null && (
                                            <div className="component-editor-panel-actions">
                                                <button
                                                    type="button"
                                                    className="muulti-button delete-button"
                                                            style={{ width: 'auto', margin: 0, padding: '4px 8px' }}
                                                            onClick={() => {
                                                                removeComponent(editingIndex);
                                                                sethasUnpublishedUpdates(true);
                                                                setIsAdding(false);
                                                                setEditingIndex(null);
                                                                setActiveFormIndex(null);
                                                                setSelectedComponentId(null);
                                                            }}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : selectedComponent ? (
                                    <div className="component-editor-panel-empty">
                                        <p style={{ margin: 0 }}>
                                            {`Selected ${componentTypeLabel(selectedComponent.type)} component`}
                                        </p>
                                        <ComponentAnalyticsSummary
                                            comp={selectedComponent}
                                            ownerPublicKey={publicKey?.toBase58?.() || publicKey?.toString?.() || ''}
                                            compact
                                        />
                                        <div className="component-editor-panel-actions">
                                            <button
                                                type="button"
                                                className="muulti-button delete-button"
                                                style={{ width: 'auto', margin: 0, padding: '4px 8px' }}
                                                onClick={() => {
                                                    removeComponent(selectedComponentIndex);
                                                    sethasUnpublishedUpdates(true);
                                                    setSelectedComponentId(null);
                                                }}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                        <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>
                                            Select a component to open its populated editor and controls here.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="component-editor-panel-empty">
                                        <p style={{ margin: 0 }}>
                                            Select a component or add a new component to edit it here.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                </aside>
                <div
                    className={`muulti-editor-stage ${componentEditorPanelOpen ? 'editor-panel-open' : 'editor-panel-collapsed'}`}
                    ref={editorStageRef}
                >
                    <div className="muulti-editor-stage-track">
                        <div className="muulti-editor-canvas">
            <div
                className={`muulti-container ${template}`}
                style={{
                    ...bgStyle,              // your page background
                    ...containerStyle,
                    width: isMobilePreviewMode && editorPreviewCanvasWidthPx > 0
                        ? `${editorPreviewCanvasWidthPx}px`
                        : shouldUseAdvancedLayout && lockContainerWidth ? '100%' : undefined,
                    maxWidth: isMobilePreviewMode && editorPreviewCanvasWidthPx > 0
                        ? `${editorPreviewCanvasWidthPx}px`
                        : shouldUseAdvancedLayout && lockContainerWidth ? '100%' : undefined,
                    overflowX: isMobilePreviewMode
                        ? 'visible'
                        : shouldUseAdvancedLayout
                        ? (allowHorizontalScroll ? 'auto' : 'hidden')
                        : undefined,
                    boxSizing: 'border-box',
                }}
            >
                <div className="muulti-heading-container">
                    <PublicHeader
                        profilePicUrl={profilePicUrl}
                        pageTitle={pageTitle || muultiName}
                        pageDescription={pageDescription}
                        alignment={alignment}
                        headerPadding={headerPadding}
                        style={headerStylePreview}
                    >
                        {componentTypeListLinksInHeader ? componentTypeListLinksPreview : null}
                    </PublicHeader>
                    {!componentTypeListLinksInHeader ? componentTypeListLinksPreview : null}
                </div>
                {error && <p className="error">{error}</p>}

                <div
                    className={`components-list${shouldUseAdvancedLayout ? ' layout-managed-grid' : ''}${shouldUseAdvancedLayout && showEditorGridLines ? ' show-grid-lines' : ''}`}
                    ref={componentsGridRef}
                    style={{
                        gridTemplateColumns: componentGridTemplateColumns,
                        gridGap: `${gridGap}px`,
                        gridAutoRows: shouldUseAdvancedLayout ? `minmax(${Math.max(40, toPositiveInt(gridRowHeightPx, 120))}px, auto)` : undefined,
                        gridAutoFlow: shouldUseAdvancedLayout ? 'row' : undefined,
                        maxWidth: shouldUseAdvancedLayout ? (() => {
                            if (isMobilePreviewMode && editorPreviewCanvasWidthPx > 0) return `${editorPreviewCanvasWidthPx}px`;
                            if (isMobileEditorViewport) return '100%';
                            const viewportPreviewWidth = layoutEditViewport === 'desktop'
                                ? 0
                                : toPositiveInt(activeEditViewportOption?.previewWidth, 0);
                            const canvasWidth = toPositiveInt(canvasMaxWidthPx, 0);
                            const appliedWidth = viewportPreviewWidth > 0 && canvasWidth > 0
                                ? Math.min(viewportPreviewWidth, canvasWidth)
                                : Math.max(viewportPreviewWidth, canvasWidth);
                            return appliedWidth > 0 ? `${appliedWidth}px` : undefined;
                        })() : undefined,
                        marginInline: shouldUseAdvancedLayout ? 'auto' : undefined,
                        width: '100%',
                        minWidth: 0,
                        position: shouldUseAdvancedLayout ? 'relative' : undefined,
                        overflowX: isMobilePreviewMode
                            ? 'visible'
                            : shouldUseAdvancedLayout
                            ? (allowHorizontalScroll ? 'auto' : 'hidden')
                            : undefined,
                        '--editor-grid-col-step': liveGridMetrics ? `${Math.max(8, liveGridMetrics.cellWidth + liveGridMetrics.gapPx)}px` : undefined,
                        '--editor-grid-row-step': liveGridMetrics ? `${Math.max(8, liveGridMetrics.rowStepPx)}px` : undefined,
                        '--editor-grid-line-strong': activeGridLineColors.strong,
                        '--editor-grid-line-soft': activeGridLineColors.soft,
                        '--editor-grid-panel-outline': activeGridLineColors.outline,
                    }}
                >
                    {snapPreviewStyle && (
                        <div
                            className="component-grid-snap-ghost"
                            style={snapPreviewStyle}
                        />
                    )}
                    {components.map((comp, idx) => {
                        const renderedComp = (liveEditingPreviewComp && editingIndex === idx)
                            ? liveEditingPreviewComp
                            : comp;
                        const panelId = String(comp?.id ?? idx);
                        const wrapperId = `panel-${panelId}`;
                        const isSelectedPanel = String(selectedComponentId) === panelId;
                        const layoutValue = getComponentLayout(panelId);
                        const showPlacementBadge = shouldUseAdvancedLayout && showEditorGridLines;
                        const wrapperStyle = {
                            position: 'relative',
                            ...getComponentWrapperStyle(panelId),
                            outline: isSelectedPanel ? '2px solid #0ea5e9' : undefined,
                            outlineOffset: isSelectedPanel ? 2 : undefined,
                        };

                        const commonProps = buildComponentCommonProps(renderedComp, idx);
                        const panelNode = renderComponentNode(renderedComp, commonProps);

                        return (
                            <div
                                key={panelId}
                                id={wrapperId}
                                className={`component-grid-item${isSelectedPanel ? ' is-selected' : ''}`}
                                style={wrapperStyle}
                            >
                                {canUsePanelLayoutControls && (
                                    <div
                                        className="component-grid-item-actions"
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            zIndex: 8,
                                            display: 'flex',
                                            gap: 6,
                                            flexWrap: 'wrap',
                                            touchAction: 'manipulation',
                                            WebkitUserSelect: 'none',
                                            userSelect: 'none',
                                        }}
                                    >
                                        <button
                                            type="button"
                                            className="muulti-button"
                                            style={{ width: 'auto', margin: 0, padding: '4px 8px' }}
                                            onClick={() => {
                                                if (isSelectedPanel) {
                                                    setSelectedComponentId(null);
                                                    setIsAdding(false);
                                                    setEditingIndex(null);
                                                    setActiveFormIndex(null);
                                                    setEditingComp(null);
                                                    setEditingType(null);
                                                    return;
                                                }
                                                if (editingIndex === idx && isAdding && activeFormIndex === idx) {
                                                    setSelectedComponentId(panelId);
                                                    setComponentEditorPanelOpen(true);
                                                    return;
                                                }
                                                handleEdit(idx);
                                            }}
                                        >
                                            {isSelectedPanel ? 'Selected' : 'Select Component'}
                                        </button>
                                        {isSelectedPanel && (
                                            <button
                                                type="button"
                                                className="muulti-button"
                                                style={{ width: 'auto', margin: 0, padding: '4px 8px', cursor: 'grab' }}
                                                onMouseDown={(event) => beginPanelInteraction(event, panelId, 'move')}
                                            >
                                                Drag to Move
                                            </button>
                                        )}
                                        {isSelectedPanel && (
                                            <div className="mobile-panel-arrow-controls" style={{ flexBasis: '100%' }}>
                                                <div className="mobile-panel-size-readout">
                                                    {`Size ${layoutValue.colSpan} x ${layoutValue.rowSpan}`}
                                                </div>
                                                <div className="mobile-panel-controls-title">Move</div>
                                                <div style={{ fontSize: 11, color: '#cbd5e1' }}>
                                                    Arrows move this panel by rows and columns.
                                                </div>
                                                <div className="mobile-panel-dpad">
                                                    <button
                                                        type="button"
                                                        className="muulti-button"
                                                        style={{ width: 'auto', margin: 0, padding: '4px 8px', userSelect: 'none' }}
                                                        onClick={() => applyPanelArrowAction(panelId, 0, -1, false)}
                                                    >
                                                        ↑
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="muulti-button"
                                                        style={{ width: 'auto', margin: 0, padding: '4px 8px', userSelect: 'none' }}
                                                        onClick={() => applyPanelArrowAction(panelId, -1, 0, false)}
                                                    >
                                                        ←
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="muulti-button"
                                                        style={{ width: 'auto', margin: 0, padding: '4px 8px', userSelect: 'none' }}
                                                        onClick={() => applyPanelArrowAction(panelId, 1, 0, false)}
                                                    >
                                                        →
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="muulti-button"
                                                        style={{ width: 'auto', margin: 0, padding: '4px 8px', userSelect: 'none' }}
                                                        onClick={() => applyPanelArrowAction(panelId, 0, 1, false)}
                                                    >
                                                        ↓
                                                    </button>
                                                </div>
                                                <div className="mobile-panel-controls-title">Resize</div>
                                                <div className="mobile-panel-dpad">
                                                    <button
                                                        type="button"
                                                        className="muulti-button"
                                                        style={{ width: 'auto', margin: 0, padding: '4px 8px', userSelect: 'none' }}
                                                        onClick={() => applyPanelArrowAction(panelId, 0, -1, true)}
                                                    >
                                                        H-
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="muulti-button"
                                                        style={{ width: 'auto', margin: 0, padding: '4px 8px', userSelect: 'none' }}
                                                        onClick={() => applyPanelArrowAction(panelId, -1, 0, true)}
                                                    >
                                                        W-
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="muulti-button"
                                                        style={{ width: 'auto', margin: 0, padding: '4px 8px', userSelect: 'none' }}
                                                        onClick={() => applyPanelArrowAction(panelId, 1, 0, true)}
                                                    >
                                                        W+
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="muulti-button"
                                                        style={{ width: 'auto', margin: 0, padding: '4px 8px', userSelect: 'none' }}
                                                        onClick={() => applyPanelArrowAction(panelId, 0, 1, true)}
                                                    >
                                                        H+
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {canUsePanelLayoutControls && isSelectedPanel && (
                                    <div className="mobile-panel-arrow-controls mobile-panel-arrow-controls--top">
                                        <div className="mobile-panel-size-readout">
                                            {`Size ${layoutValue.colSpan} x ${layoutValue.rowSpan}`}
                                        </div>
                                        <div className="mobile-panel-control-group">
                                            <div className="mobile-panel-controls-title">Move</div>
                                            <div className="mobile-panel-dpad">
                                                <button
                                                    type="button"
                                                    className="muulti-button mobile-panel-arrow-button"
                                                    onClick={() => applyPanelArrowAction(panelId, 0, -1, false)}
                                                >
                                                    ^
                                                </button>
                                                <button
                                                    type="button"
                                                    className="muulti-button mobile-panel-arrow-button"
                                                    onClick={() => applyPanelArrowAction(panelId, -1, 0, false)}
                                                >
                                                    &lt;
                                                </button>
                                                <button
                                                    type="button"
                                                    className="muulti-button mobile-panel-arrow-button"
                                                    onClick={() => applyPanelArrowAction(panelId, 1, 0, false)}
                                                >
                                                    &gt;
                                                </button>
                                                <button
                                                    type="button"
                                                    className="muulti-button mobile-panel-arrow-button"
                                                    onClick={() => applyPanelArrowAction(panelId, 0, 1, false)}
                                                >
                                                    v
                                                </button>
                                            </div>
                                        </div>
                                        <div className="mobile-panel-control-group">
                                            <div className="mobile-panel-controls-title">Resize</div>
                                            <div className="mobile-panel-dpad">
                                                <button
                                                    type="button"
                                                    className="muulti-button mobile-panel-arrow-button"
                                                    onClick={() => applyPanelArrowAction(panelId, 0, -1, true)}
                                                >
                                                    H-
                                                </button>
                                                <button
                                                    type="button"
                                                    className="muulti-button mobile-panel-arrow-button"
                                                    onClick={() => applyPanelArrowAction(panelId, -1, 0, true)}
                                                >
                                                    W-
                                                </button>
                                                <button
                                                    type="button"
                                                    className="muulti-button mobile-panel-arrow-button"
                                                    onClick={() => applyPanelArrowAction(panelId, 1, 0, true)}
                                                >
                                                    W+
                                                </button>
                                                <button
                                                    type="button"
                                                    className="muulti-button mobile-panel-arrow-button"
                                                    onClick={() => applyPanelArrowAction(panelId, 0, 1, true)}
                                                >
                                                    H+
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {showPlacementBadge && (
                                    <div className="grid-position-badge">
                                        {`C ${layoutValue.colStart ?? 'auto'}  R ${layoutValue.rowStart ?? 'auto'}  ${layoutValue.colSpan}x${layoutValue.rowSpan}`}
                                    </div>
                                )}

                                {panelNode}

                                {canUsePanelLayoutControls && isSelectedPanel && (
                                    <>
                                        <div
                                            title="Resize width"
                                            onMouseDown={(event) => beginPanelInteraction(event, panelId, 'resize-x')}
                                            style={{
                                                position: 'absolute',
                                                right: -6,
                                                top: '25%',
                                                width: 12,
                                                height: '50%',
                                                background: 'rgba(15, 23, 42, 0.18)',
                                                borderRadius: 6,
                                                cursor: 'ew-resize',
                                                zIndex: 9,
                                            }}
                                        />
                                        <div
                                            title="Resize height"
                                            onMouseDown={(event) => beginPanelInteraction(event, panelId, 'resize-y')}
                                            style={{
                                                position: 'absolute',
                                                left: '25%',
                                                bottom: -6,
                                                width: '50%',
                                                height: 12,
                                                background: 'rgba(15, 23, 42, 0.18)',
                                                borderRadius: 6,
                                                cursor: 'ns-resize',
                                                zIndex: 9,
                                            }}
                                        />
                                        <div
                                            title="Resize"
                                            onMouseDown={(event) => beginPanelInteraction(event, panelId, 'resize-both')}
                                            style={{
                                                position: 'absolute',
                                                right: -7,
                                                bottom: -7,
                                                width: 14,
                                                height: 14,
                                                background: '#0ea5e9',
                                                borderRadius: 3,
                                                cursor: 'nwse-resize',
                                                zIndex: 10,
                                            }}
                                        />
                                    </>
                                )}

                                {canUsePanelLayoutControls && isSelectedPanel && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: 10,
                                            bottom: 10,
                                            zIndex: 8,
                                            fontSize: 12,
                                            padding: '3px 6px',
                                            background: 'rgba(15, 23, 42, 0.82)',
                                            color: '#fff',
                                            borderRadius: 6,
                                        }}
                                    >
                                        {`size ${layoutValue.colSpan} x ${layoutValue.rowSpan} | col ${layoutValue.colStart ?? 'auto'} | row ${layoutValue.rowStart ?? 'auto'}`}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {isInlineAddActive && liveDraftPreview && liveDraftPanelNode && (
                        <div
                            key="panel-live-add-preview"
                            id={`panel-${INLINE_ADD_DRAFT_COMPONENT_ID}`}
                            className="component-grid-item is-live-draft-preview"
                            style={liveDraftWrapperStyle}
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    fontSize: 11,
                                    padding: '3px 6px',
                                    borderRadius: 6,
                                    background: 'rgba(15, 23, 42, 0.82)',
                                    color: '#fff',
                                    zIndex: 8,
                                }}
                            >
                                Unsaved Preview
                            </div>
                            {liveDraftPanelNode}
                        </div>
                    )}

                    <div
                        className={`add-component-slot ${isInlineAddActive ? "is-active" : ""}`}
                        style={shouldUseAdvancedLayout ? {
                            gridColumn: '1 / -1',
                            gridRow: effectiveAddSlotGridRowStart != null ? `${effectiveAddSlotGridRowStart}` : undefined,
                            order: 2147483647,
                            width: '100%',
                            minWidth: 0,
                            maxWidth: '100%',
                            overflowX: 'hidden',
                            boxSizing: 'border-box',
                        } : undefined}
                    >
                        <div className={`component-item add-component-panel ${isInlineAddActive ? "is-active" : ""}`}>
                            <div className="add-component-button-row" style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
                                {ADD_COMPONENT_OPTIONS.map((opt) => (
                                    <button
                                        key={opt.type}
                                        type="button"
                                        className="muulti-button"
                                        style={{ width: "auto" }}
                                        onClick={() => openInlineAddForm(opt.type)}
                                    >
                                        + {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>

            </div>
                        </div>
                    </div>
                </div>
            </div>
            {isPinnedStageScrollbarVisible && (
                <div
                    className="muulti-editor-stage-scrollbar"
                    style={{ '--muulti-vertical-scrollbar-width': `${browserVerticalScrollbarWidthPx}px` }}
                >
                    <div
                        className="muulti-editor-stage-scrollbar-track"
                        ref={pinnedStageScrollRef}
                        onScroll={syncStageFromPinnedScrollbar}
                    >
                        <div
                            className="muulti-editor-stage-scrollbar-inner"
                            style={{
                                width: `${Math.max(
                                    editorStageScrollMetrics.scrollWidth,
                                    pinnedScrollbarFallbackClientWidthPx + pinnedScrollbarReserveWidthPx + 2,
                                )}px`,
                                minWidth: `calc(100% + ${pinnedScrollbarReserveWidthPx + 2}px)`,
                            }}
                        />
                    </div>
                </div>
            )}
            </div>
        </>
    );
}
